import { assembleLpSmbHomeContent, splitLpSmbHomeContent, LP_SMB_HOME_BLOCK_KEYS, isLpSmbHomeContentUsable, isLpSmbHomeRunReady } from "@shared/lp-express-smb-home";
import { isSmbHomeAttempt, resolveSmbHomeLifecycle } from "@shared/lp-smb-home-lifecycle";
import { useState, useMemo, useEffect, useRef } from "react";
import { useParams, useSearch, useLocation, Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { groupAnglesByFamily } from "@/lib/angle-groups";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription, DialogHeader,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import {
  Rocket, Loader2, Users, Package, Sparkles, Folder, Wand2, ArrowRight, ArrowLeft, Pencil, Trash2, History, Radar, BookOpen,
  Globe, Copy, EyeOff, ExternalLink, CircleDot, Circle, AlertTriangle, Link2, ListOrdered, RotateCcw,
  ChevronDown, ChevronRight, ChevronLeft, Undo2, Redo2, Check, Monitor, Smartphone, Tag,
  CheckCircle2, Clock, Search, FileText, LayoutList, ArrowUpDown, ShieldCheck, SlidersHorizontal,
  PanelLeftClose, PanelLeftOpen, LayoutTemplate, Inbox,
} from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import type {
  Persona, Product, NarrativeAngle, Brand, Project, SalesDiscount, LpExpressRun, LandingPageTemplateType, ProductReview,
} from "@shared/schema";
import { resolveLpCheckoutUrl, validateLinkHref } from "@shared/schema";
import { META_PIXEL_ID_RE, META_PIXEL_ID_ERROR } from "@shared/meta-pixel";
import { expandBundleProducts } from "@shared/lp-express-bundle";
import type { LpQuizContent } from "@shared/lp-express-quiz";
import { isLpQuizContentUsable } from "@shared/lp-express-quiz";
import type { LpListicleContent } from "@shared/lp-express-listicle";
import { isLpListicleContentUsable } from "@shared/lp-express-listicle";
import type { LpAdvertorialContent } from "@shared/lp-express-advertorial";
import { isLpAdvertorialContentUsable } from "@shared/lp-express-advertorial";
import {
  hasUsableLpProductHeroSections,
  LP_PRODUCT_HERO_BLOCK_KEYS,
} from "@shared/lp-express-product-hero";
import {
  hasUsableLpProblemSolutionSections,
  LP_PROBLEM_SOLUTION_BLOCK_KEYS,
} from "@shared/lp-express-problem-solution";
import {
  LP_COLD_DRIP_REGEN_KEY_RE,
  isLpColdDripContentUsable,
  normalizeLpColdDripContent,
} from "@shared/lp-express-cold-drip";
import {
  isLpCoffeeShopContentUsable,
  normalizeLpCoffeeShopContent,
  LP_COFFEE_SHOP_REGEN_KEY_RE,
} from "@shared/lp-express-coffee-shop";
import {
  hasUsableLpOfferStackSections,
  LP_OFFER_STACK_BLOCK_KEYS,
} from "@shared/lp-express-offer-stack";
import {
  isLpTemeculaPassportContentUsable,
  normalizeLpTemeculaPassportContent,
  LP_TEMECULA_PASSPORT_REGEN_KEY_RE,
} from "@shared/lp-express-temecula-passport";
import {
  isLpPapabearLabelContentUsable,
  LP_PAPABEAR_LABEL_REGEN_KEY_RE,
  normalizeLpPapabearLabelContent,
  type LpPapabearLabelContent,
} from "@shared/lp-express-papabear-label";
import {
  LP_PAPABEAR_LABEL_LONG_WAIT_NOTICE_MS,
  resolveLpPapabearLabelPollState,
} from "@shared/lp-express-papabear-label-generation";
import { lpGetAt, lpDeepSet, lpCanSetLeaf } from "@shared/lp-deep-set";
import {
  isLpStudioDesignSnapshot,
  resolveLpStudioPreviewDesign,
  type LpStudioDesignSnapshot,
} from "@shared/lp-express-studio";
import {
  LP_SECTION_REGISTRY, lpSectionAnchorId,
  lpSectionCopyBlocks, lpSectionHasImages,
  LP_LONG_FORM_SECTION_TYPES, LP_PRODUCT_HERO_SECTION_TYPES, LP_PROBLEM_SOLUTION_SECTION_TYPES, LP_OFFER_STACK_SECTION_TYPES, lpTemplateSectionTypes,
  type LpSectionType,
  type LpTheme, type LpElementStyle, type LpComponentOrder, mergeLpTheme, mergeLpElementStyle, sanitizeLpComponentOrder,
  sanitizeLpMediaOverrides, sanitizeLpReviewOverrides,
} from "@shared/lp-express-sections";
import { apiRequest as rawApiRequest, parseJsonResponse } from "@/lib/queryClient";
import {
  lpSlugifyClient,
  lpExpressBrandSlug,
  lpExpressPublicUrl,
  lpExpressPublicLinkVisible,
} from "@/lib/lp-express-public-url";
import LpExpressRenderer from "@/components/lp-express-renderer";
import { LpExpressSmbHomeGenerator, SmbHomeWholePageRegenerate } from "@/components/lp-express-smb-home-generator";
import { SmbHomeImageFill } from "@/components/smb-home-image-fill";
import { SmbHomeImageProgress } from "@/components/smb-home-image-progress";
import { validateLpMediaFile } from "@/components/lp-editor";
import { LpPreviewSurface } from "@/components/lp-preview-frame";
import {
  type LpExpressSurface,
  withLpExpressSurfacePayload,
  withLpExpressSurfaceUrl,
} from "@/lib/lp-express-surface";
import {
  LP_PENDING_SAVE_KEY,
  canonicalSectionLayout,
  sameSectionLayout,
  sectionLayoutFromSections,
  type LpSectionLayoutItem,
} from "@/components/lp-express-page/contracts";
import { LpThemePanel } from "@/components/lp-express-page/theme-panel";
import { PickerSelect } from "@/components/lp-express-page/picker-select";
import { EmptyResource } from "@/components/lp-express-page/empty-resource";
import {
  LpRegenerateCard,
  LpStudioRegenerateCard,
  LpStudioRevisionCard,
} from "@/components/lp-express-page/regenerate-card";
import {
  LpDashboardSection,
  LpEditorShell,
  LpInputsSection,
  LpPublishSettingsSection,
  LpRegenerationControls,
} from "@/components/lp-express-page/feature-sections";

// Short, human-readable summary of a sale/discount for the inputs picker
// (e.g. "20% off", "$10 off"). Falls back to the customer-facing copy or the
// raw discount when the type is unknown so the option is never blank.
function formatSaleSummary(s: any): string {
  const raw = (s?.discountValue ?? "").toString().trim();
  const num = raw.replace(/[^0-9.]/g, "");
  switch (s?.saleType) {
    case "percentage_off":
      return num ? `${num}% off` : "Percentage off";
    case "dollar_off":
      return num ? `$${num} off` : "Dollar amount off";
    case "bogo":
      return "Buy one, get one";
    case "free_shipping":
      return "Free shipping";
    case "gwp":
      return "Gift with purchase";
    case "bundle":
      return num ? `Bundle deal · ${num}` : "Bundle deal";
    case "subscription":
      return num ? `Subscription · ${num}% off` : "Subscription discount";
    default:
      return s?.customerFacingCopy?.toString().trim() || (raw ? raw : "Discount");
  }
}
type RunRow = LpExpressRun & {
  studioStatus?: "rendering" | "ready" | "failed" | "stuck";
  pageId: string | null;
  resolvedProductName?: string | null;
  generatedContent: {
    hero?: { label: string; headline: string; subheadline: string; cta_text: string };
    leadin?: string;
    advisory?: {
      advisory_label?: string;
      advisory_headline?: string;
      advisory_subheadline?: string;
      advisory_headline_muted?: string;
      advisory_headline_bold?: string;
      advisory_headline_accent?: string;
    };
    showcase?: { headline: string; subheadlines?: string[] };
    productContext?: { cardHeader?: string; cardSubtext?: string; imageUrl?: string; altText?: string; imageStatus?: string };
    stations?: {
      section_label?: string;
      section_headline?: string;
      stations?: Array<{ num?: number | string; label?: string; sub?: string }>;
      loop_quote?: string;
    };
    villain?: VillainState;
    originCopy?: OriginCopyState;
    originImages?: Array<{ key?: string; imageUrl?: string; altText?: string; imageStatus?: string }>;
    quotes?: Record<string, QuoteState>;
    timeline?: TimelineState;
    membership?: MembershipState;
    alternatives?: AlternativesState;
    philosophy?: PhilosophyState;
    differentiator?: DifferentiatorState;
    faq?: FaqState;
    reviews?: ReviewsState;
    finalCta?: FinalCtaState;
    footer?: FooterState;
    proof?: ProofState;
    alternativesImages?: LpImageSlot[];
    philosophyImages?: LpImageSlot[];
    proofImages?: LpImageSlot[];
  } | null;
};

type OriginCopyState = {
  section_eyebrow?: string;
  section_headline?: string;
  section_subheadline?: string;
  fresh_badge?: string;
  block_1_headline?: string;
  block_1_body?: string;
  block_2_headline?: string;
  block_2_body?: string;
  block_3_headline?: string;
  block_3_body?: string;
  location_carousel_captions?: string[];
  origin_carousel_captions?: string[];
};

type QuoteState = { quote?: string; attribution?: string };

type VillainState = {
  checklist_headline?: string;
  checklist_items?: string[];
  checklist_footer?: string;
  report_eyebrow?: string;
  report_headline?: string;
  report_rows?: Array<{ name?: string; note?: string; grade?: string }>;
  belief_eyebrow?: string;
  belief_headline?: string;
};

type StationsState = {
  section_label?: string;
  section_headline?: string;
  stations?: Array<{ num?: number | string; label?: string; sub?: string }>;
  loop_quote?: string;
};

// ── Long-form section copy + image states (12-workflow integration) ──
type TimelineState = {
  section_eyebrow?: string;
  section_headline?: string;
  section_subheadline?: string;
  steps?: Array<{ label?: string; headline?: string; bullets?: string[] }>;
  disclaimer?: string;
  sticky_note_headline?: string;
};
type MembershipState = {
  section_eyebrow?: string;
  section_headline?: string;
  body_1?: string;
  body_2?: string;
  body_closing?: string;
  membership_label?: string;
  membership_tagline?: string;
  benefits?: Array<{ icon_hint?: string; label?: string; note?: string }>;
  objection_note_1?: string;
  objection_note_2?: string;
  objection_note_3?: string;
};
type AlternativesState = {
  section_eyebrow?: string;
  hero_headline?: string;
  hero_subheadline?: string;
  hero_differentiators?: string[];
  competitor_intro?: string;
  competitor_cards?: Array<{ title?: string; body?: string }>;
  comparison_them_label?: string;
  comparison_us_label?: string;
  comparison_them?: string[];
  comparison_us?: string[];
  closing_line_1?: string;
  closing_line_2?: string;
};
type PhilosophyState = {
  section_eyebrow?: string;
  section_headline?: string;
  section_body_1?: string;
  section_body_2?: string;
  pillar_card_label?: string;
  pillars?: Array<{ num?: string; title?: string; body?: string }>;
  pillar_footer_words?: string[];
};
type DifferentiatorState = {
  section_eyebrow?: string;
  section_headline?: string;
  features?: Array<{ icon_hint?: string; title?: string; body?: string }>;
  trust_badge?: string;
};
type FaqState = { faqs?: Array<{ question?: string; answer?: string }> };
type ReviewsState = {
  reviews?: Array<{ name?: string; variant?: string | null; quote?: string; verified?: boolean }>;
};
type FinalCtaState = {
  section_eyebrow?: string;
  section_headline?: string;
  section_subheadline?: string;
  bundle_label?: string;
  savings_badge?: string;
  cta_button?: string;
  cta_subtext?: string;
  trust_badges?: Array<{ icon_hint?: string; label?: string }>;
  social_proof_line?: string;
};
type FooterState = {
  tagline?: string;
  description?: string;
  explore_label?: string;
  support_label?: string;
  explore_links?: Array<{ label?: string }>;
  support_links?: Array<{ label?: string }>;
  copyright?: string;
  bottom_note?: string;
};
type ProofLink = { label?: string; url?: string };
type ProofState = {
  section_eyebrow?: string;
  section_headline?: string;
  section_subheadline?: string;
  proof_folder_headline?: string;
  proof_folder_body?: string;
  proof_folder_bullets?: string[];
  proof_cards?: Array<{
    name?: string;
    what_it_is?: string;
    what_it_does?: string;
    why_it_matters?: string;
    research_links?: ProofLink[];
  }>;
  why_it_matters_headline?: string;
  why_it_matters_body?: string;
  why_it_matters_links?: ProofLink[];
  comparison_them?: string[];
  comparison_us?: string[];
  disclaimer?: string;
};
type LpImageSlot = { key?: string; imageUrl?: string; altText?: string; imageStatus?: string };
type MagicPatternsDesignSystem = {
  id: string;
  name: string;
  isReserved: boolean;
  isActive: boolean;
};
type LpDesignSystemsResponse = {
  designSystems: MagicPatternsDesignSystem[];
  selectedDesignSystemId: string | null;
};
type PendingDesignSystemSelection = {
  brandId: string;
  surface: LpExpressSurface;
  value: string;
};
type LpStudioPageDesignStatus = {
  status?: "pending" | "ready" | "failed";
  submissionState?: string;
  workflowPhase?: string;
  designSystemId?: string | null;
  attemptId?: string | null;
  /** Presence is used only as a server-confirmed recovery gate. Never render
   *  this identifier in the editor UI. */
  mpEditorId?: string | null;
  failureReason?: string | null;
};

type LpStudioRevisionStatus = {
  status?: "ready" | "revising" | "failed";
  submissionState?: string;
  attemptId?: string | null;
  error?: string | null;
  lastCompletedAttemptId?: string | null;
};
function studioBrandDesignUserMessage(status: LpStudioPageDesignStatus | null | undefined): string {
  return status?.failureReason?.trim()
    || "Your page design could not be completed. Please try again. If it continues, contact support.";
}
type StudioDesignRecoveryVariables = {
  pageId: string;
  runId: string;
  brandId: string;
  navigationEpoch: number;
  remote: {
    attemptId: string | null;
    editorId: string;
    status: "failed";
    submissionState?: string;
  };
};

const NO_LP_DESIGN_SYSTEM_VALUE = "__no_design_system__";

function LpExpressDesignSystemPicker({
  visible,
  systems,
  loading,
  error,
  unavailable,
  failed,
  pending,
  disabled,
  value,
  onChange,
}: {
  visible: boolean;
  systems: MagicPatternsDesignSystem[];
  loading: boolean;
  error: unknown;
  unavailable: boolean;
  failed: boolean;
  pending: boolean;
  disabled: boolean;
  value: string;
  onChange: (value: string) => void;
}) {
  if (!visible) return null;
  return (
    <div className="basis-full pt-1" data-testid="lp-design-system-picker">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-medium">Brand design system</span>
        <Select value={value} onValueChange={onChange} disabled={loading || pending || disabled}>
          <SelectTrigger
            className="h-7 w-56 border-transparent bg-transparent px-2 text-xs shadow-none hover:bg-muted/50 focus:bg-background"
            data-testid="select-lp-magic-patterns-design-system"
          >
            <SelectValue placeholder={loading ? "Loading…" : "Choose design system"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_LP_DESIGN_SYSTEM_VALUE}>No design system (standard page)</SelectItem>
            {systems.map((system) => (
              <SelectItem key={system.id} value={system.id}>
                {system.name}{system.isReserved ? " (built-in)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {pending && (
          <span className="inline-flex items-center gap-1 text-muted-foreground" data-testid="text-lp-design-system-saving">
            <Loader2 className="h-3 w-3 animate-spin" />
            Saving selection…
          </span>
        )}
      </div>
      {Boolean(error) && (
        <p className="mt-1 text-[11px] text-destructive" data-testid="text-lp-design-systems-error">
          {error instanceof Error ? error.message : "Couldn't load design systems."}
        </p>
      )}
      {unavailable && !error && (
        <p className="mt-1 text-[11px] text-destructive" data-testid="text-lp-design-system-unavailable">
          The saved design system is no longer available. Choose another system or the standard page.
        </p>
      )}
      {failed && (
        <p className="mt-1 text-[11px] text-destructive" data-testid="text-lp-design-system-save-failed">
          The design-system selection was not saved. Choose a system again before generating.
        </p>
      )}
    </div>
  );
}

export default function LandingPagesExpressPage({
  surface = "lp-express-1",
}: {
  surface?: LpExpressSurface;
}) {
  const { brandId } = useParams<{ brandId: string }>();
  const search = useSearch();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  // Always invalidate through the provider's client. The shared query client
  // is the production provider, but tests and embedded surfaces may provide a
  // scoped client; using the hook keeps mutations and list queries together.
  const queryClient = useQueryClient();
  const surfacePath =
    surface === "lp-express-2"
      ? `/brands/${brandId}/landing-pages-express-2`
      : `/brands/${brandId}/landing-pages-express`;
  const fetch = (
    url: string,
    init?: RequestInit,
  ) => globalThis.fetch(withLpExpressSurfaceUrl(url, surface), init);
  const apiRequest = (
    method: Parameters<typeof rawApiRequest>[0],
    url: string,
    data?: unknown,
  ) =>
    rawApiRequest(
      method,
      withLpExpressSurfaceUrl(url, surface),
      url.includes("/lp-express") || url.includes("/landing-pages/")
        ? withLpExpressSurfacePayload(data, surface)
        : data,
    );

  const [personaId, setPersonaId] = useState<string>("");
  const [productId, setProductId] = useState<string>("");
  const [angleId, setAngleId] = useState<string>("");
  // Custom narrative angle: when the user writes their own angle instead of
  // picking a saved one. In custom mode, requests send `customAngle` (name +
  // description) and omit narrativeAngleId; the server persists the text on
  // the page's generatedContent so reloads/regeneration rebuild it.
  const [customAngleMode, setCustomAngleMode] = useState(false);
  const [customAngleText, setCustomAngleText] = useState("");
  // Friendly missing-field guidance: flips on when the user tries to generate
  // with required inputs missing; highlights the incomplete pickers.
  const [showMissingHints, setShowMissingHints] = useState(false);
  const [pendingDesignSystemSelection, setPendingDesignSystemSelection] =
    useState<PendingDesignSystemSelection | null>(null);
  const [failedDesignSystemSelection, setFailedDesignSystemSelection] =
    useState<PendingDesignSystemSelection | null>(null);
  // A ref closes the tiny render gap after a selection changes. The Generate
  // guards read it so a rapid click can never use the previous durable brand
  // system while the new selection is still being saved.
  const designSystemWriteOriginRef = useRef<Pick<PendingDesignSystemSelection, "brandId" | "surface"> | null>(null);
  // Sale/Discount applied to this page. "" = uninitialized (defaults to the
  // brand's active sale once sales load); "__none__" = explicit "No discount".
  // Any other value is a sales_discounts.id. Lets users A/B different discounts
  // on the same product without creating a new product per test.
  const [saleId, setSaleId] = useState<string>("");
  const saleInitRef = useRef(false);
  const [projectId, setProjectId] = useState<string>("");
  // Desktop/Mobile toggle for the in-canvas live preview. In-session only
  // (defaults to Desktop on open); a purely visual aid — never affects what is
  // saved/published.
  const [previewViewMode, setPreviewViewMode] = useState<"desktop" | "mobile">("desktop");
  const [hero, setHero] = useState<{
    label: string;
    headline: string;
    subheadline: string;
    cta_text: string;
  } | null>(null);
  // Surfaces a hero copy generation failure (hard error or empty workflow
  // result) so the renderer shows an error/retry state instead of silently
  // falling back to the hardcoded template placeholders.
  const [heroError, setHeroError] = useState<string | null>(null);
  const [leadin, setLeadin] = useState<string>("");
  const [advisory, setAdvisory] = useState<{
    advisory_label?: string;
    advisory_headline?: string;
    advisory_subheadline?: string;
    advisory_headline_muted?: string;
    advisory_headline_bold?: string;
    advisory_headline_accent?: string;
  } | null>(null);
  const [showcase, setShowcase] = useState<{ headline: string; subheadlines?: string[] } | null>(null);
  // Product Context: one block per belief card (image + card header/subtext),
  // each driven by its own workflow call. Rendered inside the "Real
  // ingredients" BeliefSection.
  const [productContext, setProductContext] = useState<
    Array<{ cardHeader?: string; cardSubtext?: string; imageUrl?: string; altText?: string; imageStatus?: string }> | null
  >(null);
  const [productContextImagePending, setProductContextImagePending] = useState(false);
  const [originImages, setOriginImages] = useState<
    Array<{ key?: string; imageUrl?: string; altText?: string; imageStatus?: string }> | null
  >(null);
  const [originImagesPending, setOriginImagesPending] = useState(false);
  const [stations, setStations] = useState<StationsState | null>(null);
  const [villain, setVillain] = useState<VillainState | null>(null);
  // AI farmstand/origin copy + the 7 short pull-quotes. Both fall back to the
  // template's hardcoded strings when absent.
  const [originCopy, setOriginCopy] = useState<OriginCopyState | null>(null);
  // Template variant of the loaded page (e.g. "long-form-v2"). Hydrated from the
  // stored generatedContent so the builder preview dispatches to the correct
  // renderer path (v1 vs v2); without this the preview always rendered v1.
  const [templateType, setTemplateType] = useState<string | null>(null);
  // Frozen, sanitized Template Studio document. It stays in the existing page
  // generatedContent so history, publishing snapshots, and public rendering all
  // preserve the exact design selected when this run started.
  const [studioDesign, setStudioDesign] =
    useState<LpStudioDesignSnapshot | null>(null);
  // Hydrated page-local Studio design state is kept separately from the
  // immutable source snapshot so the loading indicator can render immediately
  // on reload, before the status query's first refetch returns.
  const [smbHomeRunReady, setSmbHomeRunReady] = useState(false);
  const [studioPageDesignStatus, setStudioPageDesignStatus] =
    useState<LpStudioPageDesignStatus | null>(null);
  const [studioRevisionStatus, setStudioRevisionStatus] =
    useState<LpStudioRevisionStatus | null>(null);
  // Explicit recovery is page-scoped and read-only with respect to provider
  // admission. Keep the preview/page selection mounted while it polls so a
  // navigation click cannot discard the frozen brand/page inputs mid-recovery.
  const [studioDesignRecoveryActive, setStudioDesignRecoveryActive] =
    useState(false);
  const [studioDesignRecoveryRequestPending, setStudioDesignRecoveryRequestPending] =
    useState(false);
  const navigationEpochRef = useRef(0);
  const [studioBulkRegeneration, setStudioBulkRegeneration] = useState<any>({});
  // Quiz Funnel content (only used when templateType === "quiz"). Its whole
  // content lives in one structured blob — there is no hero/sections chain.
  const [quiz, setQuiz] = useState<LpQuizContent | null>(null);
  // True while a quiz is generating in the BACKGROUND on the server (the route
  // returns immediately with runId/pageId, then the client polls the run row
  // until the quiz blob lands). Keeps the preview in its loading state without
  // holding the generate mutation pending for the full multi-minute Sim call.
  const [quizGenerating, setQuizGenerating] = useState(false);
  // True while a REGENERATION is in flight (as opposed to a first-time generate).
  // Drives the quiz "Regenerating…" control and tells the poll/timeout handlers
  // to keep the existing quiz on failure (a first-gen failure shows the error UI
  // instead, because there is no prior quiz to fall back to).
  const [quizRegenerating, setQuizRegenerating] = useState(false);
  // Listicle content (only used when templateType === "listicle"). Like the
  // quiz, its whole COPY lives in one structured blob — no hero/sections chain.
  // Its AI imagery lives separately in `listicleImages` (below), so a copy
  // regeneration never touches the images.
  const [listicle, setListicle] = useState<LpListicleContent | null>(null);
  const [listicleGenerating, setListicleGenerating] = useState(false);
  const [listicleRegenerating, setListicleRegenerating] = useState(false);
  // Listicle AI image slots (hero / lifestyle / gallery / science) — rendered in
  // the background by the server; polled while any slot is still pending.
  const [listicleImages, setListicleImages] = useState<LpImageSlot[] | null>(null);
  const [listicleImagesPending, setListicleImagesPending] = useState(false);
  // Advertorial content (only used when templateType === "advertorial"). Like
  // the quiz/listicle, its whole COPY lives in one structured blob — no
  // hero/sections chain. Its AI imagery lives separately in `advertorialImages`
  // (below), so a copy regeneration never touches the images.
  const [advertorial, setAdvertorial] = useState<LpAdvertorialContent | null>(null);
  // Cold Drip Offer (Gaffle): imported reference template — one copy blob.
  // With SIM_LP_COLD_DRIP_URL set the copy generates in the background
  // (coldDripGenerating drives the run poll below); without it the route
  // returns the default blob synchronously. In BOTH modes the AI image slots
  // (ingredients / community wall / brand-story photos) fill in the
  // background and are polled while pending.
  const [coldDrip, setColdDrip] = useState<any | null>(null);
  const [coldDripImages, setColdDripImages] = useState<LpImageSlot[] | null>(null);
  const [coldDripImagesPending, setColdDripImagesPending] = useState(false);
  // Coffee Shop Lead Capture (Gaffle): same imported-template model as the
  // Cold Drip above — one copy blob at gc.coffeeShop plus background-filled AI
  // image slots (hero photo / drink cards / pour bar) at gc.coffeeShopImages.
  const [coffeeShop, setCoffeeShop] = useState<any | null>(null);
  const [coffeeShopImages, setCoffeeShopImages] = useState<LpImageSlot[] | null>(null);
  const [coffeeShopImagesPending, setCoffeeShopImagesPending] = useState(false);
  // Temecula Passport (Mystery Drop, Gaffle): same imported-template model as
  // the Coffee Shop above — one copy blob at gc.temeculaPassport plus
  // background-filled AI image slots (mystery card / poster / ingredients /
  // gallery / pour bar) at gc.temeculaPassportImages.
  const [temeculaPassport, setTemeculaPassport] = useState<any | null>(null);
  const [papabearLabel, setPapabearLabel] =
    useState<LpPapabearLabelContent | null>(null);
  const [papabearLabelGenerating, setPapabearLabelGenerating] =
    useState(false);
  const [papabearLabelImages, setPapabearLabelImages] =
    useState<LpImageSlot[] | null>(null);
  const [papabearLabelImagesPending, setPapabearLabelImagesPending] =
    useState(false);
  const [temeculaPassportImages, setTemeculaPassportImages] = useState<LpImageSlot[] | null>(null);
  const [temeculaPassportImagesPending, setTemeculaPassportImagesPending] = useState(false);
  const [advertorialGenerating, setAdvertorialGenerating] = useState(false);
  const [coldDripGenerating, setColdDripGenerating] = useState(false);
  const [coffeeShopGenerating, setCoffeeShopGenerating] = useState(false);
  const [coffeeShopRegenerating, setCoffeeShopRegenerating] = useState(false);
  const [temeculaPassportGenerating, setTemeculaPassportGenerating] = useState(false);
  const [temeculaPassportRegenerating, setTemeculaPassportRegenerating] = useState(false);
  const [advertorialRegenerating, setAdvertorialRegenerating] = useState(false);
  // Advertorial AI image slots (author avatar + section-1..4) — rendered in the
  // background by the server; polled while any slot is still pending.
  const [advertorialImages, setAdvertorialImages] = useState<LpImageSlot[] | null>(null);
  const [advertorialImagesPending, setAdvertorialImagesPending] = useState(false);
  // Product Hero (PDP) content (only used when templateType === "product-hero").
  // Unlike the quiz/listicle/advertorial one-blob model, the product-hero flows
  // through the SAME ordered/reorderable sections pipeline as long-form-v2: its
  // copy lives under per-SECTION generatedContent keys (phBuyBox, phTrustBar, …)
  // held here as a keyed record so in-canvas edits + per-section regeneration
  // merge back into a single key. The gallery's MAIN image + leading thumbs
  // are the brand's REAL product photos (resolved at render time); a small
  // pool of optional AI LIFESTYLE shots rides in productHeroImages below and
  // is appended to the gallery as extra thumbnails.
  const [productHeroSections, setProductHeroSections] = useState<
    Record<string, unknown>
  >({});
  const [smbPageMetadata, setSmbPageMetadata] = useState<Record<string, any>>({});
  const [productHeroGenerating, setProductHeroGenerating] = useState(false);
  // Product Hero AI lifestyle gallery slots — same per-slot status model as
  // listicleImages/advertorialImages; polled while any slot is still pending.
  const [productHeroImages, setProductHeroImages] = useState<LpImageSlot[] | null>(null);
  const [productHeroImagesPending, setProductHeroImagesPending] = useState(false);
  // Problem / Solution (PAS) content (only used when templateType ===
  // "problem-solution"). Clones the product-hero per-SECTION model: copy lives
  // under per-section generatedContent keys (psHero, psProblem, …) held here as
  // a keyed record. The only AI image is the mechanism diagram, riding in
  // problemSolutionImages; the solution-reveal photo is the brand's REAL
  // product photo (resolved at render time).
  const [problemSolutionSections, setProblemSolutionSections] = useState<
    Record<string, unknown>
  >({});
  const [problemSolutionGenerating, setProblemSolutionGenerating] = useState(false);
  const [problemSolutionImages, setProblemSolutionImages] = useState<LpImageSlot[] | null>(null);
  const [problemSolutionImagesPending, setProblemSolutionImagesPending] = useState(false);
  // Offer Stack (value-stack close) content (only used when templateType ===
  // "offer-stack"). Same per-SECTION model: copy lives under per-section
  // generatedContent keys (osHero, osStack, …) held here as a keyed record.
  // The only AI image is the intro visual, riding in offerStackImages (the
  // real product photo stays the render-time fallback + media override).
  const [offerStackSections, setOfferStackSections] = useState<
    Record<string, unknown>
  >({});
  const [offerStackGenerating, setOfferStackGenerating] = useState(false);
  const [offerStackImages, setOfferStackImages] = useState<LpImageSlot[] | null>(null);
  const [offerStackImagesPending, setOfferStackImagesPending] = useState(false);
  const [quotes, setQuotes] = useState<Record<string, QuoteState> | null>(null);
  // Long-form section copy (8 workflows) + section images (3 workflows). All
  // fall back to the template's hardcoded copy/placeholders when absent.
  const [timeline, setTimeline] = useState<TimelineState | null>(null);
  const [membership, setMembership] = useState<MembershipState | null>(null);
  const [alternatives, setAlternatives] = useState<AlternativesState | null>(null);
  const [philosophy, setPhilosophy] = useState<PhilosophyState | null>(null);
  const [differentiator, setDifferentiator] = useState<DifferentiatorState | null>(null);
  const [faq, setFaq] = useState<FaqState | null>(null);
  const [reviews, setReviews] = useState<ReviewsState | null>(null);
  const [finalCta, setFinalCta] = useState<FinalCtaState | null>(null);
  const [footer, setFooter] = useState<FooterState | null>(null);
  const [proof, setProof] = useState<ProofState | null>(null);
  // The editor's working section layout (order + per-section hidden flag) for
  // ALL blocks. Defaults to canonical; hydrated from a run's persisted
  // `sections`. Drives both the in-canvas editor and the live preview.
  const [sectionLayout, setSectionLayout] = useState<LpSectionLayoutItem[]>(
    () => canonicalSectionLayout(LP_LONG_FORM_SECTION_TYPES),
  );
  const [alternativesImages, setAlternativesImages] = useState<LpImageSlot[] | null>(null);
  const [alternativesImagesPending, setAlternativesImagesPending] = useState(false);
  const [philosophyImages, setPhilosophyImages] = useState<LpImageSlot[] | null>(null);
  const [philosophyImagesPending, setPhilosophyImagesPending] = useState(false);
  const [proofImages, setProofImages] = useState<LpImageSlot[] | null>(null);
  const [proofImagesPending, setProofImagesPending] = useState(false);
  const [pageId, setPageId] = useState<string>("");
  // Version-history nav: server-authoritative pointer + undo/redo availability.
  // Updated by every snapshot/undo/redo/restore response; drives the toolbar.
  const [snapshotNav, setSnapshotNav] = useState<{
    currentSnapshotSeq: number | null;
    canUndo: boolean;
    canRedo: boolean;
  }>({ currentSnapshotSeq: null, canUndo: false, canRedo: false });
  const [historyOpen, setHistoryOpen] = useState(false);
  // Per-page video URL overrides (Farmstand "making"/"lifestyle" slots) and
  // per-element hide/delete state, both stored on the page's generatedContent
  // (never the brand). Hydrated from a saved run, persisted via content ops, and
  // carried into the published snapshot automatically.
  const [videoOverrides, setVideoOverrides] = useState<{ making?: string; lifestyle?: string; "coffee-shop-reel"?: string }>({});
  // Per-page user-uploaded image overrides (slot key → { url }). Each shadows an
  // AI image slot at render time without touching the underlying AI URL, so a
  // "Revert to AI" clears the override and the AI image returns. Stored on the
  // page's generatedContent (mediaOverrides), never the brand.
  const [mediaOverrides, setMediaOverrides] = useState<Record<string, { url?: string }>>({});
  // Per-page review overrides: slot key (e.g. "hero") → a snapshot of the real
  // product review the user picked to feature. Lives on the page's
  // generatedContent (reviewOverrides), never the brand.
  const [reviewOverrides, setReviewOverrides] = useState<
    Record<string, { reviewer: string | null; reviewText: string; rating: number }>
  >({});
  const [elementStates, setElementStates] = useState<
    Record<string, { hidden?: boolean; deleted?: boolean }>
  >({});
  // WYSIWYG: global theme (fonts/colors/buttons/spacing) + per-element/section
  // style overrides. Both stored on the page's generatedContent (theme/styles),
  // hydrated from a saved run, persisted via pathless content ops, and carried
  // into the published snapshot automatically.
  const [theme, setTheme] = useState<LpTheme>({});
  const [elementStyles, setElementStyles] = useState<Record<string, LpElementStyle>>({});
  // Per-container component ordering (group key → ordered element keys). Stored on
  // the page's generatedContent (componentOrder), hydrated from a saved run,
  // persisted via a pathless content op, and carried into the published snapshot.
  // Reordering is SCOPED to one container — never reorders across the page.
  const [componentOrder, setComponentOrder] = useState<LpComponentOrder>({});
  // Configured checkout URL for the current page. null => not configured (the
  // field shows the resolved default); a string (incl. "") => explicit value.
  const [checkoutUrlInput, setCheckoutUrlInput] = useState<string | null>(null);
  // Local edit buffer for the brand-level Meta Pixel ID. null = untouched
  // (show the saved brand value); a string = the user's in-progress edit.
  const [metaPixelIdInput, setMetaPixelIdInput] = useState<string | null>(null);
  const checkoutSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Whether the editor's left control rail is collapsed to give the live
  // preview full width. Editor-only UI state; not persisted.
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string>("");
  const [templateTypeKey, setTemplateTypeKey] = useState<string>("long-form");
  // The canonical section types for the ACTIVE template (loaded run's
  // templateType, else the create-time selection). Scopes the section layout so
  // a product-hero page's editor + preview surface ONLY the ph* blocks, and a
  // long-form page only its blocks.
  const templateSectionTypes = useMemo(
    () => lpTemplateSectionTypes({ templateType: templateType ?? templateTypeKey, studioDesign }),
    [templateType, templateTypeKey, studioDesign],
  );
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [creatingProject, setCreatingProject] = useState(false);
  const [inputsExpanded, setInputsExpanded] = useState(true);
  // Management-dashboard controls for the "Your landing pages" panel: real-time
  // search, an All/Live/Draft filter, and a sort order. All purely client-side
  // over the merged page list (no backend involvement).
  const [pagesSearch, setPagesSearch] = useState("");
  const [pagesFilter, setPagesFilter] = useState<"all" | "live" | "draft">("all");
  const [pagesSort, setPagesSort] = useState<"newest" | "oldest" | "name" | "status">("newest");
  const [pagesPage, setPagesPage] = useState(1);
  const PAGES_PER_PAGE = 8;

  const smbHomeCapabilityQuery = useQuery<{ eligible: boolean }>({
    queryKey: ["/api/brands", brandId, "lp-express/smb-home-capability", "lp-express-2"],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/lp-express/smb-home-capability`, { credentials: "include" });
      if (res.status === 403 || res.status === 404) return { eligible: false };
      if (!res.ok) throw new Error("Couldn't check Home page availability.");
      return res.json();
    },
    enabled: surface === "lp-express-2" && !!brandId,
    retry: false,
  });
  const smbHomeEligible = surface === "lp-express-2" &&
    !smbHomeCapabilityQuery.isError && smbHomeCapabilityQuery.data?.eligible === true;
  const smbHomeEligibilityPending = surface === "lp-express-2" && smbHomeCapabilityQuery.isPending;
  const [pageTypeChoice, setPageTypeChoice] = useState<{ brandId: string; value: "lp" | "hp" } | null>(null);
  const storedPageType = useMemo(() => {
    try { return sessionStorage.getItem(`lp2-page-type:${brandId}`); } catch { return null; }
  }, [brandId]);
  const pageType = surface !== "lp-express-2" ? "lp"
    : pageTypeChoice?.brandId === brandId ? pageTypeChoice.value
      : storedPageType === "lp" || storedPageType === "hp" ? storedPageType
        : smbHomeEligible ? "hp" : "lp";
  const selectPageType = (value: "lp" | "hp") => {
    setPageTypeChoice({ brandId: brandId!, value });
    try { sessionStorage.setItem(`lp2-page-type:${brandId}`, value); } catch { /* Browsing remains available. */ }
    setPagesSearch("");
    setPagesFilter("all");
    setPagesPage(1);
  };

  const { data: previousRuns = [], isLoading: loadingRuns, error: runsError, refetch: refetchRuns } = useQuery<RunRow[]>({
    queryKey: [`/api/brands/${brandId}/lp-express/runs`, surface],
    queryFn: async () => {
      const res = await globalThis.fetch(`/api/brands/${brandId}/lp-express/runs?surface=${surface}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load saved pages");
      return res.json();
    },
    enabled: !!brandId,
  });

  // --- Publish state -------------------------------------------------------
  // Publish status for the current persisted page (Draft / Live / Unpublished
  // + slug). Only meaningful once a page has been saved (pageId set).
  type PublishStatus = {
    status: "draft" | "published" | "unpublished";
    slug: string | null;
    // Brand slug snapshot stored on the published row; the brand segment of the
    // public /p/{brandSlug}/{slug} URL. Null until first publish.
    brandSlug?: string | null;
    publishedAt?: string | null;
    // True when the working page has been edited since it was last published —
    // i.e. the live /p/:slug snapshot is stale and a republish is needed.
    hasUnpublishedChanges?: boolean;
    // Per-page opt-in to being served on the brand's custom domain.
    customDomainEnabled?: boolean;
    // Per-page opt-in: install the brand's Meta Pixel on the published page.
    // This is the LIVE working value (page-level); the published row keeps its
    // own frozen snapshot, so flips surface as hasUnpublishedChanges.
    metaPixelEnabled?: boolean;
  };
  const { data: publishState } = useQuery<PublishStatus>({
    queryKey: ["/api/landing-pages", pageId, "published", surface],
    queryFn: async () => {
      const res = await fetch(`/api/landing-pages/${pageId}/published`, { credentials: "include" });
      if (!res.ok) return { status: "draft", slug: null } as PublishStatus;
      return res.json();
    },
    enabled: !!pageId,
  });

  // Optional custom slug a user can set BEFORE the first publish. Only sent on
  // first publish; republish keeps the existing slug (server ignores it then).
  const [desiredSlugInput, setDesiredSlugInput] = useState("");
  const normalizedDesiredSlug = lpSlugifyClient(desiredSlugInput);

  const publishMutation = useMutation({
    mutationFn: async () => {
      // Send the chosen slug only when publishing for the first time and the
      // user actually typed one; otherwise the server auto-generates.
      const body =
        !publishState?.slug && normalizedDesiredSlug
          ? { slug: normalizedDesiredSlug }
          : {};
      const res = await apiRequest("POST", `/api/landing-pages/${pageId}/publish`, body);
      return parseJsonResponse(res);
    },
    onSuccess: () => {
      setDesiredSlugInput("");
      queryClient.invalidateQueries({ queryKey: ["/api/landing-pages", pageId, "published"] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/lp-express/published-pages`] });
      toast({ title: "Page published", description: "Your landing page is now live." });
    },
    onError: (e: any) => {
      toast({ title: "Publish failed", description: e?.message || "Could not publish.", variant: "destructive" });
    },
  });

  const [unpublishConfirmOpen, setUnpublishConfirmOpen] = useState(false);
  const unpublishMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/landing-pages/${pageId}/unpublish`, {});
      return parseJsonResponse(res);
    },
    onSuccess: () => {
      setUnpublishConfirmOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/landing-pages", pageId, "published"] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/lp-express/published-pages`] });
      toast({ title: "Page unpublished", description: "The public page is now offline." });
    },
    onError: (e: any) => {
      toast({ title: "Unpublish failed", description: e?.message || "Could not unpublish.", variant: "destructive" });
    },
  });

  // --- Editable slug -------------------------------------------------------
  // Rename of the public /p/:slug (in the "Public URL" card). Only available
  // once a published row exists. Changing the slug breaks any previously-shared
  // links, so we confirm before applying.
  const [slugDraft, setSlugDraft] = useState("");
  const [slugConfirmOpen, setSlugConfirmOpen] = useState(false);

  const renameSlugMutation = useMutation({
    mutationFn: async (slug: string) => {
      const res = await apiRequest("PATCH", `/api/landing-pages/${pageId}/slug`, { slug });
      return parseJsonResponse(res);
    },
    onSuccess: () => {
      setSlugConfirmOpen(false);
      setSlugDraft("");
      queryClient.invalidateQueries({ queryKey: ["/api/landing-pages", pageId, "published"] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/lp-express/published-pages`] });
      toast({ title: "URL updated", description: "Your page is now reachable at the new link." });
    },
    onError: (e: any) => {
      toast({
        title: "Couldn't update URL",
        description: e?.message || "Could not change the URL.",
        variant: "destructive",
      });
    },
  });

  // ---- Custom domain (CNAME, brand-level) ----------------------------------
  // One custom subdomain per brand. Once verified, individual published pages
  // opt into being served on it (per-page customDomainEnabled toggle), reachable
  // at https://<hostname>/<slug>. Managed via a modal on the main page.
  type CustomDomainState = {
    domain: {
      id: string;
      hostname: string;
      status: "pending" | "verified" | "failed";
      // Raw Cloudflare hostname status (e.g. "pending", "active") — drives the
      // finer-grained setup lifecycle messaging while not yet verified.
      cloudflareStatus?: string | null;
      lastError: string | null;
      verifiedAt: string | null;
      lastCheckedAt: string | null;
      // SSL/TLS is provisioned + auto-renewed by Cloudflare; these reflect
      // Cloudflare's reported ssl.status / certificate expiry.
      sslStatus?: "pending" | "secured" | "error";
      sslExpiresAt?: string | null;
      sslCheckedAt?: string | null;
      sslError?: string | null;
    } | null;
    recordType?: string;
    target: string | null;
    ttl?: number;
  };
  const [customDomainModalOpen, setCustomDomainModalOpen] = useState(false);
  // Brand-level Meta Pixel settings modal (opened from the home-view header,
  // next to Leads / Custom domain).
  const [metaPixelModalOpen, setMetaPixelModalOpen] = useState(false);
  const [customDomainInput, setCustomDomainInput] = useState("");

  const { data: customDomainState } = useQuery<CustomDomainState>({
    queryKey: [`/api/brands/${brandId}/lp-express/custom-domain`, surface],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/lp-express/custom-domain`, {
        credentials: "include",
      });
      if (!res.ok) return { domain: null, target: null } as CustomDomainState;
      return res.json();
    },
    enabled: !!brandId,
    // While DNS is still propagating, or DNS is verified but the certificate
    // hasn't been observed yet, poll so the verified/secured flip shows up on
    // its own. Read status off the query's own data to avoid a TDZ on external
    // state.
    refetchInterval: (query) => {
      const d = query.state.data?.domain;
      if (!d) return false;
      if (d.status === "pending") return 15000;
      if (d.status === "verified" && d.sslStatus !== "secured") return 15000;
      return false;
    },
  });
  const brandDomain = customDomainState?.domain ?? null;
  const brandDomainVerified = brandDomain?.status === "verified";
  // Finer-grained Cloudflare setup lifecycle for the not-yet-verified state, so
  // the badge/messaging can distinguish "registering" → "pending DNS" →
  // "securing SSL" → "active". Derived from Cloudflare's raw hostname status
  // plus our SSL mapping.
  const brandDomainLifecycle: "verified" | "registering" | "dns" | "ssl" | null = (() => {
    if (!brandDomain) return null;
    if (brandDomain.status === "verified") return "verified";
    const cf = (brandDomain.cloudflareStatus || "").toLowerCase();
    if (!cf) return "registering";
    // Cloudflare reports the hostname active but the certificate isn't issued
    // yet → SSL provisioning. Otherwise the CNAME/DCV is still pending.
    if (cf === "active") return "ssl";
    return "dns";
  })();

  const connectDomainMutation = useMutation({
    mutationFn: async (hostname: string) => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/lp-express/custom-domain`,
        { hostname },
      );
      return parseJsonResponse(res);
    },
    onSuccess: () => {
      setCustomDomainInput("");
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/custom-domain`],
      });
      toast({
        title: "Domain connected",
        description: "Add the CNAME record at your DNS provider, then check it.",
      });
    },
    onError: (e: any) => {
      toast({
        title: "Couldn't connect domain",
        description: e?.message || "Could not connect that domain.",
        variant: "destructive",
      });
    },
  });

  const checkDomainMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/lp-express/custom-domain/check`,
        {},
      );
      return parseJsonResponse(res);
    },
    onSuccess: (data: CustomDomainState) => {
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/custom-domain`],
      });
      if (data?.domain?.status === "verified") {
        toast({
          title: "Domain verified",
          description: "Your pages can now be served on your custom domain.",
        });
      } else {
        toast({
          title: "Not verified yet",
          description:
            data?.domain?.lastError || "DNS isn't pointing here yet. Try again shortly.",
          variant: "destructive",
        });
      }
    },
    onError: (e: any) => {
      toast({
        title: "Check failed",
        description: e?.message || "Could not check the domain.",
        variant: "destructive",
      });
    },
  });

  const removeDomainMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "DELETE",
        `/api/brands/${brandId}/lp-express/custom-domain`,
        {},
      );
      return parseJsonResponse(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/custom-domain`],
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/published-pages`],
      });
      toast({ title: "Domain removed" });
    },
    onError: (e: any) => {
      toast({
        title: "Couldn't remove domain",
        description: e?.message || "Could not remove the domain.",
        variant: "destructive",
      });
    },
  });

  // Per-page opt-in: serve this page on the brand's custom domain.
  const toggleCustomDomainEnabledMutation = useMutation({
    mutationFn: async (vars: { pageId: string; enabled: boolean }) => {
      const res = await apiRequest(
        "PATCH",
        `/api/landing-pages/${vars.pageId}/custom-domain-enabled`,
        { enabled: vars.enabled },
      );
      return parseJsonResponse(res);
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: ["/api/landing-pages", vars.pageId, "published"],
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/published-pages`],
      });
    },
    onError: (e: any) => {
      toast({
        title: "Couldn't update setting",
        description: e?.message || "Could not change custom-domain serving.",
        variant: "destructive",
      });
    },
  });

  // Brand-level Meta Pixel ID (entered once inside LP Express; shared by every
  // landing page of this brand). Empty string clears it.
  const saveMetaPixelIdMutation = useMutation({
    mutationFn: async (value: string) => {
      const res = await apiRequest(
        "PATCH",
        `/api/brands/${brandId}/lp-express/meta-pixel`,
        { metaPixelId: value },
      );
      return parseJsonResponse(res);
    },
    onSuccess: (data: any) => {
      setMetaPixelIdInput(null);
      queryClient.invalidateQueries({ queryKey: ["/api/brands", brandId] });
      // Pixel state affects the publish snapshot diff → refresh staleness.
      if (pageId) {
        queryClient.invalidateQueries({
          queryKey: ["/api/landing-pages", pageId, "published"],
        });
      }
      toast({ title: data?.metaPixelId ? "Pixel ID saved" : "Pixel ID removed" });
    },
    onError: (e: any) => {
      toast({
        title: "Couldn't save Pixel ID",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  // Per-page opt-in: install the brand's Meta Pixel on this page's PUBLISHED
  // output. Takes effect on the next publish (surfaced as unpublished changes).
  const toggleMetaPixelEnabledMutation = useMutation({
    mutationFn: async (vars: { pageId: string; enabled: boolean }) => {
      const res = await apiRequest(
        "PATCH",
        `/api/landing-pages/${vars.pageId}/meta-pixel-enabled`,
        { enabled: vars.enabled },
      );
      return parseJsonResponse(res);
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: ["/api/landing-pages", vars.pageId, "published"],
      });
    },
    onError: (e: any) => {
      toast({
        title: "Couldn't update setting",
        description: e?.message || "Could not change the Meta Pixel setting.",
        variant: "destructive",
      });
    },
  });

  // Live preview of how the typed slug will be normalized server-side.
  const normalizedSlugDraft = lpSlugifyClient(slugDraft);
  const slugUnchanged = normalizedSlugDraft === (publishState?.slug ?? "");
  const slugDraftValid = !!normalizedSlugDraft && normalizedSlugDraft !== "page";

  const submitSlugRename = () => {
    if (!slugDraftValid) {
      toast({
        title: "Enter a valid URL",
        description: "Use letters, numbers, and dashes.",
        variant: "destructive",
      });
      return;
    }
    if (slugUnchanged) {
      setSlugDraft("");
      return;
    }
    setSlugConfirmOpen(true);
  };

  // --- Published pages list (re-share / manage all live pages for the brand) --
  type PublishedPageRow = {
    id: string;
    slug: string;
    brandSlug: string;
    pageId: string | null;
    status: string;
    seoTitle: string | null;
    publishedAt: string | null;
    customDomainEnabled?: boolean;
    pageType?: "lp" | "hp";
  };
  const { data: publishedPages = [], isLoading: loadingPublishedPages, error: publishedPagesError, refetch: refetchPublishedPages } = useQuery<PublishedPageRow[]>({
    queryKey: [`/api/brands/${brandId}/lp-express/published-pages`, surface],
    queryFn: async () => {
      const res = await globalThis.fetch(`/api/brands/${brandId}/lp-express/published-pages?surface=${surface}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load published pages");
      return res.json();
    },
    enabled: !!brandId,
  });

  const copyPublishedLink = async (brandSlug: string, slug: string) => {
    const url = `${window.location.origin}/p/${brandSlug}/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "Link copied" });
    } catch {
      toast({ title: "Copy failed", description: "Could not copy the link.", variant: "destructive" });
    }
  };

  const togglePublishedMutation = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: "unpublish" | "republish" }) => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/lp-express/published-pages/${id}/${action}`,
        {},
      );
      return parseJsonResponse(res);
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/lp-express/published-pages`] });
      // Invalidate the per-page publish-status cache for EVERY page (not just the
      // builder's currently-loaded one, which is empty on the home view) so that
      // clicking into the toggled page shows its fresh status instead of stale.
      queryClient.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          q.queryKey[0] === "/api/landing-pages" &&
          q.queryKey[2] === "published",
      });
      toast({
        title: vars.action === "unpublish" ? "Page unpublished" : "Page republished",
        description:
          vars.action === "unpublish"
            ? "The public page is now offline."
            : "The page is live again.",
      });
    },
    onError: (e: any) => {
      toast({ title: "Action failed", description: e?.message || "Could not update the page.", variant: "destructive" });
    },
  });

  // Unified list: every LP Express run (incl. drafts) enriched with its publish
  // status, plus any published rows whose source run no longer exists. Replaces
  // the separate "Previous runs" and "Published pages" lists.
  type MergedPageRow = {
    key: string;
    runId: string;
    pageId: string | null;
    productName: string;
    angleName: string;
    personaName: string;
    createdAt: string | null;
    publishStatus: "draft" | "published" | "unpublished";
    published: PublishedPageRow | null;
    // Template key snapshotted on the run's page generatedContent (e.g. "quiz",
    // "long-form"). Null for published-only rows (their list payload omits it).
    templateType: string | null;
  };
  const mergedPages = useMemo<MergedPageRow[]>(() => {
    const byPageId = new Map<string, PublishedPageRow>();
    for (const p of publishedPages) {
      if (p.pageId) byPageId.set(p.pageId, p);
    }
    const rows: MergedPageRow[] = previousRuns.map((r) => {
      const pub = r.pageId ? byPageId.get(r.pageId) ?? null : null;
      const publishStatus: MergedPageRow["publishStatus"] =
        pub?.status === "published"
          ? "published"
          : pub?.status === "unpublished"
            ? "unpublished"
            : "draft";
      return {
        key: r.id,
        runId: r.id,
        pageId: r.pageId,
        productName:
          (isSmbHomeAttempt(r.generatedContent)
            ? pub?.seoTitle || (r.generatedContent as any)?.smbHomeCopy?.headline || r.generatedContent?.hero?.headline || "Home page"
            : null) || (r as any).productNameSnapshot ||
          r.resolvedProductName ||
          "(no product)",
        angleName: (r as any).angleNameSnapshot || "",
        personaName: (r as any).personaNameSnapshot || "",
        createdAt: (r.createdAt as any) ?? null,
        publishStatus,
        published: pub,
        templateType: ((r as any).generatedContent?.templateType as string) ?? null,
      };
    });
    const seenPageIds = new Set(
      previousRuns.map((r) => r.pageId).filter(Boolean) as string[],
    );
    for (const p of publishedPages) {
      if (p.pageId && seenPageIds.has(p.pageId)) continue;
      rows.push({
        key: `pub-${p.id}`,
        runId: "",
        pageId: p.pageId,
        productName: p.seoTitle || (p.pageType === "hp" ? "Home page" : "(published page)"),
        angleName: "",
        personaName: "",
        createdAt: p.publishedAt,
        publishStatus: p.status === "published" ? "published" : "unpublished",
        published: p,
        templateType: null,
      });
    }
    if (surface !== "lp-express-2") return rows;
    const homeRunIds = new Set(previousRuns.filter((r) => isSmbHomeAttempt(r.generatedContent)).map((r) => r.id));
    return rows.filter((row) => (row.runId ? homeRunIds.has(row.runId) : row.published?.pageType === "hp") === (pageType === "hp"));
  }, [previousRuns, publishedPages, surface, pageType]);

  // Dashboard stat-card counts derived from the merged list. Live = currently
  // published; Drafts = everything not live (true drafts + unpublished) so the
  // three cards always partition the total cleanly. The row pills still
  // distinguish Draft from Unpublished within that group.
  const pagesLiveCount = useMemo(
    () => mergedPages.filter((r) => r.publishStatus === "published").length,
    [mergedPages],
  );
  const pagesDraftCount = mergedPages.length - pagesLiveCount;

  // Search + filter + sort applied client-side over the merged list. Search
  // matches page/product name, persona, or angle.
  const visiblePages = useMemo(() => {
    const q = pagesSearch.trim().toLowerCase();
    let rows = mergedPages;
    if (q) {
      rows = rows.filter((r) =>
        [r.productName, r.personaName, r.angleName].some((v) =>
          (v || "").toLowerCase().includes(q),
        ),
      );
    }
    if (pagesFilter === "live") {
      rows = rows.filter((r) => r.publishStatus === "published");
    } else if (pagesFilter === "draft") {
      rows = rows.filter((r) => r.publishStatus !== "published");
    }
    const ts = (r: MergedPageRow) => (r.createdAt ? new Date(r.createdAt).getTime() : 0);
    const statusRank: Record<MergedPageRow["publishStatus"], number> = {
      published: 0,
      unpublished: 1,
      draft: 2,
    };
    const sorted = [...rows];
    switch (pagesSort) {
      case "newest":
        sorted.sort((a, b) => ts(b) - ts(a));
        break;
      case "oldest":
        sorted.sort((a, b) => ts(a) - ts(b));
        break;
      case "name":
        sorted.sort((a, b) => a.productName.localeCompare(b.productName));
        break;
      case "status":
        sorted.sort(
          (a, b) => statusRank[a.publishStatus] - statusRank[b.publishStatus] || ts(b) - ts(a),
        );
        break;
    }
    return sorted;
  }, [mergedPages, pagesSearch, pagesFilter, pagesSort]);

  // Client-side pagination over the filtered list.
  const pagesPageCount = Math.max(1, Math.ceil(visiblePages.length / PAGES_PER_PAGE));
  useEffect(() => {
    setPagesPage(1);
  }, [pagesSearch, pagesFilter, pagesSort, pageType, brandId]);
  useEffect(() => {
    if (pagesPage > pagesPageCount) setPagesPage(pagesPageCount);
  }, [pagesPage, pagesPageCount]);
  const pagedPages = useMemo(
    () => visiblePages.slice((pagesPage - 1) * PAGES_PER_PAGE, pagesPage * PAGES_PER_PAGE),
    [visiblePages, pagesPage],
  );

  // Brand-video fetch state. Declared ABOVE the brand query because the query's
  // refetchInterval closure reads it (reading state declared below the query
  // would be a temporal-dead-zone ReferenceError).
  const [videosFetchActive, setVideosFetchActive] = useState(false);
  const videoFetchStarted = useRef(false);

  const { data: brand } = useQuery<Brand | null>({
    queryKey: ["/api/brands", brandId, surface],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}`, { credentials: "include" });
      if (res.status === 401) return null;
      if (!res.ok) throw new Error("Failed to load brand");
      return res.json();
    },
    enabled: !!brandId,
    // While a brand-video fetch is in flight, poll the brand so the newly
    // mirrored video URLs surface in the preview as soon as they're saved.
    refetchInterval: videosFetchActive ? 5000 : false,
  });

  // LP2's picker follows EE2's design-system list/read/save contract, while
  // retaining LP2's own brand and entitlement authorization on the server.
  const {
    data: lpDesignSystemsData,
    isLoading: lpDesignSystemsLoading,
    error: lpDesignSystemsError,
  } = useQuery<LpDesignSystemsResponse>({
    queryKey: ["/api/lp-express/brand-design-systems", brandId, surface],
    queryFn: async () => {
      const res = await fetch(
        `/api/lp-express/brand-design-systems?brandId=${encodeURIComponent(brandId ?? "")}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error("Couldn't load design systems.");
      return res.json();
    },
    enabled: surface === "lp-express-2" && !!brandId,
    retry: false,
  });
  const accessibleLpDesignSystems = lpDesignSystemsData?.designSystems ?? [];
  const writeOrigin = designSystemWriteOriginRef.current;
  const isDesignSystemSelectionPending =
    (pendingDesignSystemSelection?.brandId === brandId &&
      pendingDesignSystemSelection.surface === surface) ||
    (writeOrigin?.brandId === brandId && writeOrigin.surface === surface);
  const hasFailedDesignSystemSelection =
    failedDesignSystemSelection?.brandId === brandId &&
    failedDesignSystemSelection.surface === surface;
  const persistedLpDesignSystemValue = accessibleLpDesignSystems.some(
    (system) => system.id === lpDesignSystemsData?.selectedDesignSystemId,
  )
    ? lpDesignSystemsData!.selectedDesignSystemId!
    : NO_LP_DESIGN_SYSTEM_VALUE;
  const hasUnavailablePersistedLpDesignSystem = Boolean(
    lpDesignSystemsData?.selectedDesignSystemId &&
      !accessibleLpDesignSystems.some(
        (system) => system.id === lpDesignSystemsData.selectedDesignSystemId,
      ),
  );
  const selectedLpDesignSystemValue =
    pendingDesignSystemSelection?.brandId === brandId &&
    pendingDesignSystemSelection.surface === surface
      ? pendingDesignSystemSelection.value
      : persistedLpDesignSystemValue;

  const designSystemMutation = useMutation({
    mutationFn: async ({
      origin,
      value,
    }: {
      origin: Pick<PendingDesignSystemSelection, "brandId" | "surface">;
      value: string;
    }) => {
      const designSystemId = value === NO_LP_DESIGN_SYSTEM_VALUE ? null : value;
      // Use the raw client so surface remains a query routing hint rather than
      // an unrecognized field in this deliberately strict save payload.
      const res = await rawApiRequest(
        "PATCH",
        withLpExpressSurfaceUrl("/api/lp-express/brand-design-system", origin.surface),
        { brandId: origin.brandId, designSystemId },
      );
      return (await res.json()) as { ok: true; designSystemId: string | null };
    },
    onSuccess: (saved, variables) => {
      const queryKey = ["/api/lp-express/brand-design-systems", variables.origin.brandId, variables.origin.surface];
      queryClient.setQueryData<LpDesignSystemsResponse>(queryKey, (current) =>
        current
          ? { ...current, selectedDesignSystemId: saved.designSystemId }
          : current,
      );
      queryClient.invalidateQueries({ queryKey });
      if (
        designSystemWriteOriginRef.current?.brandId === variables.origin.brandId &&
        designSystemWriteOriginRef.current?.surface === variables.origin.surface
      ) {
        setFailedDesignSystemSelection((current) =>
          current?.brandId === variables.origin.brandId && current.surface === variables.origin.surface
            ? null
            : current,
        );
        toast({ title: "Design system saved." });
      }
    },
    onError: (error: any, variables) => {
      if (
        designSystemWriteOriginRef.current?.brandId === variables.origin.brandId &&
        designSystemWriteOriginRef.current?.surface === variables.origin.surface
      ) {
        setFailedDesignSystemSelection({ ...variables.origin, value: variables.value });
        toast({
          title: "Couldn't save design system",
          description: error.message || "Try again.",
          variant: "destructive",
        });
      }
    },
    onSettled: (_data, _error, variables) => {
      if (
        designSystemWriteOriginRef.current?.brandId === variables.origin.brandId &&
        designSystemWriteOriginRef.current?.surface === variables.origin.surface
      ) {
        designSystemWriteOriginRef.current = null;
      }
      setPendingDesignSystemSelection((current) =>
        current?.brandId === variables.origin.brandId && current.surface === variables.origin.surface
          ? null
          : current,
      );
    },
  });
  const saveLpDesignSystem = (value: string) => {
    if (!brandId || surface !== "lp-express-2") return;
    const origin = { brandId, surface };
    designSystemWriteOriginRef.current = origin;
    setFailedDesignSystemSelection((current) =>
      current?.brandId === origin.brandId && current.surface === origin.surface ? null : current,
    );
    setPendingDesignSystemSelection({ ...origin, value });
    designSystemMutation.mutate({ origin, value });
  };

  // ── Brand-video fetch ──────────────────────────────────────────────────────
  // When the brand has no saved Farmstand video for one or both slots, kick off
  // a one-time background fetch (scrape the brand's Instagram → mirror to R2 →
  // save the URLs) and poll the brand until they fill in. The renderer shows a
  // loading spinner (not the dashed TODO placeholder) for missing slots while
  // videosFetchActive is true.
  const videoMakingUrl = (brand as any)?.videoMakingUrl ?? null;
  const videoLifestyleUrl = (brand as any)?.videoLifestyleUrl ?? null;

  useEffect(() => {
    if (!brand || videoFetchStarted.current) return;
    if (studioDesign?.rendererTemplateKey === "smb-home") return;
    if (new URLSearchParams(search).has("runId") && !selectedRunId) return;
    if (videoMakingUrl && videoLifestyleUrl) return; // both slots already filled
    videoFetchStarted.current = true;
    setVideosFetchActive(true);
    apiRequest("POST", `/api/brands/${brandId}/lp-express/fetch-videos`, {}).catch(() => {
      // Service is best-effort; a failure just means we stop showing the spinner.
      setVideosFetchActive(false);
    });
  }, [brand, brandId, videoMakingUrl, videoLifestyleUrl, studioDesign, selectedRunId, search]);

  // Stop polling/spinner once both slots are filled.
  useEffect(() => {
    if (videosFetchActive && videoMakingUrl && videoLifestyleUrl) {
      setVideosFetchActive(false);
    }
  }, [videosFetchActive, videoMakingUrl, videoLifestyleUrl]);

  // Safety timeout — the scrape can run for several minutes; never spin forever.
  useEffect(() => {
    if (!videosFetchActive) return;
    const t = setTimeout(() => setVideosFetchActive(false), 6 * 60 * 1000);
    return () => clearTimeout(t);
  }, [videosFetchActive]);

  const { data: templateTypes = [] } = useQuery<LandingPageTemplateType[]>({
    queryKey: ["/api/landing-page-template-types", { brandId }, surface],
    queryFn: async () => {
      const res = await fetch(
        `/api/landing-page-template-types?brandId=${encodeURIComponent(brandId ?? "")}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error("Failed to load templates");
      return res.json();
    },
    enabled: !!brandId,
  });
  const landingTemplateTypes = useMemo(() => surface === "lp-express-2"
    ? templateTypes.filter((template) => template.rendererTemplateKey !== "smb-home")
    : templateTypes, [templateTypes, surface]);

  // Map a template key (e.g. "quiz", "long-form") to its human label for the
  // page-list badge. Falls back to a title-cased version of the key so an
  // unknown/legacy type still reads cleanly instead of showing a raw slug.
  const templateTypeLabel = useMemo(() => {
    const byKey = new Map(templateTypes.map((t) => [t.key, t.label]));
    return (key: string | null): string => {
      // Legacy pages predate templateType persistence; long-form was the
      // historical default, so an unset type reads as "Long Form" (not a
      // generic "Landing Page").
      if (!key) return "Long Form";
      return (
        byKey.get(key) ??
        key
          .split(/[-_]/)
          .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
          .join(" ")
      );
    };
  }, [templateTypes]);

  const { data: personas = [], isLoading: loadingPersonas } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`, surface],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/personas`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load personas");
      return res.json();
    },
    enabled: !!brandId,
  });
  const { data: products = [], isLoading: loadingProducts } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`, surface],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/products`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load products");
      return res.json();
    },
    enabled: !!brandId,
  });
  const { data: reviewsByProduct = {} } = useQuery<Record<string, ProductReview[]>>({
    queryKey: [`/api/brands/${brandId}/product-reviews`, surface],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/product-reviews`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load product reviews");
      return res.json();
    },
    enabled: !!brandId,
  });
  const { data: angles = [], isLoading: loadingAngles } = useQuery<NarrativeAngle[]>({
    queryKey: [`/api/brands/${brandId}/narrative-angles`, surface],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/narrative-angles`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load narrative angles");
      return res.json();
    },
    enabled: !!brandId,
  });
  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: [`/api/brands/${brandId}/projects`, surface],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/projects`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load projects");
      return res.json();
    },
    enabled: !!brandId,
  });
  const { data: sales = [] } = useQuery<SalesDiscount[]>({
    queryKey: [`/api/brands/${brandId}/sales-discounts`, surface],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/sales-discounts`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load sales");
      return res.json();
    },
    enabled: !!brandId,
  });

  // Keep the selected template valid for the active brand. If the current
  // selection isn't among the brand-permitted templates (e.g. brand switched,
  // or the hard default isn't allowed), fall back to the first permitted one.
  useEffect(() => {
    if (landingTemplateTypes.length === 0) return;
    if (!landingTemplateTypes.some((t) => t.key === templateTypeKey)) {
      setTemplateTypeKey(landingTemplateTypes[0].key);
    }
  }, [landingTemplateTypes, templateTypeKey]);

  const selectedPersona = useMemo(
    () => personas.find((p) => p.id === personaId) ?? null,
    [personas, personaId],
  );
  const selectedProduct = useMemo(
    () => products.find((p) => p.id === productId) ?? null,
    [products, productId],
  );
  // When the selected product is a bundle, expand it into its member SKUs so
  // the renderer shows the included products (multi-product "Included in kit"
  // UI). Non-bundles resolve to a single-item list.
  const renderProducts = useMemo(
    () => expandBundleProducts(selectedProduct, products),
    [selectedProduct, products],
  );
  const selectedAngle = useMemo(
    () => angles.find((a) => a.id === angleId) ?? null,
    [angles, angleId],
  );
  const activeSale = useMemo(
    () => sales.find((s) => s.status === "active") ?? null,
    [sales],
  );
  // The sale the user has chosen in the inputs panel. "__none__"/"" => no
  // discount. This drives both the live preview pricing and the saleId sent to
  // every generation request (so generated copy reflects the offer too).
  const selectedSale = useMemo(
    () =>
      saleId && saleId !== "__none__"
        ? (sales.find((s) => s.id === saleId) ?? null)
        : null,
    [sales, saleId],
  );
  const saleIdForRequest = saleId && saleId !== "__none__" ? saleId : null;

  // Seed the sale picker once sales load: default to "No discount" even when the
  // brand has an active sale, so the user must opt in to applying one. They can
  // still pick the active sale (or any other) from the picker. Skipped if a run
  // hydration already set the selection.
  useEffect(() => {
    if (saleInitRef.current) return;
    if (sales.length === 0) return;
    if (saleId !== "") return;
    saleInitRef.current = true;
    setSaleId("__none__");
  }, [sales, activeSale, saleId]);

  // --- Checkout URL --------------------------------------------------------
  // The default (when nothing is configured) resolves to the product PDP URL,
  // then the brand URL. The displayed/effective value uses the configured
  // value when present, otherwise the default. This same value drives the live
  // preview's CTAs and is what gets persisted/published.
  const defaultCheckoutUrl = useMemo(
    () => resolveLpCheckoutUrl(null, (selectedProduct as any)?.pdpUrl ?? null, brand?.url ?? null),
    [selectedProduct, brand],
  );
  const checkoutUrl = studioDesign?.rendererTemplateKey === "smb-home"
    ? smbPageMetadata.ctaUrl || "" : checkoutUrlInput ?? defaultCheckoutUrl;
  // CTA/checkout buttons accept either an http(s) URL or a same-page #section
  // anchor (so a buy button can jump to e.g. the Membership/Pricing section).
  // validateLinkHref allows both; validateCheckoutUrl rejected anchors.
  const checkoutUrlValidationError = validateLinkHref(checkoutUrl);
  const checkoutUrlIsEmpty = checkoutUrl.trim() === "";

  // On-page sections the checkout/CTA buttons (and any link) can target. Mirrors
  // the renderer's `linkTargets`: visible sections only, excluding the
  // announcement bar and the sticky nav (not meaningful scroll targets).
  const checkoutSectionTargets = useMemo(
    () =>
      sectionLayout
        .filter((s) => s.type !== "announcement" && s.type !== "stickyNav" && !s.hidden)
        .map((s) => ({
          anchorId: lpSectionAnchorId(s.type as LpSectionType),
          label: LP_SECTION_REGISTRY[s.type as LpSectionType]?.label ?? s.type,
        })),
    [sectionLayout],
  );

  // When the checkout URL is a same-page anchor matching a known section,
  // reflect it in the picker; otherwise the picker sits on "Custom URL…".
  const checkoutAnchorValue =
    checkoutUrl.startsWith("#") &&
    checkoutSectionTargets.some((t) => `#${t.anchorId}` === checkoutUrl)
      ? checkoutUrl.slice(1)
      : "";

  // Mirror the server's first-publish slug derivation (headline → product →
  // brand) so the user can see the actual public URL BEFORE publishing, even
  // when they don't type a custom slug. UX-only; the server re-derives + ensures
  // uniqueness at publish (may append a short suffix on collision).
  const autoSlugPreview = useMemo(
    () =>
      lpSlugifyClient(
        hero?.headline ||
          (selectedProduct as any)?.name ||
          (brand as any)?.name ||
          "page",
      ) || "page",
    [hero?.headline, selectedProduct, brand],
  );

  // Brand segment of the public /p/{brandSlug}/{slug} URL. Prefer the snapshot
  // stored on the published row; before first publish fall back to the live
  // brand slug. UX-only preview; the server stores the authoritative snapshot.
  const previewBrandSlug = lpExpressBrandSlug(brand as any, publishState?.brandSlug);

  // Public pages live at /p/{brandSlug}/{slug}. Prefer the brand-slug SNAPSHOT
  // stored on the published row so the link stays correct even if the brand's
  // slug later changes. If that snapshot is missing (e.g. legacy row), fall back
  // to the live/preview brand slug so the link never silently disappears — the
  // only requirement is that the page is published with a slug.
  const publicUrl = lpExpressPublicUrl({
    origin: window.location.origin,
    slug: publishState?.slug,
    rowBrandSlug: publishState?.brandSlug,
    fallbackBrandSlug: previewBrandSlug,
  });
  const copyPublicUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast({ title: "Link copied", description: publicUrl });
    } catch {
      toast({ title: "Copy failed", description: "Could not copy the link.", variant: "destructive" });
    }
  };

  // --- Meta Pixel derived state ------------------------------------------
  // Saved brand-level Pixel ID (source of truth) vs the local edit buffer.
  const savedMetaPixelId = (((brand as any)?.metaPixelId ?? "") as string).trim();
  const savedMetaPixelIdValid = META_PIXEL_ID_RE.test(savedMetaPixelId);
  const metaPixelIdDraft = metaPixelIdInput ?? savedMetaPixelId;
  const metaPixelIdDraftTrimmed = metaPixelIdDraft.trim();
  // Empty is valid (= clear the ID); anything else must be 10–20 digits.
  const metaPixelIdDraftValid =
    metaPixelIdDraftTrimmed === "" || META_PIXEL_ID_RE.test(metaPixelIdDraftTrimmed);
  // "Active" = toggle on + a valid saved brand ID → the NEXT publish installs
  // the pixel. Drives the builder-chrome indicator above the preview (the
  // preview itself never loads fbevents.js — it's a React canvas, not the
  // published HTML, so no PageView can ever fire from the builder).
  const metaPixelActive = !!publishState?.metaPixelEnabled && savedMetaPixelIdValid;

  const saveCheckoutUrlMutation = useMutation({
    mutationKey: LP_PENDING_SAVE_KEY,
    mutationFn: async (value: string) => {
      const res = await apiRequest("PATCH", `/api/landing-pages/${pageId}/checkout-url`, {
        checkoutUrl: value,
      });
      return parseJsonResponse(res);
    },
    onSuccess: () => {
      recordSnapshot("checkout-url");
    },
    onError: (e: any) => {
      toast({
        title: "Couldn't save checkout link",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  // Edit handler: update local state, validate, and debounce-persist valid
  // values. Empty is valid (=> inert buttons) and is persisted as "".
  const handleCheckoutUrlChange = (value: string) => {
    setCheckoutUrlInput(value);
    if (!pageId) return;
    if (checkoutSaveTimer.current) clearTimeout(checkoutSaveTimer.current);
    if (validateLinkHref(value)) return; // don't persist invalid URLs/anchors
    checkoutSaveTimer.current = setTimeout(() => {
      saveCheckoutUrlMutation.mutate(value.trim());
    }, 600);
  };

  // Flush any pending debounced save immediately (on blur, and right before
  // publishing) so the persisted value always reflects the latest edit.
  // Returns true when the effective value is safely persisted (or there is
  // nothing to persist); returns false when the edit is invalid or the save
  // failed, so callers (e.g. publish) can abort.
  const flushCheckoutUrl = async (): Promise<boolean> => {
    if (!pageId) return true;
    if (checkoutSaveTimer.current) {
      clearTimeout(checkoutSaveTimer.current);
      checkoutSaveTimer.current = null;
    }
    if (checkoutUrlInput === null) return true; // nothing edited => keep default
    if (validateLinkHref(checkoutUrlInput)) return false; // invalid edit
    try {
      await saveCheckoutUrlMutation.mutateAsync(checkoutUrlInput.trim());
      return true;
    } catch {
      // onError toast already surfaced the failure.
      return false;
    }
  };

  // Block until every in-flight content/section/checkout save (tagged with
  // LP_PENDING_SAVE_KEY) has settled, so a Publish click can't snapshot stale
  // generatedContent while a just-made edit is still committing. Returns true
  // once everything settled, or false if it timed out with saves still pending
  // (the caller MUST abort publish in that case rather than snapshot stale data).
  const waitForPendingSaves = async (timeoutMs = 10000): Promise<boolean> => {
    const start = Date.now();
    while (queryClient.isMutating({ mutationKey: LP_PENDING_SAVE_KEY }) > 0) {
      if (Date.now() - start > timeoutMs) return false;
      await new Promise((r) => setTimeout(r, 80));
    }
    return true;
  };

  // Persist the current section layout to the page. Sends the COMPLETE ordered
  // list of {type,hidden} (deleted sections are simply absent); the server
  // validates + stores it as generatedContent.sections, which getLpSections reads.
  const saveSectionOrderMutation = useMutation({
    mutationKey: LP_PENDING_SAVE_KEY,
    mutationFn: async (vars: { layout: LpSectionLayoutItem[]; actionType: string }) => {
      const sections = vars.layout.map(({ type, hidden }) => ({ type, hidden }));
      const res = await apiRequest(
        "PATCH",
        `/api/landing-pages/${pageId}/lp-express/section-order`,
        { sections },
      );
      return parseJsonResponse(res);
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/runs`],
      });
      // The edit bumped page.updatedAt, so a live page is now out of date —
      // refresh the publish status so the "Unpublished changes" badge appears.
      queryClient.invalidateQueries({
        queryKey: ["/api/landing-pages", pageId, "published"],
      });
      recordSnapshot(vars.actionType);
    },
    onError: (e: any) => {
      toast({
        title: "Couldn't save section layout",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  // Apply a layout change locally (instant live-preview) and persist it when the
  // draft has a pageId. Single helper used by move / hide / delete / reset.
  const applySectionLayout = (next: LpSectionLayoutItem[], actionType: string) => {
    setSectionLayout(next);
    if (pageId) saveSectionOrderMutation.mutate({ layout: next, actionType });
  };

  // Move a section one slot up or down by type.
  const moveSection = (type: LpSectionType, dir: "up" | "down") => {
    const idx = sectionLayout.findIndex((s) => s.type === type);
    if (idx < 0) return;
    const to = dir === "up" ? idx - 1 : idx + 1;
    if (to < 0 || to >= sectionLayout.length) return;
    const next = [...sectionLayout];
    [next[idx], next[to]] = [next[to], next[idx]];
    applySectionLayout(next, "section-moved");
  };

  // Toggle a section's hidden flag (kept in the layout so it can be restored).
  const toggleSectionHidden = (type: LpSectionType) => {
    const cur = sectionLayout.find((s) => s.type === type);
    const willHide = !cur?.hidden;
    const next = sectionLayout.map((s) => (s.type === type ? { ...s, hidden: !s.hidden } : s));
    applySectionLayout(next, willHide ? "section-hidden" : "section-shown");
  };

  // Delete a section (removed from the layout; restorable via "Reset layout").
  const deleteSection = (type: LpSectionType) => {
    const next = sectionLayout.filter((s) => s.type !== type);
    if (next.length === 0) return;
    applySectionLayout(next, "section-deleted");
  };

  // Restore the template's canonical layout (all sections visible, default order).
  const resetSectionLayout = () =>
    applySectionLayout(canonicalSectionLayout(templateSectionTypes), "section-layout-reset");

  const sectionLayoutIsCustom = useMemo(
    () => !sameSectionLayout(sectionLayout, canonicalSectionLayout(templateSectionTypes)),
    [sectionLayout, templateSectionTypes],
  );

  // The COMPLETE ordered section list (with hidden flags) fed to the renderer so
  // the live preview reflects the chosen layout. getLpSections trusts this for
  // order + membership + hidden and re-derives each section's fields from the
  // content.
  const lpSectionsForRender = useMemo(
    () => sectionLayout.map(({ type, hidden }) => ({ id: type, type, hidden })),
    [sectionLayout],
  );

  // ── In-canvas CONTENT editing (text / item hide-delete) ────────────────────
  // Each editable top-level content key maps to its live state value + setter,
  // so an edit at path [root, ...rest] is applied optimistically by deep-setting
  // into a synthetic { [root]: value } container and writing the new root value
  // back through the matching setter. The same path is sent to the server, which
  // applies the identical op to persisted generatedContent.
  const lpContentState: Record<string, { value: any; set: (v: any) => void }> = {
    hero: { value: hero, set: setHero as (v: any) => void },
    leadin: { value: leadin, set: setLeadin as (v: any) => void },
    advisory: { value: advisory, set: setAdvisory as (v: any) => void },
    showcase: { value: showcase, set: setShowcase as (v: any) => void },
    productContext: { value: productContext, set: setProductContext as (v: any) => void },
    stations: { value: stations, set: setStations as (v: any) => void },
    villain: { value: villain, set: setVillain as (v: any) => void },
    originCopy: { value: originCopy, set: setOriginCopy as (v: any) => void },
    papabearLabel: {
      value: papabearLabel,
      set: setPapabearLabel as (v: any) => void,
    },
    quotes: { value: quotes, set: setQuotes as (v: any) => void },
    timeline: { value: timeline, set: setTimeline as (v: any) => void },
    membership: { value: membership, set: setMembership as (v: any) => void },
    alternatives: { value: alternatives, set: setAlternatives as (v: any) => void },
    philosophy: { value: philosophy, set: setPhilosophy as (v: any) => void },
    differentiator: { value: differentiator, set: setDifferentiator as (v: any) => void },
    faq: { value: faq, set: setFaq as (v: any) => void },
    reviews: { value: reviews, set: setReviews as (v: any) => void },
    finalCta: { value: finalCta, set: setFinalCta as (v: any) => void },
    footer: { value: footer, set: setFooter as (v: any) => void },
    proof: { value: proof, set: setProof as (v: any) => void },
    // Product-hero sections (phBuyBox, phTrustBar, …) each live under their own
    // generatedContent key, held together in productHeroSections. Registering
    // each key lets in-canvas path edits + optimistic preview flow through the
    // same applyContentOp path the long-form sections use.
    ...Object.fromEntries(
      ([...LP_PRODUCT_HERO_BLOCK_KEYS, ...LP_SMB_HOME_BLOCK_KEYS] as readonly string[]).map((k) => [
        k,
        {
          value: (productHeroSections as Record<string, any>)[k],
          set: (v: any) =>
            setProductHeroSections((prev) => ({ ...prev, [k]: v })),
        },
      ]),
    ),
    // Problem/Solution sections (psHero, psProblem, …) — same per-key model as
    // product-hero above.
    ...Object.fromEntries(
      (LP_PROBLEM_SOLUTION_BLOCK_KEYS as readonly string[]).map((k) => [
        k,
        {
          value: (problemSolutionSections as Record<string, any>)[k],
          set: (v: any) =>
            setProblemSolutionSections((prev) => ({ ...prev, [k]: v })),
        },
      ]),
    ),
    // Offer-stack sections (osHero, osStack, …) — same per-key model.
    ...Object.fromEntries(
      (LP_OFFER_STACK_BLOCK_KEYS as readonly string[]).map((k) => [
        k,
        {
          value: (offerStackSections as Record<string, any>)[k],
          set: (v: any) =>
            setOfferStackSections((prev) => ({ ...prev, [k]: v })),
        },
      ]),
    ),
  };

  // Map a persisted content op to a version-history actionType the server turns
  // into a friendly label. Covers all saveContentMutation op variants.
  const contentOpActionType = (body: Record<string, any>): string => {
    switch (body?.op) {
      case "setText": return "text-edit";
      case "setLinkUrl": return "link-url";
      case "setItemHidden": return body.hidden ? "item-hidden" : "item-shown";
      case "deleteItem": return "item-deleted";
      case "moveItem": return "item-moved";
      case "setVideoUrl": return (body.url ?? "").toString().trim() ? "video-swapped" : "video-removed";
      case "setMediaUrl": return "image-swapped";
      case "clearMediaUrl": return "image-reset";
      case "setReviewSelection": return "review-changed";
      case "clearReviewSelection": return "review-reset";
      case "setFieldHidden": return body.hidden ? "element-hidden" : "element-shown";
      case "deleteField": return "element-deleted";
      case "setTheme": return "theme-changed";
      case "resetTheme": return "theme-reset";
      case "setElementStyle": return "style-changed";
      case "resetElementStyle": return "style-reset";
      case "setComponentOrder": return "components-reordered";
      default: return "edit";
    }
  };

  // Persist a single content op to the page (no-op when the draft isn't saved
  // yet — the edit still lives in local state and is written out at finalize).
  const saveContentMutation = useMutation({
    mutationKey: LP_PENDING_SAVE_KEY,
    mutationFn: async (body: Record<string, any>) => {
      const res = await apiRequest(
        "PATCH",
        `/api/landing-pages/${pageId}/lp-express/content`,
        body,
      );
      return parseJsonResponse(res);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/runs`],
      });
      // A persisted edit bumps page.updatedAt, so a live page is now stale —
      // refresh publish status so the "Unpublished changes" badge appears.
      queryClient.invalidateQueries({
        queryKey: ["/api/landing-pages", pageId, "published"],
      });
      // Snapshot the now-persisted state for undo/redo.
      recordSnapshot(contentOpActionType(variables as Record<string, any>));
    },
    onError: (e: any) => {
      toast({
        title: "Couldn't save edit",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });
  const saveContentErrorRef = useRef(saveContentMutation.isError);
  saveContentErrorRef.current = saveContentMutation.isError;

  // ── Regenerate the WHOLE Quiz Funnel ───────────────────────────────────────
  // The quiz stores its entire content (intro + questions + result screens) as
  // one blob, so — unlike the long-form per-block copy regeneration — this
  // rebuilds everything at once. The server PRESERVES the current quiz during
  // the in-flight window and only swaps in the new one on success, so the quiz-
  // status poll must gate "done" on the RUN status (not merely quiz presence);
  // see the quiz-status effect below. We start polling only in onSuccess (after
  // the server has flipped the run to "running"), avoiding a race where the
  // first poll sees the still-"completed" old run and resolves immediately with
  // the stale quiz.
  const regenerateQuizMutation = useMutation({
    mutationFn: async (feedback: string) => {
      if (!pageId) throw new Error("Save the page first");
      const res = await apiRequest(
        "POST",
        `/api/landing-pages/${pageId}/lp-express/regenerate-quiz`,
        feedback ? { feedback } : {},
      );
      return parseJsonResponse(res);
    },
    onMutate: () => {
      setHeroError(null);
      setQuizRegenerating(true);
      toast({
        title: "Regenerating your quiz…",
        description: "This usually takes a minute or two.",
      });
    },
    onSuccess: (data: any) => {
      const runId = typeof data?.runId === "string" ? data.runId : selectedRunId;
      if (runId) setSelectedRunId(runId);
      // Start polling now that the server has flipped the run to "running".
      setQuizGenerating(true);
    },
    onError: (e: any) => {
      setQuizRegenerating(false);
      toast({
        title: "Couldn't start regeneration",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  // ── Regenerate the WHOLE Listicle copy blob ────────────────────────────────
  // Same model as regenerate-quiz: the listicle's entire COPY lives in one blob
  // (generatedContent.listicle), so regeneration rebuilds it all at once. The
  // server preserves the current listicle during the in-flight window (and its
  // AI imagery is never touched), so the status poll gates "done" on the RUN
  // status — see the listicle-status effect below. Polling starts only in
  // onSuccess, after the server has flipped the run to "running".
  const regenerateListicleMutation = useMutation({
    mutationFn: async (feedback: string) => {
      if (!pageId) throw new Error("Save the page first");
      const res = await apiRequest(
        "POST",
        `/api/landing-pages/${pageId}/lp-express/regenerate-listicle`,
        feedback ? { feedback } : {},
      );
      return parseJsonResponse(res);
    },
    onMutate: () => {
      setHeroError(null);
      setListicleRegenerating(true);
      toast({
        title: "Regenerating your listicle…",
        description: "This usually takes a minute or two.",
      });
    },
    onSuccess: (data: any) => {
      const runId = typeof data?.runId === "string" ? data.runId : selectedRunId;
      if (runId) setSelectedRunId(runId);
      // Start polling now that the server has flipped the run to "running".
      setListicleGenerating(true);
    },
    onError: (e: any) => {
      setListicleRegenerating(false);
      toast({
        title: "Couldn't start regeneration",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  // ── Regenerate the WHOLE Advertorial copy blob ─────────────────────────────
  // Same model as regenerate-listicle: the advertorial's entire COPY lives in
  // one blob (generatedContent.advertorial), so regeneration rebuilds it all at
  // once. The server preserves the current advertorial during the in-flight
  // window (and its AI imagery is never touched), so the status poll gates
  // "done" on the RUN status — see the advertorial-status effect below.
  const regenerateAdvertorialMutation = useMutation({
    mutationFn: async (feedback: string) => {
      if (!pageId) throw new Error("Save the page first");
      const res = await apiRequest(
        "POST",
        `/api/landing-pages/${pageId}/lp-express/regenerate-advertorial`,
        feedback ? { feedback } : {},
      );
      return parseJsonResponse(res);
    },
    onMutate: () => {
      setHeroError(null);
      setAdvertorialRegenerating(true);
      toast({
        title: "Regenerating your advertorial…",
        description: "This usually takes a minute or two.",
      });
    },
    onSuccess: (data: any) => {
      const runId = typeof data?.runId === "string" ? data.runId : selectedRunId;
      if (runId) setSelectedRunId(runId);
      // Start polling now that the server has flipped the run to "running".
      setAdvertorialGenerating(true);
    },
    onError: (e: any) => {
      setAdvertorialRegenerating(false);
      toast({
        title: "Couldn't start regeneration",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  // ── Regenerate the WHOLE Coffee Shop (Lead Magnet) copy blob ───────────────
  // Same model as regenerate-listicle: the page's entire COPY lives in one blob
  // (generatedContent.coffeeShop), so regeneration rebuilds it all at once. The
  // server preserves the current copy during the in-flight window (and its AI
  // imagery — coffeeShopImages — is never touched), so the status poll gates
  // "done" on the RUN status — see the coffee-shop-status effect below. Polling
  // starts only in onSuccess, after the server has flipped the run to "running".
  const regenerateCoffeeShopMutation = useMutation({
    mutationFn: async (feedback: string) => {
      if (!pageId) throw new Error("Save the page first");
      const res = await apiRequest(
        "POST",
        `/api/landing-pages/${pageId}/lp-express/regenerate-coffee-shop`,
        feedback ? { feedback } : {},
      );
      return parseJsonResponse(res);
    },
    onMutate: () => {
      setHeroError(null);
      setCoffeeShopRegenerating(true);
      toast({
        title: "Regenerating your page copy…",
        description: "This usually takes a minute or two.",
      });
    },
    onSuccess: (data: any) => {
      const runId = typeof data?.runId === "string" ? data.runId : selectedRunId;
      if (runId) setSelectedRunId(runId);
      // Start polling now that the server has flipped the run to "running".
      setCoffeeShopGenerating(true);
    },
    onError: (e: any) => {
      setCoffeeShopRegenerating(false);
      toast({
        title: "Couldn't start regeneration",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  // ── Regenerate the WHOLE Temecula Passport (Mystery Drop) copy blob ────────
  // Mirrors the coffee-shop whole-page regen above: one blob at
  // generatedContent.temeculaPassport, AI imagery (temeculaPassportImages) is
  // never touched, and the status poll gates "done" on the RUN status.
  const regenerateTemeculaPassportMutation = useMutation({
    mutationFn: async (feedback: string) => {
      if (!pageId) throw new Error("Save the page first");
      const res = await apiRequest(
        "POST",
        `/api/landing-pages/${pageId}/lp-express/regenerate-temecula-passport`,
        feedback ? { feedback } : {},
      );
      return parseJsonResponse(res);
    },
    onMutate: () => {
      setHeroError(null);
      setTemeculaPassportRegenerating(true);
      toast({
        title: "Regenerating your page copy…",
        description: "This usually takes a minute or two.",
      });
    },
    onSuccess: (data: any) => {
      const runId = typeof data?.runId === "string" ? data.runId : selectedRunId;
      if (runId) setSelectedRunId(runId);
      // Start polling now that the server has flipped the run to "running".
      setTemeculaPassportGenerating(true);
    },
    onError: (e: any) => {
      setTemeculaPassportRegenerating(false);
      toast({
        title: "Couldn't start regeneration",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  // ── Version history (undo / redo / restore) ────────────────────────────────
  // Snapshots are server-authoritative: after a persisted edit we ask the server
  // to snapshot the page's CURRENT stored state with an actionType (it maps the
  // friendly label). The nav response keeps the toolbar in sync. undo/redo/
  // restore return the restored generatedContent + page fields, which we feed
  // back through applyGcToLocalState so the preview reflects the snapshot.
  const lpHistoryKey = ["/api/landing-pages", pageId, "lp-express", "history", surface];

  const historyQuery = useQuery<{
    snapshots: Array<{ id: string; seq: number; actionLabel: string; userId: string | null; createdAt: string }>;
    currentSnapshotSeq: number | null;
    canUndo: boolean;
    canRedo: boolean;
  }>({
    queryKey: lpHistoryKey,
    queryFn: async () => {
      const res = await fetch(`/api/landing-pages/${pageId}/lp-express/history`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load page history");
      return res.json();
    },
    enabled: !!pageId && historyOpen,
  });

  // Seed the "Page generated" baseline once per page, before any edit, so the
  // first undo returns to the freshly generated page. Idempotent server-side.
  const ensureBaselineMutation = useMutation({
    mutationFn: async (pid: string) => {
      const res = await apiRequest("POST", `/api/landing-pages/${pid}/lp-express/ensure-baseline`, {});
      return parseJsonResponse(res);
    },
    onSuccess: (data: any) => {
      if (data) setSnapshotNav({
        currentSnapshotSeq: data.currentSnapshotSeq ?? null,
        canUndo: !!data.canUndo,
        canRedo: !!data.canRedo,
      });
    },
  });

  const recordSnapshotMutation = useMutation({
    mutationFn: async (actionType: string) => {
      const body = { actionType };
      const res = await apiRequest("POST", `/api/landing-pages/${pageId}/lp-express/snapshot`, body);
      return parseJsonResponse(res);
    },
    onSuccess: (data: any) => {
      if (data) setSnapshotNav({
        currentSnapshotSeq: data.currentSnapshotSeq ?? null,
        canUndo: !!data.canUndo,
        canRedo: !!data.canRedo,
      });
      queryClient.invalidateQueries({ queryKey: lpHistoryKey });
    },
  });

  // Fire-and-forget snapshot after a persisted edit (no-op until the draft has a
  // pageId). Errors are swallowed: a missed snapshot must never break the edit.
  const recordSnapshot = (actionType: string) => {
    if (pageId) {
      recordSnapshotMutation.mutate(actionType);
    }
  };

  // Variables shape for the section copy-regeneration mutations. The automatic
  // content-backfill effect calls these with no arguments (variables === void),
  // which must NOT snapshot — otherwise machine-generated backfill would flood
  // the version history. A deliberate, user-initiated regeneration instead calls
  // `.mutate({ snapshot: true })`, which records a "<Section> regenerated" version
  // so the user can undo an unwanted regeneration.
  // `feedback` is optional free-text the user types to steer a rewrite;
  // `currentCopy` is the block's current text (JSON-stringified for structured
  // blocks) so the workflow revises rather than blindly replaces it. Both are
  // threaded into the copy mutation request bodies via `makeCopyBody(vars)`.
  type LpRegenVars =
    | { snapshot?: boolean; feedback?: string; currentCopy?: string }
    | void;
  const maybeSnapshotRegen = (vars: LpRegenVars, actionType: string) => {
    if (vars && vars.snapshot) recordSnapshot(actionType);
  };

  // Apply a restored snapshot (from undo/redo/restore) to local editor state +
  // the cached page query, and sync the nav pointer/flags.
  const applyRestoreResult = (data: any) => {
    if (!data) return;
    applyGcToLocalState(data.generatedContent ?? {});
    setCheckoutUrlInput(data.checkoutUrl ?? null);
    setSnapshotNav({
      currentSnapshotSeq: data.currentSnapshotSeq ?? null,
      canUndo: !!data.canUndo,
      canRedo: !!data.canRedo,
    });
    queryClient.invalidateQueries({ queryKey: lpHistoryKey });
    queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/lp-express/runs`] });
    queryClient.invalidateQueries({ queryKey: ["/api/landing-pages", pageId, "published"] });
  };

  const undoMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/landing-pages/${pageId}/lp-express/undo`, {});
      return parseJsonResponse(res);
    },
    onSuccess: applyRestoreResult,
    onError: (e: any) => {
      toast({ title: "Couldn't undo", description: e?.message || "Nothing to undo.", variant: "destructive" });
    },
  });

  const redoMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/landing-pages/${pageId}/lp-express/redo`, {});
      return parseJsonResponse(res);
    },
    onSuccess: applyRestoreResult,
    onError: (e: any) => {
      toast({ title: "Couldn't redo", description: e?.message || "Nothing to redo.", variant: "destructive" });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (seq: number) => {
      const res = await apiRequest("POST", `/api/landing-pages/${pageId}/lp-express/restore`, { seq });
      return parseJsonResponse(res);
    },
    onSuccess: applyRestoreResult,
    onError: (e: any) => {
      toast({ title: "Couldn't restore", description: e?.message || "Please try again.", variant: "destructive" });
    },
  });

  const snapshotBusy =
    undoMutation.isPending || redoMutation.isPending || restoreMutation.isPending;
  const studioRevisionHistoryBusy = studioRevisionStatus?.status === "revising";
  const saveBusy =
    saveContentMutation.isPending ||
    saveSectionOrderMutation.isPending ||
    saveCheckoutUrlMutation.isPending ||
    recordSnapshotMutation.isPending;

  const triggerUndo = () => {
    if (pageId && snapshotNav.canUndo && !snapshotBusy && !studioRevisionHistoryBusy) undoMutation.mutate();
  };
  const triggerRedo = () => {
    if (pageId && snapshotNav.canRedo && !snapshotBusy && !studioRevisionHistoryBusy) redoMutation.mutate();
  };

  // Seed the "Page generated" baseline once per saved page (after the hero is
  // present, i.e. the page exists and is editable). Idempotent server-side; the
  // guard ref prevents re-seeding across re-renders and undo/redo (which keep the
  // same pageId). Resets nav when the page changes (e.g. selecting another run).
  const baselinedPageIdRef = useRef<string>("");
  useEffect(() => {
    if (!pageId || !hero) return;
    if (baselinedPageIdRef.current === pageId) return;
    baselinedPageIdRef.current = pageId;
    setSnapshotNav({ currentSnapshotSeq: null, canUndo: false, canRedo: false });
    ensureBaselineMutation.mutate(pageId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, hero]);

  // Keyboard shortcuts: Cmd/Ctrl+Z = undo, Cmd/Ctrl+Shift+Z = redo. Ignored
  // while an inline text field / contenteditable is focused so native text undo
  // keeps working during in-canvas editing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta || e.key.toLowerCase() !== "z") return;
      const t = e.target as HTMLElement | null;
      const tag = (t?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || t?.isContentEditable) return;
      e.preventDefault();
      if (e.shiftKey) triggerRedo();
      else triggerUndo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, snapshotNav.canUndo, snapshotNav.canRedo, snapshotBusy, studioRevisionHistoryBusy]);

  // Apply a content op to local state (instant preview) then persist it. `path`
  // always starts with a known content key. For item ops the path points at the
  // item array; `index` selects the item.
  const applyContentOp = (
    op:
      | { kind: "setText"; path: (string | number)[]; value: string }
      | { kind: "setItemHidden"; path: (string | number)[]; index: number; hidden: boolean }
      | { kind: "setLinkUrl"; path: (string | number)[]; value: string }
      | { kind: "deleteItem"; path: (string | number)[]; index: number }
      | { kind: "moveItem"; path: (string | number)[]; index: number; dir: "up" | "down" }
      | { kind: "setVideoUrl"; slot: "making" | "lifestyle" | "coffee-shop-reel"; url: string | null }
      | { kind: "setMediaUrl"; key: string; url: string }
      | { kind: "clearMediaUrl"; key: string }
      | { kind: "setReviewSelection"; key: string; review: { reviewer: string | null; reviewText: string; rating: number } }
      | { kind: "clearReviewSelection"; key: string }
      | { kind: "setFieldHidden"; key: string; hidden: boolean }
      | { kind: "deleteField"; key: string }
      | { kind: "setTheme"; patch: Partial<LpTheme> }
      | { kind: "resetTheme" }
      | { kind: "setElementStyle"; key: string; patch: Partial<LpElementStyle> }
      | { kind: "resetElementStyle"; key: string }
      | { kind: "setComponentOrder"; group: string; keys: string[] },
  ) => {
    // Pathless ops: per-page video overrides + per-element hide/delete. Update
    // local state optimistically, then persist (when the draft is saved).
    if (op.kind === "setVideoUrl") {
      setVideoOverrides((prev) => {
        const next = { ...prev };
        const url = (op.url ?? "").trim();
        if (url) next[op.slot] = url;
        else delete next[op.slot];
        return next;
      });
      if (pageId) saveContentMutation.mutate({ op: "setVideoUrl", slot: op.slot, url: op.url });
      return;
    }
    if (op.kind === "setMediaUrl" || op.kind === "clearMediaUrl") {
      setMediaOverrides((prev) => {
        const next = { ...prev };
        if (op.kind === "clearMediaUrl") {
          delete next[op.key];
        } else {
          const url = (op.url ?? "").trim();
          if (url) next[op.key] = { url };
          else delete next[op.key];
        }
        return next;
      });
      if (pageId) {
        if (op.kind === "clearMediaUrl") {
          saveContentMutation.mutate({ op: "clearMediaUrl", key: op.key });
        } else {
          saveContentMutation.mutate({ op: "setMediaUrl", key: op.key, url: op.url });
        }
      }
      return;
    }
    if (op.kind === "setReviewSelection" || op.kind === "clearReviewSelection") {
      setReviewOverrides((prev) => {
        const next = { ...prev };
        if (op.kind === "clearReviewSelection") delete next[op.key];
        else next[op.key] = op.review;
        return next;
      });
      if (pageId) {
        if (op.kind === "clearReviewSelection") {
          saveContentMutation.mutate({ op: "clearReviewSelection", key: op.key });
        } else {
          saveContentMutation.mutate({ op: "setReviewSelection", key: op.key, review: op.review });
        }
      }
      return;
    }
    if (op.kind === "setFieldHidden" || op.kind === "deleteField") {
      setElementStates((prev) => {
        const next = { ...prev };
        if (op.kind === "deleteField") {
          next[op.key] = { ...(next[op.key] || {}), deleted: true };
        } else {
          const cur = { ...(next[op.key] || {}) };
          if (op.hidden) cur.hidden = true;
          else delete cur.hidden;
          if (Object.keys(cur).length === 0) delete next[op.key];
          else next[op.key] = cur;
        }
        return next;
      });
      if (pageId) {
        if (op.kind === "deleteField") {
          saveContentMutation.mutate({ op: "deleteField", key: op.key });
        } else {
          saveContentMutation.mutate({ op: "setFieldHidden", key: op.key, hidden: op.hidden });
        }
      }
      return;
    }
    // ── WYSIWYG theme + per-element style ops ── optimistic local merge (mirrors
    // the server's sanitize+merge), then persist. A null/"" patch field clears
    // that field; an emptied object is dropped entirely.
    if (op.kind === "setTheme") {
      setTheme((prev) => mergeLpTheme(prev, op.patch));
      if (pageId) saveContentMutation.mutate({ op: "setTheme", theme: op.patch });
      return;
    }
    if (op.kind === "resetTheme") {
      setTheme({});
      if (pageId) saveContentMutation.mutate({ op: "resetTheme" });
      return;
    }
    if (op.kind === "setElementStyle") {
      setElementStyles((prev) => {
        const next = { ...prev };
        const merged = mergeLpElementStyle(next[op.key], op.patch);
        if (Object.keys(merged).length === 0) delete next[op.key];
        else next[op.key] = merged;
        return next;
      });
      if (pageId) saveContentMutation.mutate({ op: "setElementStyle", key: op.key, style: op.patch });
      return;
    }
    if (op.kind === "resetElementStyle") {
      setElementStyles((prev) => {
        const next = { ...prev };
        delete next[op.key];
        return next;
      });
      if (pageId) saveContentMutation.mutate({ op: "resetElementStyle", key: op.key });
      return;
    }
    if (op.kind === "setComponentOrder") {
      setComponentOrder((prev) =>
        sanitizeLpComponentOrder({ ...prev, [op.group]: op.keys }) ?? {},
      );
      if (pageId) saveContentMutation.mutate({ op: "setComponentOrder", group: op.group, keys: op.keys });
      return;
    }
    const root = op.path[0] as string;
    const entry = lpContentState[root];
    if (!entry) return;
    const container: any = { [root]: entry.value };
    if (op.kind === "setText" || op.kind === "setLinkUrl") {
      // Branch-safety: a scalar write must land on a leaf. If the path resolves
      // to an object/array (a path/state mismatch), writing a string there would
      // wipe every sibling field in that branch — so no-op instead of clobbering.
      if (!lpCanSetLeaf(container, op.path)) {
        console.warn(
          "[lp-express] skipped non-leaf text/link write",
          op.path,
        );
        return;
      }
      const next = lpDeepSet(container, op.path, op.value);
      entry.set(next[root]);
    } else {
      const arr = lpGetAt(container, op.path);
      if (!Array.isArray(arr)) return;
      let newArr: any[];
      if (op.kind === "deleteItem") {
        newArr = arr.filter((_: any, i: number) => i !== op.index);
      } else if (op.kind === "moveItem") {
        const j = op.dir === "up" ? op.index - 1 : op.index + 1;
        newArr = [...arr];
        if (j >= 0 && j < newArr.length) {
          [newArr[op.index], newArr[j]] = [newArr[j], newArr[op.index]];
        }
      } else {
        newArr = arr.map((it: any, i: number) =>
          i === op.index ? { ...(it && typeof it === "object" ? it : {}), hidden: op.hidden } : it,
        );
      }
      const next = lpDeepSet(container, op.path, newArr);
      entry.set(next[root]);
    }
    if (!pageId) return;
    if (op.kind === "setText") {
      saveContentMutation.mutate({ op: "setText", path: op.path, value: op.value });
    } else if (op.kind === "setLinkUrl") {
      saveContentMutation.mutate({ op: "setLinkUrl", path: op.path, value: op.value });
    } else if (op.kind === "setItemHidden") {
      saveContentMutation.mutate({
        op: "setItemHidden",
        path: op.path,
        index: op.index,
        hidden: op.hidden,
      });
    } else if (op.kind === "moveItem") {
      saveContentMutation.mutate({ op: "moveItem", path: op.path, index: op.index, dir: op.dir });
    } else {
      saveContentMutation.mutate({ op: "deleteItem", path: op.path, index: op.index });
    }
  };

  // Editor controls handed to the renderer to turn the live preview into an
  // in-canvas editor (hover toolbar per section). Built fresh each render (NOT
  // memoized): the handlers close over `pageId`/`sectionLayout`, which are empty
  // on first render and only populated after hydration. A stale memo would
  // freeze the empty-`pageId` closures so edits never persist.
  // ── Regenerate-with-feedback handlers ──────────────────────────────────────
  // Per-block + per-section copy regeneration steered by optional user feedback.
  // `onRegenerateBlock` re-runs one block's workflow; `onRegenerateSection` fans
  // the shared feedback out to every copy workflow in a section (and optionally
  // re-renders the section's image slots). `currentCopy` is the block's current
  // generated text, passed back so the workflow revises rather than re-rolls.
  // Defined before the copy mutations they call — safe because they are only
  // invoked on a user click, long after those mutation consts initialize.
  // Copy block keys whose regeneration workflow is currently in flight (user
  // initiated). Drives the editor's "Regenerating…" spinners.
  const [regeneratingBlocks, setRegeneratingBlocks] = useState<Set<string>>(
    () => new Set(),
  );
  const blockCurrentCopy = (key: string): string => {
    if (key === "leadin") return leadin || "";
    // The hero copy block rewrites the headline AND the subheadline together, so
    // both must be sent back as the current copy — otherwise the workflow only
    // ever sees the headline and can't revise the subheadline against feedback
    // (it falls back to re-rolling it blind). Mirror the JSON shape the other
    // multi-field blocks (e.g. showcase) send.
    if (key === "hero")
      return JSON.stringify({
        headline: hero?.headline || "",
        subheadline: hero?.subheadline || "",
      });
    const v = lpContentState[key]?.value;
    if (v == null) return "";
    return typeof v === "string" ? v : JSON.stringify(v);
  };
  // `runCopyBlockMutation` is the single entry point for USER-initiated copy
  // regeneration (per-block + per-section). It tracks the in-flight block keys in
  // `regeneratingBlocks` so the editor can show a "Regenerating…" spinner on the
  // affected field/control, then clears the key when the workflow settles. The
  // auto-backfill effect calls the mutations directly (not through here), so it
  // never lights up the regenerate spinners.
  const runCopyBlockMutation = (key: string, vars: LpRegenVars) => {
    const mutations: Record<string, { mutate: (v: LpRegenVars, o?: { onSettled?: () => void }) => void }> = {
      hero: heroMutation,
      leadin: leadinMutation,
      showcase: showcaseMutation,
      productContext: productContextMutation,
      advisory: advisoryMutation,
      stations: stationsMutation,
      villain: villainMutation,
      quotes: quotesMutation,
      originCopy: originCopyMutation,
      timeline: timelineMutation,
      membership: membershipMutation,
      proof: proofMutation,
      alternatives: alternativesMutation,
      philosophy: philosophyMutation,
      differentiator: differentiatorMutation,
      faq: faqMutation,
      reviews: reviewsMutation,
      finalCta: finalCtaMutation,
    };
    const m = mutations[key];
    if (!m) return;
    setRegeneratingBlocks((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    m.mutate(vars, {
      onSettled: () =>
        setRegeneratingBlocks((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        }),
    });
  };
  // Re-render a section's image slots using the existing image-render mechanisms
  // (unchanged per-slot retry for hero and product-showcase cards; the
  // whole-array image mutations for the others). differentiator re-renders
  // proof's images because it reuses them. Stay in lockstep with
  // LP_IMAGE_SECTION_TYPES in shared/lp-express-sections.ts.
  const triggerSectionImages = (type: LpSectionType) => {
    switch (type) {
      case "farmstand": originImagesMutation.mutate(); break;
      case "alternatives": alternativesImagesMutation.mutate(); break;
      case "philosophy": philosophyImagesMutation.mutate(); break;
      case "proof": proofImagesMutation.mutate(); break;
      case "differentiator": proofImagesMutation.mutate(); break;
      case "productShowcase": {
        const count = Array.isArray(productContext) ? productContext.length : 0;
        for (let i = 0; i < count; i++) void handleRetryProductContext(i);
        break;
      }
      case "hero": {
        const count = Array.isArray(heroImageUrls) ? heroImageUrls.length : 0;
        for (let i = 0; i < count; i++) void handleRetryHeroSlot(i);
        break;
      }
    }
  };
  // Per-SECTION advertorial copy regen. The whole advertorial comes from ONE
  // workflow, so the server re-runs it with the feedback auto-scoped to the
  // target section and merges ONLY that section back. Runs as a background job
  // (the Sim call can outlive the gateway timeout) that we poll here; the
  // section's fields dim via the same `regeneratingBlocks` set the static
  // blocks use. A 404 on the poll means the job was lost to a server restart —
  // surface a clean retry instead of spinning forever.
  const regenerateAdvertorialSection = async (index: number, feedback: string) => {
    if (!pageId) return;
    const key = `advertorial-section-${index}`;
    if (regeneratingBlocks.has(key)) return;
    setRegeneratingBlocks((prev) => new Set(prev).add(key));
    try {
      const res = await apiRequest(
        "POST",
        `/api/landing-pages/${pageId}/lp-express/regenerate-advertorial-section`,
        { sectionIndex: index, ...(feedback ? { feedback } : {}) },
      );
      const data: any = await parseJsonResponse(res);
      const jobId = typeof data?.jobId === "string" ? data.jobId : null;
      if (!jobId) throw new Error("Couldn't start the section regeneration");
      const deadline = Date.now() + 5 * 60_000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const jr = await fetch(
          `/api/landing-pages/${pageId}/lp-express/advertorial-section-job/${jobId}`,
          { credentials: "include" },
        );
        if (jr.status === 404) {
          throw new Error("The regeneration was interrupted — please try again.");
        }
        if (!jr.ok) continue; // transient poll error — keep waiting
        const job: any = await jr.json();
        if (job?.status === "completed") {
          if (job.advertorial) setAdvertorial(job.advertorial as LpAdvertorialContent);
          toast({ title: "Section updated" });
          return;
        }
        if (job?.status === "failed") {
          throw new Error(job.error || "Section regeneration failed — please try again.");
        }
      }
      throw new Error("Section regeneration timed out — please try again.");
    } catch (e: any) {
      toast({
        title: "Couldn't regenerate the section",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setRegeneratingBlocks((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };
  // Per-SECTION product-hero copy regen. The whole product-hero page is
  // generated by ONE Sim workflow and split into per-section keys (phBuyBox, …),
  // so a regen re-runs that SAME workflow with block-scoped feedback (server
  // seeds it with the assembled whole-page copy for context) and merges ONLY
  // the requested key back. Runs as a
  // background job we poll here; the section dims via the same
  // `regeneratingBlocks` set the static blocks use. A 404 on the poll means the
  // job was lost to a server restart — surface a clean retry.
  const regenerateProductHeroSection = async (blockKey: string, feedback: string) => {
    if (!pageId) return;
    if (regeneratingBlocks.has(blockKey)) return;
    setRegeneratingBlocks((prev) => new Set(prev).add(blockKey));
    try {
      const res = await apiRequest(
        "POST",
        `/api/landing-pages/${pageId}/lp-express/regenerate-product-hero-section`,
        { blockKey, ...(feedback ? { feedback } : {}) },
      );
      const data: any = await parseJsonResponse(res);
      const jobId = typeof data?.jobId === "string" ? data.jobId : null;
      if (!jobId) throw new Error("Couldn't start the section regeneration");
      const deadline = Date.now() + 5 * 60_000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const jr = await fetch(
          `/api/landing-pages/${pageId}/lp-express/product-hero-section-job/${jobId}`,
          { credentials: "include" },
        );
        if (jr.status === 404) {
          throw new Error("The regeneration was interrupted — please try again.");
        }
        if (!jr.ok) continue; // transient poll error — keep waiting
        const job: any = await jr.json();
        if (job?.status === "completed") {
          if (typeof job.blockKey === "string" && "content" in job) {
            setProductHeroSections((prev) => ({ ...prev, [job.blockKey]: job.content }));
          }
          toast({ title: "Section updated" });
          return;
        }
        if (job?.status === "failed") {
          throw new Error(job.error || "Section regeneration failed — please try again.");
        }
      }
      throw new Error("Section regeneration timed out — please try again.");
    } catch (e: any) {
      toast({
        title: "Couldn't regenerate the section",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setRegeneratingBlocks((prev) => {
        const next = new Set(prev);
        next.delete(blockKey);
        return next;
      });
    }
  };
  // Per-SECTION problem-solution copy regen — mirror of
  // regenerateProductHeroSection above, against the problem-solution job pair.
  const regenerateProblemSolutionSection = async (blockKey: string, feedback: string) => {
    if (!pageId) return;
    if (regeneratingBlocks.has(blockKey)) return;
    setRegeneratingBlocks((prev) => new Set(prev).add(blockKey));
    try {
      const res = await apiRequest(
        "POST",
        `/api/landing-pages/${pageId}/lp-express/regenerate-problem-solution-section`,
        { blockKey, ...(feedback ? { feedback } : {}) },
      );
      const data: any = await parseJsonResponse(res);
      const jobId = typeof data?.jobId === "string" ? data.jobId : null;
      if (!jobId) throw new Error("Couldn't start the section regeneration");
      const deadline = Date.now() + 5 * 60_000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const jr = await fetch(
          `/api/landing-pages/${pageId}/lp-express/problem-solution-section-job/${jobId}`,
          { credentials: "include" },
        );
        if (jr.status === 404) {
          throw new Error("The regeneration was interrupted — please try again.");
        }
        if (!jr.ok) continue; // transient poll error — keep waiting
        const job: any = await jr.json();
        if (job?.status === "completed") {
          if (typeof job.blockKey === "string" && "content" in job) {
            setProblemSolutionSections((prev) => ({ ...prev, [job.blockKey]: job.content }));
          }
          toast({ title: "Section updated" });
          return;
        }
        if (job?.status === "failed") {
          throw new Error(job.error || "Section regeneration failed — please try again.");
        }
      }
      throw new Error("Section regeneration timed out — please try again.");
    } catch (e: any) {
      toast({
        title: "Couldn't regenerate the section",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setRegeneratingBlocks((prev) => {
        const next = new Set(prev);
        next.delete(blockKey);
        return next;
      });
    }
  };
  // Per-SECTION Cold Drip copy regen: same job-and-poll pattern as the
  // advertorial, keyed by section KEY (the blob is an object, not an array).
  const regenerateColdDripSection = async (sectionKey: string, feedback: string) => {
    if (!pageId) return;
    const key = `cold-drip-${sectionKey}`;
    if (regeneratingBlocks.has(key)) return;
    setRegeneratingBlocks((prev) => new Set(prev).add(key));
    try {
      const res = await apiRequest(
        "POST",
        `/api/landing-pages/${pageId}/lp-express/regenerate-cold-drip-section`,
        { sectionKey, ...(feedback ? { feedback } : {}) },
      );
      const data: any = await parseJsonResponse(res);
      const jobId = typeof data?.jobId === "string" ? data.jobId : null;
      if (!jobId) throw new Error("Couldn't start the section regeneration");
      const deadline = Date.now() + 5 * 60_000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const jr = await fetch(
          `/api/landing-pages/${pageId}/lp-express/offer-stack-section-job/${jobId}`,
          { credentials: "include" },
        );
        if (jr.status === 404) {
          throw new Error("The regeneration was interrupted — please try again.");
        }
        if (!jr.ok) continue; // transient poll error — keep waiting
        const job: any = await jr.json();
        if (job?.status === "completed") {
          if (typeof job.blockKey === "string" && "content" in job) {
            setOfferStackSections((prev) => ({ ...prev, [job.blockKey]: job.content }));
          }
          toast({ title: "Section updated" });
          return;
        }
        if (job?.status === "failed") {
          throw new Error(job.error || "Section regeneration failed — please try again.");
        }
      }
      throw new Error("Section regeneration timed out — please try again.");
    } catch (e: any) {
      toast({
        title: "Couldn't regenerate the section",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setRegeneratingBlocks((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // PapaBear Label section copy regeneration reuses the established LP Express
  // Sim workflows through a server-side claim-safe mapper. The server merges
  // only the requested top-level blob section; this poll hydrates that fresh
  // full blob back into the editor without touching media/review overrides.
  const regeneratePapabearLabelSection = async (
    sectionKey: string,
    feedback: string,
  ) => {
    if (!pageId) return;
    const key = `papabear-${sectionKey}`;
    if (regeneratingBlocks.has(key)) return;
    setRegeneratingBlocks((prev) => new Set(prev).add(key));
    try {
      const res = await apiRequest(
        "POST",
        `/api/landing-pages/${pageId}/lp-express/regenerate-papabear-label-section`,
        { sectionKey, ...(feedback ? { feedback } : {}) },
      );
      const data: any = await parseJsonResponse(res);
      const jobId = typeof data?.jobId === "string" ? data.jobId : null;
      if (!jobId) throw new Error("Couldn't start the section regeneration");
      const deadline = Date.now() + 5 * 60_000;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        const jobResponse = await fetch(
          `/api/landing-pages/${pageId}/lp-express/papabear-label-section-job/${jobId}`,
          { credentials: "include" },
        );
        if (jobResponse.status === 404) {
          throw new Error("The regeneration was interrupted — please try again.");
        }
        if (!jobResponse.ok) continue;
        const job: any = await jobResponse.json();
        if (job?.status === "completed") {
          if (job.papabearLabel) {
            setPapabearLabel(job.papabearLabel as LpPapabearLabelContent);
          }
          toast({ title: "Section updated" });
          return;
        }
        if (job?.status === "failed") {
          throw new Error(
            job.error || "Section regeneration failed — please try again.",
          );
        }
      }
      throw new Error("Section regeneration timed out — please try again.");
    } catch (error: any) {
      toast({
        title: "Couldn't regenerate the section",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setRegeneratingBlocks((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // Per-SECTION offer-stack copy regen — mirror of
  // regenerateProblemSolutionSection above, against the offer-stack job pair.
  const regenerateOfferStackSection = async (blockKey: string, feedback: string) => {
    if (!pageId) return;
    if (regeneratingBlocks.has(blockKey)) return;
    setRegeneratingBlocks((prev) => new Set(prev).add(blockKey));
    try {
      const res = await apiRequest(
        "POST",
        `/api/landing-pages/${pageId}/lp-express/regenerate-offer-stack-section`,
        { blockKey, ...(feedback ? { feedback } : {}) },
      );
      const data: any = await parseJsonResponse(res);
      const jobId = typeof data?.jobId === "string" ? data.jobId : null;
      if (!jobId) throw new Error("Couldn't start the section regeneration");
      const deadline = Date.now() + 5 * 60_000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const jr = await fetch(
          `/api/landing-pages/${pageId}/lp-express/offer-stack-section-job/${jobId}`,
          { credentials: "include" },
        );
        if (jr.status === 404) {
          throw new Error("The regeneration was interrupted — please try again.");
        }
        if (!jr.ok) continue; // transient poll error — keep waiting
        const job: any = await jr.json();
        if (job?.status === "completed") {
          if (typeof job.blockKey === "string" && "content" in job) {
            setOfferStackSections((prev) => ({ ...prev, [job.blockKey]: job.content }));
          }
          toast({ title: "Section updated" });
          return;
        }
        if (job?.status === "failed") {
          throw new Error(job.error || "Section regeneration failed — please try again.");
        }
      }
      throw new Error("Section regeneration timed out — please try again.");
    } catch (e: any) {
      toast({
        title: "Couldn't regenerate the section",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setRegeneratingBlocks((prev) => {
        const next = new Set(prev);
        next.delete(blockKey);
        return next;
      });
    }
  };
  // Coffee Shop Lead Capture (Gaffle) per-section copy regen — same job+poll
  // shape as the cold-drip regen above, against the coffee-shop routes.
  const regenerateCoffeeShopSection = async (sectionKey: string, feedback: string) => {
    if (!pageId) return;
    const key = `coffee-shop-${sectionKey}`;
    if (regeneratingBlocks.has(key)) return;
    setRegeneratingBlocks((prev) => new Set(prev).add(key));
    try {
      const res = await apiRequest(
        "POST",
        `/api/landing-pages/${pageId}/lp-express/regenerate-coffee-shop-section`,
        { sectionKey, ...(feedback ? { feedback } : {}) },
      );
      const data: any = await parseJsonResponse(res);
      const jobId = typeof data?.jobId === "string" ? data.jobId : null;
      if (!jobId) throw new Error("Couldn't start the section regeneration");
      const deadline = Date.now() + 5 * 60_000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const jr = await fetch(
          `/api/landing-pages/${pageId}/lp-express/coffee-shop-section-job/${jobId}`,
          { credentials: "include" },
        );
        if (jr.status === 404) {
          throw new Error("The regeneration was interrupted — please try again.");
        }
        if (!jr.ok) continue; // transient poll error — keep waiting
        const job: any = await jr.json();
        if (job?.status === "completed") {
          if (job.coffeeShop) setCoffeeShop(job.coffeeShop);
          toast({ title: "Section updated" });
          return;
        }
        if (job?.status === "failed") {
          throw new Error(job.error || "Section regeneration failed — please try again.");
        }
      }
      throw new Error("Section regeneration timed out — please try again.");
    } catch (e: any) {
      toast({
        title: "Couldn't regenerate the section",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setRegeneratingBlocks((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };
  // Temecula Passport (Mystery Drop, Gaffle) per-section copy regen — same
  // job+poll shape as the coffee-shop regen above, against the
  // temecula-passport routes.
  const regenerateTemeculaPassportSection = async (sectionKey: string, feedback: string) => {
    if (!pageId) return;
    const key = `temecula-passport-${sectionKey}`;
    if (regeneratingBlocks.has(key)) return;
    setRegeneratingBlocks((prev) => new Set(prev).add(key));
    try {
      const res = await apiRequest(
        "POST",
        `/api/landing-pages/${pageId}/lp-express/regenerate-temecula-passport-section`,
        { sectionKey, ...(feedback ? { feedback } : {}) },
      );
      const data: any = await parseJsonResponse(res);
      const jobId = typeof data?.jobId === "string" ? data.jobId : null;
      if (!jobId) throw new Error("Couldn't start the section regeneration");
      const deadline = Date.now() + 5 * 60_000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const jr = await fetch(
          `/api/landing-pages/${pageId}/lp-express/temecula-passport-section-job/${jobId}`,
          { credentials: "include" },
        );
        if (jr.status === 404) {
          throw new Error("The regeneration was interrupted — please try again.");
        }
        if (!jr.ok) continue; // transient poll error — keep waiting
        const job: any = await jr.json();
        if (job?.status === "completed") {
          if (job.temeculaPassport) setTemeculaPassport(job.temeculaPassport);
          toast({ title: "Section updated" });
          return;
        }
        if (job?.status === "failed") {
          throw new Error(job.error || "Section regeneration failed — please try again.");
        }
      }
      throw new Error("Section regeneration timed out — please try again.");
    } catch (e: any) {
      toast({
        title: "Couldn't regenerate the section",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setRegeneratingBlocks((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };
  const onRegenerateBlock = (key: string, feedback: string) => {
    const adv = /^advertorial-section-(\d+)$/.exec(key);
    if (adv) {
      void regenerateAdvertorialSection(Number(adv[1]), feedback);
      return;
    }
    // Product-hero blocks (phBuyBox, phTrustBar, …) each regen via their own job.
    if (([...LP_PRODUCT_HERO_BLOCK_KEYS, ...LP_SMB_HOME_BLOCK_KEYS] as readonly string[]).includes(key)) {
      void regenerateProductHeroSection(key, feedback);
      return;
    }
    // Problem-solution blocks (psHero, psProblem, …) likewise.
    if ((LP_PROBLEM_SOLUTION_BLOCK_KEYS as readonly string[]).includes(key)) {
      void regenerateProblemSolutionSection(key, feedback);
      return;
    }
    const cdm = LP_COLD_DRIP_REGEN_KEY_RE.exec(key);
    if (cdm) {
      void regenerateColdDripSection(cdm[1], feedback);
      return;
    }
    const csm = LP_COFFEE_SHOP_REGEN_KEY_RE.exec(key);
    if (csm) {
      void regenerateCoffeeShopSection(csm[1], feedback);
      return;
    }
    // Offer-stack blocks (osHero, osStack, …) likewise.
    if ((LP_OFFER_STACK_BLOCK_KEYS as readonly string[]).includes(key)) {
      void regenerateOfferStackSection(key, feedback);
      return;
    }
    const tpm = LP_TEMECULA_PASSPORT_REGEN_KEY_RE.exec(key);
    if (tpm) {
      void regenerateTemeculaPassportSection(tpm[1], feedback);
      return;
    }
    const pbm = LP_PAPABEAR_LABEL_REGEN_KEY_RE.exec(key);
    if (pbm) {
      void regeneratePapabearLabelSection(pbm[1], feedback);
      return;
    }
    runCopyBlockMutation(key, {
      snapshot: true,
      feedback: feedback || "",
      currentCopy: blockCurrentCopy(key),
    });
  };
  const onRegenerateSection = (
    type: LpSectionType,
    feedback: string,
    scope: "text" | "images" | "both",
  ) => {
    if (scope === "text" || scope === "both") {
      const blocks = lpSectionCopyBlocks(type);
      blocks.forEach((b, i) => {
        // Product-hero copy blocks regen through their own per-section job.
        if (([...LP_PRODUCT_HERO_BLOCK_KEYS, ...LP_SMB_HOME_BLOCK_KEYS] as readonly string[]).includes(b.key)) {
          void regenerateProductHeroSection(b.key, feedback);
          return;
        }
        // Problem-solution copy blocks likewise regen through their own job.
        if ((LP_PROBLEM_SOLUTION_BLOCK_KEYS as readonly string[]).includes(b.key)) {
          void regenerateProblemSolutionSection(b.key, feedback);
          return;
        }
        // Offer-stack copy blocks likewise regen through their own job.
        if ((LP_OFFER_STACK_BLOCK_KEYS as readonly string[]).includes(b.key)) {
          void regenerateOfferStackSection(b.key, feedback);
          return;
        }
        runCopyBlockMutation(b.key, {
          // Snapshot only the first block so one section regen is a single undo
          // step rather than flooding history with one entry per workflow.
          snapshot: i === 0,
          feedback: feedback || "",
          currentCopy: blockCurrentCopy(b.key),
        });
      });
    }
    if (scope === "images" || scope === "both") triggerSectionImages(type);
  };

  const lpEditorControls = {
    onMove: moveSection,
    onToggleHidden: toggleSectionHidden,
    onDelete: deleteSection,
    busy: saveSectionOrderMutation.isPending,
    onRegenerateBlock,
    onRegenerateSection,
    onEditText: (path: (string | number)[], value: string) =>
      applyContentOp({ kind: "setText", path, value }),
    onEditLinkUrl: (path: (string | number)[], url: string) =>
      applyContentOp({ kind: "setLinkUrl", path, value: url }),
    onItemSetHidden: (path: (string | number)[], index: number, hidden: boolean) =>
      applyContentOp({ kind: "setItemHidden", path, index, hidden }),
    onItemDelete: (path: (string | number)[], index: number) =>
      applyContentOp({ kind: "deleteItem", path, index }),
    onItemMove: (path: (string | number)[], index: number, dir: "up" | "down") =>
      applyContentOp({ kind: "moveItem", path, index, dir }),
    onSetVideoUrl: (slot: "making" | "lifestyle" | "coffee-shop-reel", url: string | null) =>
      applyContentOp({ kind: "setVideoUrl", slot, url }),
    onUploadMedia: async (
      target: { kind: "image"; key: string } | { kind: "video"; slot: "making" | "lifestyle" | "coffee-shop-reel" },
      file: File,
    ) => {
      // Client pre-check for an obviously wrong-type/oversized file — instant
      // feedback, leaving existing media untouched. The server re-validates.
      const preErr = validateLpMediaFile(file, target.kind);
      if (preErr) {
        toast({ title: "Can't use that file", description: preErr, variant: "destructive" });
        return;
      }
      if (!pageId) {
        toast({
          title: "Save the page first",
          description: "Generate or open a saved landing page before uploading your own media.",
          variant: "destructive",
        });
        return;
      }
      try {
        const fd = new FormData();
        fd.append("kind", target.kind);
        fd.append("file", file);
        // apiRequest is JSON-only; use raw fetch so the multipart body is sent.
        const res = await fetch(`/api/landing-pages/${pageId}/lp-express/upload-media`, {
          method: "POST",
          body: fd,
          credentials: "include",
        });
        if (!res.ok) {
          let msg = "Upload failed. Please try again.";
          try {
            const j = await res.json();
            if (j?.message && typeof j.message === "string") msg = j.message;
          } catch {}
          toast({ title: "Upload failed", description: msg, variant: "destructive" });
          return;
        }
        const data = await res.json();
        const url = typeof data?.url === "string" ? data.url : "";
        if (!url) {
          toast({ title: "Upload failed", description: "No file URL was returned.", variant: "destructive" });
          return;
        }
        if (target.kind === "image") {
          applyContentOp({ kind: "setMediaUrl", key: target.key, url });
        } else {
          applyContentOp({ kind: "setVideoUrl", slot: target.slot, url });
        }
        toast({ title: target.kind === "image" ? "Image uploaded" : "Video uploaded" });
      } catch (e: any) {
        toast({
          title: "Upload failed",
          description: e?.message ? String(e.message) : "Please try again.",
          variant: "destructive",
        });
      }
    },
    onClearMedia: (key: string) => applyContentOp({ kind: "clearMediaUrl", key }),
    onChooseMedia: (key: string, url: string) =>
      applyContentOp({ kind: "setMediaUrl", key, url }),
    onChooseReview: (
      key: string,
      review: { reviewer: string | null; reviewText: string; rating: number },
    ) => applyContentOp({ kind: "setReviewSelection", key, review }),
    onClearReview: (key: string) => applyContentOp({ kind: "clearReviewSelection", key }),
    onSetFieldHidden: (key: string, hidden: boolean) =>
      applyContentOp({ kind: "setFieldHidden", key, hidden }),
    onDeleteField: (key: string) => applyContentOp({ kind: "deleteField", key }),
    onSetTheme: (patch: Partial<LpTheme>) => applyContentOp({ kind: "setTheme", patch }),
    onResetTheme: () => applyContentOp({ kind: "resetTheme" }),
    onSetElementStyle: (key: string, patch: Partial<LpElementStyle>) =>
      applyContentOp({ kind: "setElementStyle", key, patch }),
    onResetElementStyle: (key: string) => applyContentOp({ kind: "resetElementStyle", key }),
    onMoveElement: (group: string, keys: string[]) =>
      applyContentOp({ kind: "setComponentOrder", group, keys }),
    contentBusy: saveContentMutation.isPending,
    isBlockRegenerating: (key: string) => regeneratingBlocks.has(key),
  };

  // Shared Checkout URL field. Rendered both in the builder editing area and in
  // the publish card; both read the same `checkoutUrl` state and reuse
  // handleCheckoutUrlChange/flushCheckoutUrl, so edits in one stay in sync with
  // the other. `suffix` keeps element ids/test ids unique when both render.
  const renderCheckoutUrlField = (suffix: string) => {
    if (studioDesign?.rendererTemplateKey === "smb-home") return null;
    const inputId = suffix ? `input-checkout-url-${suffix}` : "input-checkout-url";
    const warningTestId = suffix ? `warning-checkout-url-${suffix}` : "warning-checkout-url";
    return (
      <div className="space-y-1.5">
        <label
          htmlFor={inputId}
          className="flex items-center gap-1.5 text-xs font-semibold text-foreground"
        >
          <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
          Checkout URL
        </label>
        {checkoutSectionTargets.length > 0 && (
          <select
            value={checkoutAnchorValue}
            onChange={(e) => {
              const id = e.target.value;
              handleCheckoutUrlChange(id ? `#${id}` : "");
            }}
            onBlur={flushCheckoutUrl}
            className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm outline-none focus:border-primary"
            data-testid={`select-checkout-section-${suffix || "default"}`}
          >
            <option value="">Custom URL…</option>
            {checkoutSectionTargets.map((t) => (
              <option key={t.anchorId} value={t.anchorId}>
                Jump to: {t.label}
              </option>
            ))}
          </select>
        )}
        <Input
          id={inputId}
          type="text"
          inputMode="url"
          placeholder="https://yourstore.com/checkout or #lp-membership"
          value={checkoutUrl}
          onChange={(e) => handleCheckoutUrlChange(e.target.value)}
          onBlur={flushCheckoutUrl}
          className="text-sm"
          data-testid={inputId}
        />
        <p className="text-[11px] text-muted-foreground">
          Every buy button on this page links here. Enter a web address, pick an on-page section to jump to, or leave blank to make the buttons non-clickable.
        </p>
        {checkoutUrlValidationError ? (
          <p
            className="flex items-center gap-1.5 text-[11px] font-medium text-destructive"
            data-testid={warningTestId}
          >
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            {checkoutUrlValidationError}
          </p>
        ) : checkoutUrlIsEmpty ? (
          <p
            className="flex items-center gap-1.5 text-[11px] font-medium text-amber-600"
            data-testid={warningTestId}
          >
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            No checkout URL set — the buy buttons will not be clickable.
          </p>
        ) : null}
      </div>
    );
  };

  const availableAngles = useMemo(
    () =>
      angles.filter((a) => {
        if (a.status === "archived") return false;
        if (personaId && a.personaId !== personaId) return false;
        return !productId || a.productId === productId;
      }),
    [angles, personaId, productId],
  );

  // Group angle options by Opportunity Group (`family`) for the dropdown.
  const angleGroups = useMemo(
    () =>
      groupAnglesByFamily(availableAngles).map((g) => ({
        label: g.label,
        options: g.angles.map((a) => ({
          value: a.id,
          label: a.name,
          description: a.summary ?? null,
        })),
      })),
    [availableAngles],
  );

  const isLoadingAny = loadingPersonas || loadingProducts || loadingAngles;

  const selectedTemplateType = useMemo(
    () => templateTypes.find((template) => template.key === templateTypeKey) ?? null,
    [templateTypes, templateTypeKey],
  );
  const selectedTemplateUsesLpDesignSystem =
    surface === "lp-express-2" && selectedTemplateType?.source === "template-studio";
  const hasValidTemplate =
    landingTemplateTypes.length > 0 &&
    landingTemplateTypes.some((t) => t.key === templateTypeKey);

  // A usable custom concept angle needs some text (single textarea, same as
  // Ads/Email Express — the server falls back to a "Custom angle" name).
  const customAngleValid = customAngleMode && !!customAngleText.trim();
  const hasAngle = customAngleMode ? customAngleValid : !!angleId;
  // The customAngle body field sent with every generate/regenerate request.
  // Undefined outside custom mode so saved-angle requests are byte-identical.
  const customAngleForRequest = customAngleValid
    ? { name: "", description: customAngleText.trim() }
    : undefined;

  const canGenerate =
    !!brandId &&
    !!personaId &&
    !!productId &&
    hasAngle &&
    hasValidTemplate &&
    (!selectedTemplateUsesLpDesignSystem ||
      (!isDesignSystemSelectionPending &&
        !hasFailedDesignSystemSelection &&
        !hasUnavailablePersistedLpDesignSystem));

  // Friendly checklist of what's still missing before Generate can run.
  const missingInputs = useMemo(() => {
    const out: Array<{ key: string; label: string }> = [];
    if (!personaId) out.push({ key: "persona", label: "pick a persona" });
    if (!productId) out.push({ key: "product", label: "pick a product" });
    if (!hasAngle)
      out.push({
        key: "angle",
        label:
          customAngleMode
            ? "describe your custom concept angle"
            : availableAngles.length === 0 && personaId && productId
              ? "switch to a custom concept angle (no saved angles for this pair)"
              : "choose a narrative angle or write a custom concept",
      });
    if (!hasValidTemplate) out.push({ key: "template", label: "choose a template" });
    return out;
  }, [personaId, productId, hasAngle, customAngleMode, availableAngles.length, hasValidTemplate]);

  // Once everything is filled in, retire the highlight state.
  useEffect(() => {
    if (canGenerate && showMissingHints) setShowMissingHints(false);
  }, [canGenerate, showMissingHints]);

  const heroMutation = useMutation<
    { label?: string; headline?: string; subheadline?: string; cta_text?: string },
    Error,
    LpRegenVars
  >({
    mutationFn: async (vars: LpRegenVars) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-hero`, makeCopyBody(vars));
      return parseJsonResponse(res);
    },
    retry: 1,
    onSuccess: (data, vars) => {
      setHero({
        label: data?.label ?? "",
        headline: data?.headline ?? "",
        subheadline: data?.subheadline ?? "",
        cta_text: data?.cta_text ?? "",
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/runs`],
      });
      maybeSnapshotRegen(vars, "regenerate-hero");
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to generate hero",
        description: err?.message || "Try again.",
        variant: "destructive",
      });
    },
  });

  const leadinMutation = useMutation<{ leadin?: string }, Error, LpRegenVars>({
    mutationFn: async (vars: LpRegenVars) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-leadin`, makeCopyBody(vars));
      return parseJsonResponse(res);
    },
    retry: 1,
    onSuccess: (data, vars) => {
      setLeadin(data?.leadin ?? "");
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/runs`],
      });
      maybeSnapshotRegen(vars, "regenerate-leadin");
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to generate lead-in",
        description: err?.message || "Try again.",
        variant: "destructive",
      });
    },
  });

  const showcaseMutation = useMutation<{ headline?: string; subheadlines?: string[] }, Error, LpRegenVars>({
    mutationFn: async (vars: LpRegenVars) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-showcase`, makeCopyBody(vars));
      return parseJsonResponse(res);
    },
    retry: 1,
    onSuccess: (data, vars) => {
      setShowcase({
        headline: data?.headline ?? "",
        subheadlines: Array.isArray(data?.subheadlines) ? data.subheadlines : [],
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/runs`],
      });
      maybeSnapshotRegen(vars, "regenerate-showcase");
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to generate recipe card",
        description: err?.message || "Try again.",
        variant: "destructive",
      });
    },
  });

  // Product Context — separate section: card header/subtext + a background-
  // generated image. Distinct from the recipe card above.
  const productContextMutation = useMutation<Array<{ cardHeader?: string; cardSubtext?: string; imageUrl?: string; altText?: string; imageStatus?: string }>, Error, LpRegenVars>({
    mutationFn: async (vars: LpRegenVars) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-product-context`, makeCopyBody(vars));
      return parseJsonResponse(res);
    },
    retry: 1,
    onSuccess: (data, vars) => {
      const cards = Array.isArray(data) ? data : [];
      setProductContext(cards);
      // Images are generated in the background (gpt-image-2 is slow). When any
      // card is still "pending", flip on the poll so images drop in once each
      // is persisted to R2.
      setProductContextImagePending(
        cards.some((c) => c?.imageStatus === "pending" && !c?.imageUrl),
      );
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/runs`],
      });
      maybeSnapshotRegen(vars, "regenerate-product-context");
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to generate product context",
        description: err?.message || "Try again.",
        variant: "destructive",
      });
    },
  });

  const villainMutation = useMutation<VillainState, Error, LpRegenVars>({
    mutationFn: async (vars: LpRegenVars) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-villain`, makeCopyBody(vars));
      return parseJsonResponse(res);
    },
    retry: 1,
    onSuccess: (data, vars) => {
      setVillain({
        checklist_headline: data?.checklist_headline ?? "",
        checklist_items: Array.isArray(data?.checklist_items) ? data.checklist_items : [],
        checklist_footer: data?.checklist_footer ?? "",
        report_eyebrow: data?.report_eyebrow ?? "",
        report_headline: data?.report_headline ?? "",
        report_rows: Array.isArray(data?.report_rows) ? data.report_rows : [],
        belief_eyebrow: data?.belief_eyebrow ?? "",
        belief_headline: data?.belief_headline ?? "",
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/runs`],
      });
      maybeSnapshotRegen(vars, "regenerate-villain");
    },
    // Per spec: fail silently. The renderer falls back to the hardcoded
    // villain copy if the workflow returns nothing.
    onError: (err: Error) => {
      console.warn("[lp-express] villain generation failed silently:", err?.message);
    },
  });

  const stationsMutation = useMutation<StationsState, Error, LpRegenVars>({
    mutationFn: async (vars: LpRegenVars) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-stations`, makeCopyBody(vars));
      return parseJsonResponse(res);
    },
    retry: 1,
    onSuccess: (data, vars) => {
      setStations({
        section_label: data?.section_label ?? "",
        section_headline: data?.section_headline ?? "",
        stations: Array.isArray(data?.stations) ? data.stations : [],
        loop_quote: data?.loop_quote ?? "",
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/runs`],
      });
      maybeSnapshotRegen(vars, "regenerate-stations");
    },
    // Per spec: fail silently. The renderer's STATIC fallback covers the
    // empty case, so we don't toast here on error.
    onError: (err: Error) => {
      console.warn("[lp-express] stations generation failed silently:", err?.message);
    },
  });

  // Origin copy — the cream-bg farmstand/origin section.
  const originCopyMutation = useMutation<OriginCopyState, Error, LpRegenVars>({
    mutationFn: async (vars: LpRegenVars) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-origin-copy`, makeCopyBody(vars));
      return parseJsonResponse(res);
    },
    retry: 1,
    onSuccess: (data, vars) => {
      setOriginCopy(data ?? null);
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/runs`],
      });
      maybeSnapshotRegen(vars, "regenerate-origin");
    },
    // Per spec: fail silently. The renderer falls back to the hardcoded
    // farmstand copy when origin copy is absent.
    onError: (err: Error) => {
      console.warn("[lp-express] origin copy generation failed silently:", err?.message);
    },
  });

  // Origin images — the Farmstand section's still images. Image prompts come
  // back immediately; the durable R2 images are generated in the background and
  // surfaced via the originImagesQuery poll below.
  const originImagesMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-origin-images`, {
        personaId,
        productId,
        narrativeAngleId: customAngleMode ? undefined : angleId,
        customAngle: customAngleForRequest,
        saleId: saleIdForRequest,
        pageId: pageId || undefined,
      });
      return parseJsonResponse(res);
    },
    retry: 1,
    onSuccess: (data: Array<{ key?: string; imageUrl?: string; altText?: string; imageStatus?: string }>) => {
      const slots = Array.isArray(data) ? data : [];
      setOriginImages(slots);
      // Images render in the background; poll until each slot is ready/failed.
      setOriginImagesPending(
        slots.some((s) => s?.imageStatus === "pending" && !s?.imageUrl),
      );
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/runs`],
      });
    },
    // Per spec: fail silently. The renderer falls back to the hardcoded
    // farmstand placeholders when origin images are absent.
    onError: (err: Error) => {
      console.warn("[lp-express] origin image generation failed silently:", err?.message);
    },
  });

  // Quote copy — the 7 short pull-quotes scattered across the page. The route
  // fans out one workflow call per variant and returns them keyed by variant.
  const quotesMutation = useMutation<{ quotes?: Record<string, QuoteState> }, Error, LpRegenVars>({
    mutationFn: async (vars: LpRegenVars) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-quotes`, makeCopyBody(vars));
      return parseJsonResponse(res);
    },
    retry: 1,
    onSuccess: (data, vars) => {
      setQuotes(data?.quotes ?? null);
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/runs`],
      });
      maybeSnapshotRegen(vars, "regenerate-quotes");
    },
    // Per spec: fail silently. Missing quotes fall back to hardcoded copy.
    onError: (err: Error) => {
      console.warn("[lp-express] quote copy generation failed silently:", err?.message);
    },
  });

  // ── Long-form section copy mutations (8 workflows). Each fails silently and
  // falls back to the template's hardcoded copy. ──
  const makeCopyBody = (vars?: LpRegenVars) => ({
    personaId,
    productId,
    narrativeAngleId: customAngleMode ? undefined : angleId,
    customAngle: customAngleForRequest,
    saleId: saleIdForRequest,
    pageId: pageId || undefined,
    // Copy-regeneration steering. Empty strings on automatic backfill / image
    // re-renders; populated only when the user submits per-block/section feedback.
    feedback: (vars && vars.feedback) || "",
    currentCopy: (vars && vars.currentCopy) || "",
  });

  // ── Retry a single failed page-image slot one at a time. Optimistically sets
  // the slot back to "pending" (so its spinner shows) and re-enables the array's
  // background poll, then calls the per-slot retry route. On request failure the
  // slot is reverted to "failed" so the retry affordance returns. ──
  type LpRetryMergeKey =
    | "originImages"
    | "alternativesImages"
    | "philosophyImages"
    | "proofImages"
    | "listicleImages"
    | "advertorialImages"
    | "coldDripImages"
    | "coffeeShopImages"
    | "temeculaPassportImages"
    | "papabearLabelImages"
    | "productHeroImages"
    | "problemSolutionImages"
    | "offerStackImages";
  const handleRetrySlot = async (mergeKey: LpRetryMergeKey, slotKey: string) => {
    if (!pageId || !slotKey) return;
    const setSlotStatus = (
      arr: LpImageSlot[] | null,
      status: string,
    ): LpImageSlot[] | null => {
      if (!Array.isArray(arr)) return arr;
      return arr.map((s) =>
        s?.key === slotKey
          ? { ...s, imageStatus: status, ...(status === "pending" ? { imageUrl: undefined } : {}) }
          : s,
      );
    };
    const setters: Record<
      LpRetryMergeKey,
      { update: (status: string) => void; enablePoll: () => void }
    > = {
      originImages: {
        update: (status) => setOriginImages((prev) => setSlotStatus(prev, status)),
        enablePoll: () => setOriginImagesPending(true),
      },
      alternativesImages: {
        update: (status) => setAlternativesImages((prev) => setSlotStatus(prev, status)),
        enablePoll: () => setAlternativesImagesPending(true),
      },
      philosophyImages: {
        update: (status) => setPhilosophyImages((prev) => setSlotStatus(prev, status)),
        enablePoll: () => setPhilosophyImagesPending(true),
      },
      proofImages: {
        update: (status) => setProofImages((prev) => setSlotStatus(prev, status)),
        enablePoll: () => setProofImagesPending(true),
      },
      listicleImages: {
        update: (status) => setListicleImages((prev) => setSlotStatus(prev, status)),
        enablePoll: () => setListicleImagesPending(true),
      },
      advertorialImages: {
        update: (status) => setAdvertorialImages((prev) => setSlotStatus(prev, status)),
        enablePoll: () => setAdvertorialImagesPending(true),
      },
      coldDripImages: {
        update: (status) => setColdDripImages((prev) => setSlotStatus(prev, status)),
        enablePoll: () => setColdDripImagesPending(true),
      },
      coffeeShopImages: {
        update: (status) => setCoffeeShopImages((prev) => setSlotStatus(prev, status)),
        enablePoll: () => setCoffeeShopImagesPending(true),
      },
      temeculaPassportImages: {
        update: (status) => setTemeculaPassportImages((prev) => setSlotStatus(prev, status)),
        enablePoll: () => setTemeculaPassportImagesPending(true),
      },
      papabearLabelImages: {
        update: (status) => setPapabearLabelImages((prev) => setSlotStatus(prev, status)),
        enablePoll: () => setPapabearLabelImagesPending(true),
      },
      productHeroImages: {
        update: (status) => setProductHeroImages((prev) => setSlotStatus(prev, status)),
        enablePoll: () => setProductHeroImagesPending(true),
      },
      problemSolutionImages: {
        update: (status) => setProblemSolutionImages((prev) => setSlotStatus(prev, status)),
        enablePoll: () => setProblemSolutionImagesPending(true),
      },
      offerStackImages: {
        update: (status) => setOfferStackImages((prev) => setSlotStatus(prev, status)),
        enablePoll: () => setOfferStackImagesPending(true),
      },
    };
    const ctl = setters[mergeKey];
    const currentSlots: Record<LpRetryMergeKey, LpImageSlot[] | null> = {
      originImages,
      alternativesImages,
      philosophyImages,
      proofImages,
      listicleImages,
      advertorialImages,
      coldDripImages,
      coffeeShopImages,
      temeculaPassportImages,
      papabearLabelImages,
      productHeroImages,
      problemSolutionImages,
      offerStackImages,
    };
    const savedSlot = currentSlots[mergeKey]?.find(
      (slot) => slot?.key === slotKey,
    );
    const expectedImageUrl =
      typeof savedSlot?.imageUrl === "string" && savedSlot.imageUrl.trim()
        ? savedSlot.imageUrl.trim()
        : null;
    ctl.update("pending");
    try {
      await apiRequest("POST", `/api/brands/${brandId}/lp-express/retry-image`, {
        ...makeCopyBody(),
        pageId,
        mergeKey,
        slotKey,
        expectedImageUrl,
      });
      // Only start polling AFTER the server has reset the slot to pending (the
      // request returns once that reset is persisted). Enabling the poll earlier
      // lets it read the still-stale image and clobber the optimistic spinner.
      ctl.enablePoll();
    } catch (err: any) {
      ctl.update("failed");
      toast({
        title: "Retry failed",
        description: "Could not regenerate that image. Please try again.",
        variant: "destructive",
      });
      console.warn("[lp-express] retry-image request failed:", err?.message);
    }
  };

  // Retry a single hero-carousel slot. Optimistically flips the page query to
  // "rendering" (so polling resumes and that slot shows a spinner), then asks
  // the server to reset + re-render just that slot.
  const handleRetryHeroSlot = async (slotIndex: number) => {
    if (!pageId) return;
    queryClient.setQueryData(["/api/landing-pages", pageId], (prev: any) => {
      if (!prev) return prev;
      const urls = Array.isArray(prev.heroImageUrls) ? [...prev.heroImageUrls] : [];
      while (urls.length <= slotIndex) urls.push(null);
      urls[slotIndex] = null;
      const statuses = Array.isArray(prev.heroImageSlotStatuses)
        ? [...prev.heroImageSlotStatuses]
        : [];
      while (statuses.length <= slotIndex) statuses.push("pending");
      statuses[slotIndex] = "pending";
      return {
        ...prev,
        heroImageStatus: "rendering",
        heroImageError: null,
        heroImageUrls: urls,
        heroImageSlotStatuses: statuses,
      };
    });
    try {
      await apiRequest("POST", `/api/landing-pages/${pageId}/retry-hero-image`, { slotIndex });
      queryClient.invalidateQueries({ queryKey: ["/api/landing-pages", pageId] });
    } catch (err: any) {
      // Revert the optimistic flip so the failed slot's retry affordance returns.
      queryClient.setQueryData(["/api/landing-pages", pageId], (prev: any) => {
        if (!prev) return prev;
        const statuses = Array.isArray(prev.heroImageSlotStatuses)
          ? [...prev.heroImageSlotStatuses]
          : [];
        while (statuses.length <= slotIndex) statuses.push("failed");
        statuses[slotIndex] = "failed";
        return { ...prev, heroImageSlotStatuses: statuses };
      });
      toast({
        title: "Retry failed",
        description: "Could not regenerate that image. Please try again.",
        variant: "destructive",
      });
      console.warn("[lp-express] retry-hero-image request failed:", err?.message);
    }
  };

  // Retry a single "Real ingredients" belief-card image. Optimistically marks
  // that card pending (spinner) and re-enables the product-context poll, then
  // asks the server to re-render just that card's image (copy is preserved).
  const handleRetryProductContext = async (cardIndex: number) => {
    if (!pageId) return;
    setProductContext((prev) =>
      Array.isArray(prev)
        ? prev.map((c, i) =>
            i === cardIndex
              ? { ...c, imageStatus: "pending", imageUrl: undefined }
              : c,
          )
        : prev,
    );
    try {
      await apiRequest(
        "POST",
        `/api/brands/${brandId}/lp-express/retry-product-context-image`,
        { ...makeCopyBody(), pageId, cardIndex },
      );
      // Only start polling AFTER the server has reset the card to pending (the
      // request returns once that reset is persisted). Enabling the poll earlier
      // lets it read the still-ready server card, clobber the optimistic spinner,
      // and stop polling before the new image finishes rendering.
      setProductContextImagePending(true);
    } catch (err: any) {
      setProductContext((prev) =>
        Array.isArray(prev)
          ? prev.map((c, i) =>
              i === cardIndex ? { ...c, imageStatus: "failed" } : c,
            )
          : prev,
      );
      toast({
        title: "Retry failed",
        description: "Could not regenerate that image. Please try again.",
        variant: "destructive",
      });
      console.warn("[lp-express] retry-product-context-image request failed:", err?.message);
    }
  };
  const timelineMutation = useMutation<TimelineState, Error, LpRegenVars>({
    mutationFn: async (vars: LpRegenVars) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-timeline`, makeCopyBody(vars));
      return parseJsonResponse(res);
    },
    retry: 1,
    onSuccess: (data, vars) => {
      setTimeline(data ?? null);
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/lp-express/runs`] });
      maybeSnapshotRegen(vars, "regenerate-timeline");
    },
    onError: (err: Error) => { console.warn("[lp-express] timeline copy generation failed silently:", err?.message); },
  });
  const membershipMutation = useMutation<MembershipState, Error, LpRegenVars>({
    mutationFn: async (vars: LpRegenVars) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-membership`, makeCopyBody(vars));
      return parseJsonResponse(res);
    },
    retry: 1,
    onSuccess: (data, vars) => {
      setMembership(data ?? null);
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/lp-express/runs`] });
      maybeSnapshotRegen(vars, "regenerate-membership");
    },
    onError: (err: Error) => { console.warn("[lp-express] membership copy generation failed silently:", err?.message); },
  });
  const alternativesMutation = useMutation<AlternativesState, Error, LpRegenVars>({
    mutationFn: async (vars: LpRegenVars) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-alternatives`, makeCopyBody(vars));
      return parseJsonResponse(res);
    },
    retry: 1,
    onSuccess: (data, vars) => {
      setAlternatives(data ?? null);
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/lp-express/runs`] });
      maybeSnapshotRegen(vars, "regenerate-alternatives");
    },
    onError: (err: Error) => { console.warn("[lp-express] alternatives copy generation failed silently:", err?.message); },
  });
  const philosophyMutation = useMutation<PhilosophyState, Error, LpRegenVars>({
    mutationFn: async (vars: LpRegenVars) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-philosophy`, makeCopyBody(vars));
      return parseJsonResponse(res);
    },
    retry: 1,
    onSuccess: (data, vars) => {
      setPhilosophy(data ?? null);
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/lp-express/runs`] });
      maybeSnapshotRegen(vars, "regenerate-philosophy");
    },
    onError: (err: Error) => { console.warn("[lp-express] philosophy copy generation failed silently:", err?.message); },
  });
  const differentiatorMutation = useMutation<DifferentiatorState, Error, LpRegenVars>({
    mutationFn: async (vars: LpRegenVars) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-differentiator`, makeCopyBody(vars));
      return parseJsonResponse(res);
    },
    retry: 1,
    onSuccess: (data, vars) => {
      setDifferentiator(data ?? null);
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/lp-express/runs`] });
      maybeSnapshotRegen(vars, "regenerate-differentiator");
    },
    onError: (err: Error) => { console.warn("[lp-express] differentiator copy generation failed silently:", err?.message); },
  });
  const faqMutation = useMutation<FaqState, Error, LpRegenVars>({
    mutationFn: async (vars: LpRegenVars) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-faq`, makeCopyBody(vars));
      return parseJsonResponse(res);
    },
    retry: 1,
    onSuccess: (data, vars) => {
      setFaq(data ?? null);
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/lp-express/runs`] });
      maybeSnapshotRegen(vars, "regenerate-faq");
    },
    onError: (err: Error) => { console.warn("[lp-express] faq copy generation failed silently:", err?.message); },
  });
  const reviewsMutation = useMutation<ReviewsState, Error, LpRegenVars>({
    mutationFn: async (vars: LpRegenVars) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-reviews`, makeCopyBody(vars));
      return parseJsonResponse(res);
    },
    retry: 1,
    onSuccess: (data, vars) => {
      setReviews(data ?? null);
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/lp-express/runs`] });
      maybeSnapshotRegen(vars, "regenerate-reviews");
    },
    onError: (err: Error) => { console.warn("[lp-express] reviews copy generation failed silently:", err?.message); },
  });
  const finalCtaMutation = useMutation<FinalCtaState, Error, LpRegenVars>({
    mutationFn: async (vars: LpRegenVars) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-final-cta`, makeCopyBody(vars));
      return parseJsonResponse(res);
    },
    retry: 1,
    onSuccess: (data, vars) => {
      setFinalCta(data ?? null);
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/lp-express/runs`] });
      maybeSnapshotRegen(vars, "regenerate-final-cta");
    },
    onError: (err: Error) => { console.warn("[lp-express] final-cta copy generation failed silently:", err?.message); },
  });
  const proofMutation = useMutation<ProofState, Error, LpRegenVars>({
    mutationFn: async (vars: LpRegenVars) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-proof`, makeCopyBody(vars));
      return parseJsonResponse(res);
    },
    retry: 1,
    onSuccess: (data, vars) => {
      setProof(data ?? null);
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/lp-express/runs`] });
      maybeSnapshotRegen(vars, "regenerate-proof");
    },
    onError: (err: Error) => { console.warn("[lp-express] proof copy generation failed silently:", err?.message); },
  });

  // ── Section image mutations (3 workflows). Prompts return immediately;
  // durable R2 images render in the background and are surfaced via the polls
  // below. Missing/failed slots fall back to the hardcoded placeholders. ──
  const alternativesImagesMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-alternatives-images`, makeCopyBody());
      return parseJsonResponse(res);
    },
    retry: 1,
    onSuccess: (data: LpImageSlot[]) => {
      const slots = Array.isArray(data) ? data : [];
      setAlternativesImages(slots);
      setAlternativesImagesPending(slots.some((s) => s?.imageStatus === "pending" && !s?.imageUrl));
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/lp-express/runs`] });
    },
    onError: (err: Error) => { console.warn("[lp-express] alternatives image generation failed silently:", err?.message); },
  });
  const philosophyImagesMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-philosophy-images`, makeCopyBody());
      return parseJsonResponse(res);
    },
    retry: 1,
    onSuccess: (data: LpImageSlot[]) => {
      const slots = Array.isArray(data) ? data : [];
      setPhilosophyImages(slots);
      setPhilosophyImagesPending(slots.some((s) => s?.imageStatus === "pending" && !s?.imageUrl));
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/lp-express/runs`] });
    },
    onError: (err: Error) => { console.warn("[lp-express] philosophy image generation failed silently:", err?.message); },
  });
  const proofImagesMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-proof-images`, makeCopyBody());
      return parseJsonResponse(res);
    },
    retry: 1,
    onSuccess: (data: LpImageSlot[]) => {
      const slots = Array.isArray(data) ? data : [];
      setProofImages(slots);
      setProofImagesPending(slots.some((s) => s?.imageStatus === "pending" && !s?.imageUrl));
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/lp-express/runs`] });
    },
    onError: (err: Error) => { console.warn("[lp-express] proof image generation failed silently:", err?.message); },
  });
  const papabearLabelImagesMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/lp-express/generate-papabear-label-images`,
        makeCopyBody(),
      );
      return parseJsonResponse(res);
    },
    retry: 0,
    onSuccess: (data: LpImageSlot[]) => {
      const slots = Array.isArray(data) ? data : [];
      setPapabearLabelImages(slots);
      setPapabearLabelImagesPending(
        slots.some((slot) => slot?.imageStatus === "pending" && !slot?.imageUrl),
      );
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/runs`],
      });
    },
    onError: (err: Error) => {
      console.warn(
        "[lp-express] Papabear reason image generation failed safely:",
        err?.message,
      );
    },
  });

  const advisoryMutation = useMutation<{
    advisory_label?: string;
    advisory_headline?: string;
    advisory_subheadline?: string;
    advisory_headline_muted?: string;
    advisory_headline_bold?: string;
    advisory_headline_accent?: string;
  }, Error, LpRegenVars>({
    mutationFn: async (vars: LpRegenVars) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-advisory`, makeCopyBody(vars));
      return parseJsonResponse(res);
    },
    retry: 1,
    onSuccess: (data, vars) => {
      setAdvisory({
        advisory_label: data?.advisory_label ?? "",
        advisory_headline: data?.advisory_headline ?? "",
        advisory_subheadline: data?.advisory_subheadline ?? "",
        advisory_headline_muted: data?.advisory_headline_muted ?? "",
        advisory_headline_bold: data?.advisory_headline_bold ?? "",
        advisory_headline_accent: data?.advisory_headline_accent ?? "",
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/runs`],
      });
      maybeSnapshotRegen(vars, "regenerate-advisory");
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to generate advisory",
        description: err?.message || "Try again.",
        variant: "destructive",
      });
    },
  });

  // Apply a stored generatedContent blob to local editor state. Reused by the
  // run-fetch hydration effect and by undo/redo/restore so the preview reflects
  // a restored snapshot without a full page refetch.
  const applyGcToLocalState = (gc: any) => {
    if (isSmbHomeAttempt(gc)) setInputsExpanded(false);
    setLeadin(gc.leadin ?? "");
    setAdvisory(gc.advisory ?? null);
    setShowcase(gc.showcase ?? null);
    const pcArr = Array.isArray(gc.productContext) ? gc.productContext : null;
    setProductContext(pcArr);
    // Resume polling if any saved card's product-context image is still
    // generating.
    setProductContextImagePending(
      !!pcArr?.some((c: any) => c?.imageStatus === "pending" && !c?.imageUrl),
    );
    setStations(gc.stations ?? null);
    // Only hydrate villain if it carries the new structured schema
    // (checklist_items / report_rows / headlines). Older saved rows
    // with the legacy intro-band shape are treated as "regenerate".
    const v: any = gc.villain;
    const hasNewVillainSchema =
      v &&
      typeof v === "object" &&
      (Array.isArray(v.checklist_items) ||
        Array.isArray(v.report_rows) ||
        typeof v.checklist_headline === "string" ||
        typeof v.report_headline === "string");
    setVillain(hasNewVillainSchema ? v : null);
    setOriginCopy(gc.originCopy ?? null);
    setTemplateType(gc.studioDesign?.rendererTemplateKey === "smb-home" ? "smb-home" : gc.templateType ?? null);
    setStudioDesign(resolveLpStudioPreviewDesign(gc));
    setStudioPageDesignStatus(
      gc.studioBrandDesign && typeof gc.studioBrandDesign === "object"
        ? gc.studioBrandDesign
        : gc.studioBrandDesignInput &&
            (gc.studioDesign?.rendererTemplateKey !== "smb-home" ||
              !!gc.studioBrandDesignInput.designSystemId) &&
            typeof gc.studioBrandDesignInput === "object"
          ? {
              status: "pending",
              submissionState: "reserved",
              designSystemId: gc.studioBrandDesignInput.designSystemId ?? null,
            }
          : null,
    );
    setStudioRevisionStatus(
      gc.studioRevision && typeof gc.studioRevision === "object"
        ? gc.studioRevision
        : null,
    );
    setStudioBulkRegeneration(gc.lp2Regeneration ?? {});
    setQuiz(gc.templateType === "quiz" ? (gc.quiz ?? null) : null);
    setListicle(gc.templateType === "listicle" ? (gc.listicle ?? null) : null);
    const liImgs =
      gc.templateType === "listicle" && Array.isArray(gc.listicleImages)
        ? gc.listicleImages
        : null;
    setListicleImages(liImgs);
    // Resume polling if any saved listicle image slot is still generating.
    setListicleImagesPending(
      !!liImgs?.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl),
    );
    setAdvertorial(gc.templateType === "advertorial" ? (gc.advertorial ?? null) : null);
    setColdDrip(gc.templateType === "cold-drip" ? (gc.coldDrip ?? null) : null);
    const cdImgs =
      gc.templateType === "cold-drip" && Array.isArray(gc.coldDripImages)
        ? gc.coldDripImages
        : null;
    setColdDripImages(cdImgs);
    // Resume polling if any saved cold-drip image slot is still generating.
    setColdDripImagesPending(
      !!cdImgs?.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl),
    );
    setCoffeeShop(gc.templateType === "coffee-shop" ? (gc.coffeeShop ?? null) : null);
    const csImgs =
      gc.templateType === "coffee-shop" && Array.isArray(gc.coffeeShopImages)
        ? gc.coffeeShopImages
        : null;
    setCoffeeShopImages(csImgs);
    // Resume polling if any saved coffee-shop image slot is still generating.
    setCoffeeShopImagesPending(
      !!csImgs?.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl),
    );
    setTemeculaPassport(gc.templateType === "temecula-passport" ? (gc.temeculaPassport ?? null) : null);
    setPapabearLabel(
      gc.templateType === "papabear-label" ? (gc.papabearLabel ?? null) : null,
    );
    const pbImgs =
      gc.templateType === "papabear-label" &&
      Array.isArray(gc.papabearLabelImages)
        ? gc.papabearLabelImages
        : null;
    setPapabearLabelImages(pbImgs);
    setPapabearLabelImagesPending(
      !!pbImgs?.some(
        (slot: any) =>
          slot?.imageStatus === "pending" && !slot?.imageUrl,
      ),
    );
    const tpImgs =
      gc.templateType === "temecula-passport" && Array.isArray(gc.temeculaPassportImages)
        ? gc.temeculaPassportImages
        : null;
    setTemeculaPassportImages(tpImgs);
    // Resume polling if any saved temecula-passport image slot is still generating.
    setTemeculaPassportImagesPending(
      !!tpImgs?.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl),
    );
    const advImgs =
      gc.templateType === "advertorial" && Array.isArray(gc.advertorialImages)
        ? gc.advertorialImages
        : null;
    setAdvertorialImages(advImgs);
    // Resume polling if any saved advertorial image slot is still generating.
    setAdvertorialImagesPending(
      !!advImgs?.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl),
    );
    // Product Hero: per-section copy keys (the gallery's main image stays the
    // brand's real product photos) plus the optional AI lifestyle gallery
    // slots in productHeroImages.
    setProductHeroSections(
      gc.studioDesign?.rendererTemplateKey === "smb-home"
        ? splitLpSmbHomeContent(assembleLpSmbHomeContent(gc))
        : gc.templateType === "product-hero"
        ? Object.fromEntries(
            (LP_PRODUCT_HERO_BLOCK_KEYS as readonly string[])
              .filter((k) => k in gc)
              .map((k) => [k, (gc as any)[k]]),
          )
        : {},
    );
    setSmbPageMetadata(gc.studioDesign?.rendererTemplateKey === "smb-home" ? {
      smbHomeCopy: assembleLpSmbHomeContent(gc), ctaUrl: gc.ctaUrl,
      smbActionSnapshot: gc.smbActionSnapshot,
      smbHomeAssets: gc.smbHomeAssets ?? gc.studioCreation?.intent?.imageAssets ?? gc.studioBrandDesignInput?.imageAssets ?? {},
      smbHomeAssetProvenance: gc.smbHomeAssetProvenance ?? {},
      smbHomeImages: gc.smbHomeImages,
    } : {});
    const phImgs =
      gc.templateType === "product-hero" && Array.isArray(gc.productHeroImages)
        ? gc.productHeroImages
        : null;
    setProductHeroImages(phImgs);
    // Resume polling if any saved lifestyle slot is still generating.
    setProductHeroImagesPending(
      !!phImgs?.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl),
    );
    // Problem/Solution: per-section copy keys plus the AI mechanism-diagram
    // slot in problemSolutionImages (the solution-reveal photo stays the
    // brand's real product photo).
    setProblemSolutionSections(
      gc.templateType === "problem-solution"
        ? Object.fromEntries(
            (LP_PROBLEM_SOLUTION_BLOCK_KEYS as readonly string[])
              .filter((k) => k in gc)
              .map((k) => [k, (gc as any)[k]]),
          )
        : {},
    );
    const psImgs =
      gc.templateType === "problem-solution" && Array.isArray(gc.problemSolutionImages)
        ? gc.problemSolutionImages
        : null;
    setProblemSolutionImages(psImgs);
    // Resume polling if the saved mechanism-diagram slot is still generating.
    setProblemSolutionImagesPending(
      !!psImgs?.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl),
    );
    // Offer Stack: per-section copy keys plus the AI intro-visual slot in
    // offerStackImages (the real product photo stays the fallback).
    setOfferStackSections(
      gc.templateType === "offer-stack"
        ? Object.fromEntries(
            (LP_OFFER_STACK_BLOCK_KEYS as readonly string[])
              .filter((k) => k in gc)
              .map((k) => [k, (gc as any)[k]]),
          )
        : {},
    );
    const osImgs =
      gc.templateType === "offer-stack" && Array.isArray(gc.offerStackImages)
        ? gc.offerStackImages
        : null;
    setOfferStackImages(osImgs);
    // Resume polling if the saved intro-visual slot is still generating.
    setOfferStackImagesPending(
      !!osImgs?.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl),
    );
    const oiArr = Array.isArray(gc.originImages) ? gc.originImages : null;
    setOriginImages(oiArr);
    // Resume polling if any saved origin slot's image is still generating.
    setOriginImagesPending(
      !!oiArr?.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl),
    );
    setQuotes(gc.quotes ?? null);
    setTimeline(gc.timeline ?? null);
    setMembership(gc.membership ?? null);
    setAlternatives(gc.alternatives ?? null);
    setPhilosophy(gc.philosophy ?? null);
    setDifferentiator(gc.differentiator ?? null);
    setFaq(gc.faq ?? null);
    setReviews(gc.reviews ?? null);
    setFinalCta(gc.finalCta ?? null);
    setFooter(gc.footer ?? null);
    setProof(gc.proof ?? null);
    // Hydrate the saved section layout (order + hidden; falls back to the
    // template's canonical block list so a product-hero page never surfaces the
    // long-form blocks and vice-versa).
    setSectionLayout(
      sectionLayoutFromSections((gc as any).sections, lpTemplateSectionTypes(gc)),
    );
    // Per-page video overrides + element hide/delete state.
    setVideoOverrides(
      (gc as any).videoOverrides && typeof (gc as any).videoOverrides === "object"
        ? (gc as any).videoOverrides
        : {},
    );
    setElementStates(
      (gc as any).elements && typeof (gc as any).elements === "object"
        ? (gc as any).elements
        : {},
    );
    setTheme(
      (gc as any).theme && typeof (gc as any).theme === "object"
        ? (gc as any).theme
        : {},
    );
    setElementStyles(
      (gc as any).styles && typeof (gc as any).styles === "object"
        ? (gc as any).styles
        : {},
    );
    setComponentOrder(sanitizeLpComponentOrder((gc as any).componentOrder) ?? {});
    setMediaOverrides(sanitizeLpMediaOverrides((gc as any).mediaOverrides));
    setReviewOverrides(sanitizeLpReviewOverrides((gc as any).reviewOverrides));
    const altImgs = Array.isArray(gc.alternativesImages) ? gc.alternativesImages : null;
    setAlternativesImages(altImgs);
    setAlternativesImagesPending(!!altImgs?.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl));
    const philImgs = Array.isArray(gc.philosophyImages) ? gc.philosophyImages : null;
    setPhilosophyImages(philImgs);
    setPhilosophyImagesPending(!!philImgs?.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl));
    const prfImgs = Array.isArray(gc.proofImages) ? gc.proofImages : null;
    setProofImages(prfImgs);
    setProofImagesPending(!!prfImgs?.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl));
    // Quiz/listicle/advertorial/product-hero pages have no hero/sections chain —
    // keep hero null so the section backfill effect (all guarded on `hero`)
    // never fires for them.
    setHero(
      gc.templateType === "quiz" ||
        gc.templateType === "listicle" ||
        gc.templateType === "advertorial" ||
        gc.templateType === "product-hero" ||
        isSmbHomeAttempt(gc) ||
        gc.templateType === "problem-solution" ||
        gc.templateType === "offer-stack"
        ? null
        : (gc.hero ?? { label: "", headline: "", subheadline: "", cta_text: "" }),
    );
    // Honor a server-persisted hero failure (e.g. a regen that failed) so the
    // error/retry state survives reload/hydration.
    // Home lifecycle belongs to the owning run, not an independently fetched
    // content snapshot (which may precede failure or recovery).
    if (!isSmbHomeAttempt(gc)) setHeroError((gc as any)?.heroError ?? null);
  };

  const studioBulkQuery = useQuery<any>({
    queryKey: ["/api/landing-pages", pageId, "lp2-bulk-regeneration", surface],
    queryFn: async () => {
      const response = await fetch(`/api/landing-pages/${pageId}`, {
        credentials: "include",
      });
      return parseJsonResponse(response);
    },
    enabled:
      surface === "lp-express-2" &&
      !!pageId &&
      !!studioDesign,
    refetchInterval: (query) => {
      const state = query.state.data?.generatedContent?.lp2Regeneration;
      const revision = query.state.data?.generatedContent?.studioRevision;
      return state?.copy?.status === "pending" ||
        state?.images?.status === "pending" ||
        revision?.status === "revising"
        ? 2000
        : false;
    },
  });
  useEffect(() => {
    const gc = studioBulkQuery.data?.generatedContent;
    if (gc?.catalogSource === "template-studio" &&
        (!isSmbHomeAttempt(gc) || (smbHomeRunReady && !productHeroGenerating && !heroError))) {
      applyGcToLocalState(gc);
    }
    if (gc?.studioRevision?.status === "ready" && gc?.studioRevision?.snapshotRecordedAttemptId) {
      // The server commits the chat-revision history row atomically with the
      // fenced HTML update. Refresh navigation only; never create a snapshot
      // from a browser effect.
      queryClient.invalidateQueries({ queryKey: lpHistoryKey });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studioBulkQuery.data]);

  const regenerateStudioMutation = useMutation({
    mutationFn: async (mode: "copy" | "images") => {
      const response = await apiRequest(
        "POST",
        `/api/landing-pages/${pageId}/lp-express/regenerate-studio/${mode}`,
        {},
      );
      return { mode, data: await parseJsonResponse(response) };
    },
    onSuccess: ({ mode, data }) => {
      setStudioBulkRegeneration((previous: any) => ({
        ...previous,
        [mode]: {
          status: "pending",
          jobId: data.jobId,
          error: null,
        },
      }));
      void studioBulkQuery.refetch();
    },
    onError: (error: any) => {
      toast({
        title: "Couldn't start regeneration",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const studioRevisionMutation = useMutation({
    mutationFn: async (instruction: string) => {
      const response = await apiRequest(
        "POST",
        `/api/landing-pages/${pageId}/lp-express/studio-revision`,
        { instruction },
      );
      return parseJsonResponse(response);
    },
    onSuccess: () => {
      void studioBulkQuery.refetch();
    },
    onError: (error: any) => {
      toast({
        title: "Couldn't update design",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const studioRevisionBusy =
    studioRevisionStatus?.status === "revising" ||
    studioRevisionMutation.isPending;

  const hydratingFromRunRef = useRef(false);
  useEffect(() => {
    if (hydratingFromRunRef.current) {
      hydratingFromRunRef.current = false;
    }
    // Backfill runs both for a fresh Generate (every section is null) and when
    // hydrating a previously-saved run. Each branch is guarded by `!<data>`, so
    // a fully-generated run fires nothing, while an older run that predates a
    // newly-integrated workflow auto-runs ONLY the missing section(s) and
    // persists the result back to that run (pageId is set during hydration).
    {
      if (hero && !leadin && !leadinMutation.isPending) {
        leadinMutation.mutate();
      }
      if (hero && !advisory && !advisoryMutation.isPending) {
        // Stations depends on the villain frame being set, so kick it off
        // right after advisory resolves (not before).
        advisoryMutation.mutate(undefined, {
          onSettled: () => {
            if (!stations && !stationsMutation.isPending) {
              stationsMutation.mutate();
            }
          },
        });
      } else if (hero && advisory && !stations && !stationsMutation.isPending) {
        stationsMutation.mutate();
      }
      if (hero && !showcase && !showcaseMutation.isPending) {
        showcaseMutation.mutate();
      }
      if (hero && !productContext && !productContextMutation.isPending) {
        productContextMutation.mutate();
      }
      if (hero && !villain && !villainMutation.isPending) {
        villainMutation.mutate();
      }
      if (hero && !originCopy && !originCopyMutation.isPending) {
        // Origin images are kicked off once the origin section exists, right
        // after origin copy resolves (so we don't double-load Sim).
        originCopyMutation.mutate(undefined, {
          onSettled: () => {
            if (!originImages && !originImagesMutation.isPending) {
              originImagesMutation.mutate();
            }
          },
        });
      } else if (hero && originCopy && !originImages && !originImagesMutation.isPending) {
        originImagesMutation.mutate();
      }
      if (hero && !quotes && !quotesMutation.isPending) {
        quotesMutation.mutate();
      }
      // Long-form section copy (8 workflows) — fire in parallel on hero ready.
      if (hero && !timeline && !timelineMutation.isPending) timelineMutation.mutate();
      if (hero && !membership && !membershipMutation.isPending) membershipMutation.mutate();
      if (hero && !alternatives && !alternativesMutation.isPending) alternativesMutation.mutate();
      if (hero && !philosophy && !philosophyMutation.isPending) philosophyMutation.mutate();
      if (hero && !differentiator && !differentiatorMutation.isPending) differentiatorMutation.mutate();
      if (hero && !faq && !faqMutation.isPending) faqMutation.mutate();
      if (hero && !reviews && !reviewsMutation.isPending) reviewsMutation.mutate();
      if (hero && !finalCta && !finalCtaMutation.isPending) finalCtaMutation.mutate();
      if (hero && !proof && !proofMutation.isPending) proofMutation.mutate();
      // Section images (3 workflows) — fire separately; render in background.
      if (hero && !alternativesImages && !alternativesImagesMutation.isPending) alternativesImagesMutation.mutate();
      if (hero && !philosophyImages && !philosophyImagesMutation.isPending) philosophyImagesMutation.mutate();
      if (hero && !proofImages && !proofImagesMutation.isPending) proofImagesMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hero]);

  // On mount: if URL has ?runId=..., fetch that run and hydrate state. The
  // hydratingFromRunRef flag lets the [hero] effect above know this is a
  // run-load (not a fresh Generate); that effect then backfills ONLY the
  // sections this run is missing (e.g. a newly-integrated workflow) and
  // persists them back to the run, rather than re-running everything.
  const urlRunId = useMemo(
    () => new URLSearchParams(search).get("runId") || "",
    [search],
  );
  useEffect(() => {
    if (!brandId || !urlRunId) return;
    // Skip refetch if we're already showing this run (e.g. right after Generate
    // updates the URL via setSelectedRunId + setLocation).
    if (urlRunId === selectedRunId && hero) return;
    let cancelled = false;
    const requestBrandId = brandId;
    const requestRunId = urlRunId;
    const requestNavigationEpoch = navigationEpochRef.current;
    (async () => {
      try {
        const res = await fetch(`/api/lp-express/runs/${urlRunId}`, { credentials: "include" });
        if (!res.ok) {
          if (!cancelled) {
            let detail = "";
            try {
              detail = (await res.json())?.message || "";
            } catch {
              /* non-JSON body */
            }
            toast({
              title: "Couldn't open this page",
              description:
                detail || `The page failed to load (error ${res.status}).`,
              variant: "destructive",
            });
          }
          return;
        }
        const r = (await res.json()) as RunRow;
        if (
          cancelled ||
          brandId !== requestBrandId ||
          urlRunId !== requestRunId ||
          navigationEpochRef.current !== requestNavigationEpoch ||
          r.brandId !== requestBrandId ||
          r.id !== requestRunId
        ) return;
        const gc = r.generatedContent ?? {};
        setSmbHomeRunReady(isLpSmbHomeRunReady(r));
        hydratingFromRunRef.current = true;
        if (r.personaId) setPersonaId(r.personaId);
        if (r.productId) setProductId(r.productId);
        if (r.narrativeAngleId) {
          setAngleId(r.narrativeAngleId);
          setCustomAngleMode(false);
        } else if ((gc as any)?.customAngle) {
          // Custom-angle run: restore the user's written angle so section
          // backfills and regeneration reuse the exact same angle text.
          const ca = (gc as any).customAngle as { name?: string; description?: string };
          setCustomAngleMode(true);
          // Single-textarea concept (older runs may only have a name).
          setCustomAngleText(
            (typeof ca.description === "string" && ca.description) ||
              (typeof ca.name === "string" ? ca.name : ""),
          );
        }
        // Restore the sale chosen when this run was generated (null => the page
        // was built with no discount). Mark init done so the default-seed effect
        // doesn't clobber it once sales load.
        saleInitRef.current = true;
        setSaleId((r as any).saleId ?? "__none__");
        applyGcToLocalState(gc);
        setPageId(r.pageId ?? "");
        // Hydrate the configured checkout URL (null => not configured, so the
        // field falls back to the resolved default).
        setCheckoutUrlInput((r as any).checkoutUrl ?? null);
        setSelectedRunId(r.id);
        setInputsExpanded(false);
        // Resume polling if this is a quiz run whose background generation is
        // still in flight (e.g. the user reloaded while it was generating).
        const gcAny = gc as any;
        if (
          gcAny.templateType === "quiz" &&
          !(gcAny.quiz && isLpQuizContentUsable(gcAny.quiz)) &&
          r.status !== "failed" &&
          r.status !== "completed"
        ) {
          setQuizGenerating(true);
        } else {
          setQuizGenerating(false);
        }
        // Same resume logic for a listicle run still generating in the background.
        if (
          gcAny.templateType === "listicle" &&
          !(gcAny.listicle && isLpListicleContentUsable(gcAny.listicle)) &&
          r.status !== "failed" &&
          r.status !== "completed"
        ) {
          setListicleGenerating(true);
        } else {
          setListicleGenerating(false);
        }
        // Same resume logic for an advertorial run still generating in the background.
        if (
          gcAny.templateType === "advertorial" &&
          !(gcAny.advertorial && isLpAdvertorialContentUsable(gcAny.advertorial)) &&
          r.status !== "failed" &&
          r.status !== "completed"
        ) {
          setAdvertorialGenerating(true);
        } else {
          setAdvertorialGenerating(false);
        }
        // Product Hero copy can be usable before the page-local Studio design
        // fork is ready. Resume from the durable run/state rather than from
        // section presence alone, otherwise a reload hides a paid in-flight
        // design admission behind the completed-looking copy.
        const studioDesignPending = gcAny.studioBrandDesign?.status === "pending";
        const smbHomeRun = isSmbHomeAttempt(gcAny);
        if (smbHomeRun) {
          const home = resolveSmbHomeLifecycle(r);
          setProductHeroGenerating(home.status === "rendering");
          setHeroError(home.message);
          setSmbHomeRunReady(home.status === "ready");
        } else if (
          gcAny.templateType === "product-hero" &&
            r.status !== "failed" &&
            (r.status !== "completed" || studioDesignPending)
        ) {
          setProductHeroGenerating(true);
        } else {
          setProductHeroGenerating(false);
          // A failed product-hero run has no usable sections and no persisted
          // gc.heroError — surface the run's errorMessage so the error/retry
          // state survives reload instead of dropping back to the home view.
          if (
            gcAny.templateType === "product-hero" &&
            !hasUsableLpProductHeroSections(gcAny) &&
            r.status === "failed" &&
            !studioDesignPending
          ) {
              setHeroError(
                gcAny.studioBrandDesign?.status === "failed"
                  ? studioBrandDesignUserMessage(gcAny.studioBrandDesign)
                  : (r as any).errorMessage ||
                    "Product page generation failed. Please try again.",
              );
          }
        }
        // Same resume logic for a problem-solution run still generating in the
        // background.
        if (
          gcAny.templateType === "problem-solution" &&
          !hasUsableLpProblemSolutionSections(gcAny) &&
          r.status !== "failed" &&
          r.status !== "completed"
        ) {
          setProblemSolutionGenerating(true);
        } else {
          setProblemSolutionGenerating(false);
          // A failed problem-solution run has no usable sections and no
          // persisted gc.heroError — surface the run's errorMessage so the
          // error/retry state survives reload.
          if (
            gcAny.templateType === "problem-solution" &&
            !hasUsableLpProblemSolutionSections(gcAny) &&
            r.status === "failed"
          ) {
            setHeroError(
              (r as any).errorMessage ||
                "Sales page generation failed. Please try again.",
            );
          }
        }
        // Same resume logic for an offer-stack run still generating in the
        // background.
        if (
          gcAny.templateType === "offer-stack" &&
          !hasUsableLpOfferStackSections(gcAny) &&
          r.status !== "failed" &&
          r.status !== "completed"
        ) {
          setOfferStackGenerating(true);
        } else {
          setOfferStackGenerating(false);
          // A failed offer-stack run has no usable sections and no persisted
          // gc.heroError — surface the run's errorMessage so the error/retry
          // state survives reload.
          if (
            gcAny.templateType === "offer-stack" &&
            !hasUsableLpOfferStackSections(gcAny) &&
            r.status === "failed"
          ) {
            setHeroError(
              (r as any).errorMessage ||
                "Offer page generation failed. Please try again.",
            );
          }
        }
        // PapaBear first-time generation runs its claim-safe section workflow
        // fan-out in the background. Resume polling after a reload until the
        // final normalized blob is persisted.
        const papabearPollState = resolveLpPapabearLabelPollState(r);
        if (papabearPollState.belongsToTemplate) {
          setPapabearLabelGenerating(papabearPollState.generating);
          if (papabearPollState.error) {
            setHeroError(papabearPollState.error);
          }
        }
      } catch (e: any) {
        if (!cancelled) {
          toast({
            title: "Couldn't open this page",
            description: e?.message || "A network error occurred while loading the page.",
            variant: "destructive",
          });
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, urlRunId]);

  // Some deep-links point at a page by ?pageId= rather than ?runId= (e.g. the
  // "Hero images ready" notification). The editor hydrates from a run, so
  // resolve the page's owning run and swap the URL to ?runId=, which the
  // load-by-url effect above then hydrates. Without this, clicking such a link
  // leaves the editor in its default empty state ("page not loading").
  const urlPageId = useMemo(
    () => new URLSearchParams(search).get("pageId") || "",
    [search],
  );
  useEffect(() => {
    if (!brandId || urlRunId || !urlPageId) return;
    const match = previousRuns.find((r) => r.pageId === urlPageId);
    if (!match) return;
    navigationEpochRef.current += 1;
    navigate(`${surfacePath}?runId=${match.id}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, urlRunId, urlPageId, previousRuns]);

  const generateMutation = useMutation({
    mutationFn: async () => {
      if (studioDesign?.rendererTemplateKey === "smb-home" || templateType === "smb-home") {
        throw new Error("Use the dedicated SMB Home page controls to create another page.");
      }
      const payload = {
        personaId,
        productId,
        narrativeAngleId: customAngleMode ? undefined : angleId,
        customAngle: customAngleForRequest,
        saleId: saleIdForRequest,
        templateTypeKey,
      };
      const res = await apiRequest("POST", `/api/brands/${brandId}/lp-express/generate-hero`, payload);
      return parseJsonResponse(res);
    },
    // Generate can admit paid brand-design work before a later fidelity
    // validation failure reaches the client. Never replay the whole mutation
    // automatically; the user must explicitly choose to generate again.
    retry: false,
    onMutate: () => {
      // Entering the loading state — clear any prior hero failure so the
      // renderer shows the in-flight loading skeleton, not a stale error.
      setHeroError(null);
      // Immediately switch from the home/inputs view to the editor surface so
      // the user sees a loading state right away. Essential for the quiz funnel,
      // which never produces a `hero` to trigger the switch on its own.
      setInputsExpanded(false);
    },
    onSuccess: (data: { label?: string; headline?: string; subheadline?: string; cta_text?: string; pageId?: string; runId?: string; templateType?: string; pending?: boolean; quiz?: LpQuizContent; listicle?: LpListicleContent; advertorial?: LpAdvertorialContent; coldDrip?: any; coldDripImages?: any; papabearLabel?: LpPapabearLabelContent; studioDesign?: LpStudioDesignSnapshot }) => {
      // Background generation responses use pending and include their durable
      // run/page ids. Keep the no-id guard defensive for older route responses,
      // but never turn it into a local preflight or a second provider request.
      if (data?.pending && !data?.pageId && !data?.runId) return;
      // Clear a previously loaded Studio design when generating any hand-coded
      // template; retain the admission-time snapshot immediately for LP2.
      setStudioDesign(
        isLpStudioDesignSnapshot(data?.studioDesign) ? data.studioDesign : null,
      );
      setStudioPageDesignStatus(null);
      // ── Quiz Funnel path ──────────────────────────────────────────────────
      // The quiz template generates its whole content in one shot; there is no
      // hero or section backfill chain. Set the quiz blob + templateType, keep
      // hero null (so the section backfill effect stays dormant), and skip the
      // hero-image kickoff entirely.
      if (data?.templateType === "quiz") {
        setTemplateType("quiz");
        setHero(null);
        setHeroError(null);
        setPageId(data?.pageId ?? "");
        setSelectedRunId(data?.runId ?? "");
        setCheckoutUrlInput(null);
        setInputsExpanded(false);
        queryClient.invalidateQueries({
          queryKey: [`/api/brands/${brandId}/lp-express/runs`],
        });
        // The route now returns immediately (pending) and generates the quiz in
        // the background; enter the polling/loading state and let the poll below
        // hydrate the quiz once it lands. (A `quiz` in the response is only
        // possible from a legacy synchronous path — apply it directly.)
        if (data.quiz && isLpQuizContentUsable(data.quiz)) {
          setQuiz(data.quiz);
          setQuizGenerating(false);
          toast({ title: "Quiz ready" });
        } else {
          setQuiz(null);
          setQuizGenerating(true);
          toast({
            title: "Generating your quiz…",
            description: "This usually takes a minute or two.",
          });
        }
        return;
      }
      // ── Listicle path ─────────────────────────────────────────────────────
      // Same blob model as the quiz: the whole copy generates in one shot in
      // the background (its AI images render in parallel and are polled
      // separately), so there is no hero/section backfill chain either.
      if (data?.templateType === "listicle") {
        setTemplateType("listicle");
        setHero(null);
        setHeroError(null);
        setQuiz(null);
        setQuizGenerating(false);
        setListicleImages(null);
        setListicleImagesPending(false);
        setPageId(data?.pageId ?? "");
        setSelectedRunId(data?.runId ?? "");
        setCheckoutUrlInput(null);
        setInputsExpanded(false);
        queryClient.invalidateQueries({
          queryKey: [`/api/brands/${brandId}/lp-express/runs`],
        });
        // The route returns immediately (pending) and generates everything in
        // the background; enter the polling/loading state and let the poll
        // below hydrate the listicle once it lands.
        if (data.listicle && isLpListicleContentUsable(data.listicle)) {
          setListicle(data.listicle);
          setListicleGenerating(false);
          toast({ title: "Listicle ready" });
        } else {
          setListicle(null);
          setListicleGenerating(true);
          toast({
            title: "Generating your listicle…",
            description: "This usually takes a minute or two.",
          });
        }
        return;
      }
      // ── Advertorial path ──────────────────────────────────────────────────
      // Same blob model as the quiz/listicle: the whole copy generates in one
      // shot in the background (its AI images render in parallel and are
      // polled separately), so there is no hero/section backfill chain either.
      if (data?.templateType === "advertorial") {
        setTemplateType("advertorial");
        setHero(null);
        setHeroError(null);
        setQuiz(null);
        setQuizGenerating(false);
        setListicle(null);
        setListicleGenerating(false);
        setAdvertorialImages(null);
        setAdvertorialImagesPending(false);
        setPageId(data?.pageId ?? "");
        setSelectedRunId(data?.runId ?? "");
        setCheckoutUrlInput(null);
        setInputsExpanded(false);
        queryClient.invalidateQueries({
          queryKey: [`/api/brands/${brandId}/lp-express/runs`],
        });
        // The route returns immediately (pending) and generates everything in
        // the background; enter the polling/loading state and let the poll
        // below hydrate the advertorial once it lands.
        if (data.advertorial && isLpAdvertorialContentUsable(data.advertorial)) {
          setAdvertorial(data.advertorial);
          setAdvertorialGenerating(false);
          toast({ title: "Advertorial ready" });
        } else {
          setAdvertorial(null);
          setAdvertorialGenerating(true);
          toast({
            title: "Generating your advertorial…",
            description: "This usually takes a minute or two.",
          });
        }
        return;
      }
      // ── Product Hero (PDP) path ─────────────────────────────────────────────
      // Same blob model as the advertorial, but with NO AI image pool — the
      // gallery uses the brand's REAL product photos, so there is nothing extra
      // to poll. The route returns immediately (pending) and generates the copy
      // in the background; enter the polling/loading state and let the poll
      // below hydrate the productHero once it lands.
      if (data?.templateType === "product-hero") {
        setTemplateType("product-hero");
        setHero(null);
        setHeroError(null);
        setQuiz(null);
        setQuizGenerating(false);
        setListicle(null);
        setListicleGenerating(false);
        setAdvertorial(null);
        setAdvertorialGenerating(false);
        setAdvertorialImages(null);
        setAdvertorialImagesPending(false);
        setPageId(data?.pageId ?? "");
        setSelectedRunId(data?.runId ?? "");
        setCheckoutUrlInput(null);
        setInputsExpanded(false);
        queryClient.invalidateQueries({
          queryKey: [`/api/brands/${brandId}/lp-express/runs`],
        });
        // The server generates the product-hero SECTIONS in the background (one
        // job per phBuyBox/phTrustBar/… key), so the create call returns before
        // any copy exists. Enter the generating state + scope the layout to the
        // product-hero blocks; the status poll hydrates productHeroSections.
        setProductHeroSections({});
        setProductHeroImages(null);
        setProductHeroImagesPending(false);
        setProductHeroGenerating(true);
        setSectionLayout(canonicalSectionLayout(LP_PRODUCT_HERO_SECTION_TYPES));
        toast({
          title: "Generating your product page…",
          description: "This usually takes a minute or two.",
        });
        return;
      }
      // ── Problem / Solution (PAS) path ──────────────────────────────────────
      // Mirror of the product-hero path: per-section copy generated in the
      // background (psHero, psProblem, …) plus one AI mechanism-diagram slot in
      // problemSolutionImages; the status poll hydrates the sections.
      if (data?.templateType === "problem-solution") {
        setTemplateType("problem-solution");
        setHero(null);
        setHeroError(null);
        setQuiz(null);
        setQuizGenerating(false);
        setListicle(null);
        setListicleGenerating(false);
        setAdvertorial(null);
        setAdvertorialGenerating(false);
        setAdvertorialImages(null);
        setAdvertorialImagesPending(false);
        setProductHeroSections({});
        setProductHeroGenerating(false);
        setProductHeroImages(null);
        setProductHeroImagesPending(false);
        setPageId(data?.pageId ?? "");
        setSelectedRunId(data?.runId ?? "");
        setCheckoutUrlInput(null);
        setInputsExpanded(false);
        queryClient.invalidateQueries({
          queryKey: [`/api/brands/${brandId}/lp-express/runs`],
        });
        setProblemSolutionSections({});
        setProblemSolutionImages(null);
        setProblemSolutionImagesPending(false);
        setProblemSolutionGenerating(true);
        setSectionLayout(canonicalSectionLayout(LP_PROBLEM_SOLUTION_SECTION_TYPES));
        toast({
          title: "Generating your sales page…",
          description: "This usually takes a minute or two.",
        });
        return;
      }
      // ── Cold Drip Offer (Gaffle) path ─────────────────────────────────────
      // Two server modes: with SIM_LP_COLD_DRIP_URL set the route returns
      // `pending: true` and generates the copy blob in the background (we poll
      // the run like the advertorial); without it the route persists the
      // imported default blob synchronously and hands it back directly.
      if (data?.templateType === "cold-drip") {
        setTemplateType("cold-drip");
        setHero(null);
        setHeroError(null);
        setQuiz(null);
        setQuizGenerating(false);
        setListicle(null);
        setListicleGenerating(false);
        setAdvertorial(null);
        setAdvertorialGenerating(false);
        setAdvertorialImages(null);
        setAdvertorialImagesPending(false);
        setProductHeroSections({});
        setProductHeroGenerating(false);
        setProductHeroImages(null);
        setProductHeroImagesPending(false);
        setPageId(data?.pageId ?? "");
        setSelectedRunId(data?.runId ?? "");
        setCheckoutUrlInput(null);
        setInputsExpanded(false);
        queryClient.invalidateQueries({
          queryKey: [`/api/brands/${brandId}/lp-express/runs`],
        });
        if (data?.pending) {
          setColdDrip(null);
          setColdDripImages(null);
          setColdDripImagesPending(false);
          setColdDripGenerating(true);
          toast({
            title: "Writing your offer page…",
            description: "This usually takes a minute or two.",
          });
        } else {
          setColdDrip(data.coldDrip ?? null);
          // Synchronous path: the route seeds the AI image slots pending and
          // fills them in the background — start the slot poll right away.
          const cdImgs = Array.isArray(data.coldDripImages) ? data.coldDripImages : null;
          setColdDripImages(cdImgs);
          setColdDripImagesPending(
            !!cdImgs?.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl),
          );
          setColdDripGenerating(false);
          toast({ title: "Offer page ready" });
        }
        return;
      }

      // PapaBear uses the imported inventory as the starting context, then runs
      // each supported section through the existing LP Express workflows in the
      // background. Commerce and review facts remain live renderer inputs.
      if (data?.templateType === "papabear-label") {
        setTemplateType("papabear-label");
        setHero(null);
        setHeroError(null);
        setQuiz(null);
        setQuizGenerating(false);
        setListicle(null);
        setListicleGenerating(false);
        setAdvertorial(null);
        setAdvertorialGenerating(false);
        setColdDrip(null);
        setColdDripImages(null);
        setColdDripImagesPending(false);
        setColdDripGenerating(false);
        setProductHeroSections({});
        setProductHeroGenerating(false);
        setProductHeroImages(null);
        setProductHeroImagesPending(false);
        setPageId(data?.pageId ?? "");
        setSelectedRunId(data?.runId ?? "");
        setCheckoutUrlInput(null);
        setInputsExpanded(false);
        queryClient.invalidateQueries({
          queryKey: [`/api/brands/${brandId}/lp-express/runs`],
        });
        if (data.pending) {
          setPapabearLabel(null);
          setPapabearLabelGenerating(true);
          toast({
            title: "Writing your Papabear page…",
            description: "This usually takes a minute or two.",
          });
        } else {
          // Backward-compatible handling for any already-running server that
          // still returns the completed blob synchronously.
          setPapabearLabel(data.papabearLabel ?? null);
          setPapabearLabelGenerating(false);
          toast({ title: "Papabear page ready" });
        }
        return;
      }

      // ── Coffee Shop Lead Capture (Gaffle) path ────────────────────────────
      // Same two server modes as the cold-drip above: pending → background
      // copy blob (polled via coffeeShopStatusQuery), otherwise the imported
      // default blob is persisted synchronously and handed back here.
      if (data?.templateType === "coffee-shop") {
        setTemplateType("coffee-shop");
        setHero(null);
        setHeroError(null);
        setQuiz(null);
        setQuizGenerating(false);
        setListicle(null);
        setListicleGenerating(false);
        setAdvertorial(null);
        setAdvertorialGenerating(false);
        setAdvertorialImages(null);
        setAdvertorialImagesPending(false);
        setColdDrip(null);
        setColdDripImages(null);
        setColdDripImagesPending(false);
        setColdDripGenerating(false);
        setProductHeroSections({});
        setProductHeroGenerating(false);
        setProductHeroImages(null);
        setProductHeroImagesPending(false);
        setPageId(data?.pageId ?? "");
        setSelectedRunId(data?.runId ?? "");
        setCheckoutUrlInput(null);
        setInputsExpanded(false);
        queryClient.invalidateQueries({
          queryKey: [`/api/brands/${brandId}/lp-express/runs`],
        });
        if (data?.pending) {
          setCoffeeShop(null);
          setCoffeeShopImages(null);
          setCoffeeShopImagesPending(false);
          setCoffeeShopGenerating(true);
          toast({
            title: "Writing your lead-capture page…",
            description: "This usually takes a minute or two.",
          });
        } else {
          setCoffeeShop((data as any).coffeeShop ?? null);
          // Synchronous path: the route seeds the AI image slots pending and
          // fills them in the background — start the slot poll right away.
          const csImgs = Array.isArray((data as any).coffeeShopImages) ? (data as any).coffeeShopImages : null;
          setCoffeeShopImages(csImgs);
          setCoffeeShopImagesPending(
            !!csImgs?.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl),
          );
          setCoffeeShopGenerating(false);
          toast({ title: "Lead-capture page ready" });
        }
        return;
      }

      // ── Offer Stack (value-stack close) path ──────────────────────────────
      // Mirror of the problem-solution path: per-section copy generated in the
      // background (osHero, osStack, …); NO AI image slots. The status poll
      // hydrates the sections.
      if (data?.templateType === "offer-stack") {
        setTemplateType("offer-stack");
        setHero(null);
        setHeroError(null);
        setQuiz(null);
        setQuizGenerating(false);
        setListicle(null);
        setListicleGenerating(false);
        setAdvertorial(null);
        setAdvertorialGenerating(false);
        setAdvertorialImages(null);
        setAdvertorialImagesPending(false);
        setProductHeroSections({});
        setProductHeroGenerating(false);
        setProductHeroImages(null);
        setProductHeroImagesPending(false);
        setPageId(data?.pageId ?? "");
        setSelectedRunId(data?.runId ?? "");
        setCheckoutUrlInput(null);
        setInputsExpanded(false);
        queryClient.invalidateQueries({
          queryKey: [`/api/brands/${brandId}/lp-express/runs`],
        });
        setOfferStackSections({});
        setOfferStackGenerating(true);
        setOfferStackImages(null);
        setOfferStackImagesPending(false);
        setSectionLayout(canonicalSectionLayout(LP_OFFER_STACK_SECTION_TYPES));
        toast({
          title: "Generating your offer page…",
          description: "This usually takes a minute or two.",
        });
        return;
      }
      // ── Temecula Passport (Mystery Drop, Gaffle) path ─────────────────────
      // Same two server modes as the coffee-shop above: pending → background
      // copy blob (polled via temeculaPassportStatusQuery), otherwise the
      // imported default blob is persisted synchronously and handed back here.
      if (data?.templateType === "temecula-passport") {
        setTemplateType("temecula-passport");
        setHero(null);
        setHeroError(null);
        setQuiz(null);
        setQuizGenerating(false);
        setListicle(null);
        setListicleGenerating(false);
        setAdvertorial(null);
        setAdvertorialGenerating(false);
        setAdvertorialImages(null);
        setAdvertorialImagesPending(false);
        setColdDrip(null);
        setColdDripImages(null);
        setColdDripImagesPending(false);
        setColdDripGenerating(false);
        setCoffeeShop(null);
        setCoffeeShopImages(null);
        setCoffeeShopImagesPending(false);
        setCoffeeShopGenerating(false);
        setProductHeroSections({});
        setProductHeroGenerating(false);
        setProductHeroImages(null);
        setProductHeroImagesPending(false);
        setProblemSolutionSections({});
        setProblemSolutionGenerating(false);
        setProblemSolutionImages(null);
        setProblemSolutionImagesPending(false);
        setPageId(data?.pageId ?? "");
        setSelectedRunId(data?.runId ?? "");
        setCheckoutUrlInput(null);
        setInputsExpanded(false);
        queryClient.invalidateQueries({
          queryKey: [`/api/brands/${brandId}/lp-express/runs`],
        });
        if (data?.pending) {
          setTemeculaPassport(null);
          setTemeculaPassportImages(null);
          setTemeculaPassportImagesPending(false);
          setTemeculaPassportGenerating(true);
          toast({
            title: "Writing your mystery-drop page…",
            description: "This usually takes a minute or two.",
          });
        } else {
          setTemeculaPassport((data as any).temeculaPassport ?? null);
          // Synchronous path: the route seeds the AI image slots pending and
          // fills them in the background — start the slot poll right away.
          const tpImgs = Array.isArray((data as any).temeculaPassportImages) ? (data as any).temeculaPassportImages : null;
          setTemeculaPassportImages(tpImgs);
          setTemeculaPassportImagesPending(
            !!tpImgs?.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl),
          );
          setTemeculaPassportGenerating(false);
          toast({ title: "Mystery-drop page ready" });
        }
        return;
      }
      setLeadin("");
      setAdvisory(null);
      setShowcase(null);
      setProductContext(null);
      setProductContextImagePending(false);
      setStations(null);
      setVillain(null);
      setOriginCopy(null);
      setOriginImages(null);
      setOriginImagesPending(false);
      setQuotes(null);
      setTimeline(null);
      setMembership(null);
      setAlternatives(null);
      setPhilosophy(null);
      setDifferentiator(null);
      setFaq(null);
      setReviews(null);
      setFinalCta(null);
      setFooter(null);
      setProof(null);
      setAlternativesImages(null);
      setAlternativesImagesPending(false);
      setPhilosophyImages(null);
      setPhilosophyImagesPending(false);
      setProofImages(null);
      setProofImagesPending(false);
      setSectionLayout(canonicalSectionLayout(LP_LONG_FORM_SECTION_TYPES));
      setVideoOverrides({});
      setElementStates({});
      setTheme({});
      setElementStyles({});
      setComponentOrder({});
      setReviewOverrides({});
      setPageId(data?.pageId ?? "");
      // Fresh generate: no configured checkout URL yet (falls back to default).
      setCheckoutUrlInput(null);
      setSelectedRunId(data?.runId ?? "");
      setHero({
        label: data?.label ?? "",
        headline: data?.headline ?? "",
        subheadline: data?.subheadline ?? "",
        cta_text: data?.cta_text ?? "",
      });
      // Workflow returned but produced no usable copy — flag it so the renderer
      // shows the error/retry state rather than the hardcoded placeholders.
      const heroEmpty = !data?.headline && !data?.subheadline && !data?.cta_text;
      setHeroError(
        heroEmpty
          ? "The hero workflow returned no copy. Please try generating again."
          : null,
      );
      setInputsExpanded(false);
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/runs`],
      });
      // Kick off the hero-image worker (temp-capped to 1 image while we
      // verify the Sim → Fal → R2 pipeline end-to-end). Fire-and-forget;
      // the heroImagesQuery below polls the page row for status/URLs.
      const newPageId = data?.pageId;
      if (newPageId) {
        // Optimistically seed the polling query with status="pending"
        // BEFORE the kickoff POST resolves. Otherwise the initial
        // auto-fetch (triggered by setPageId above) races the POST and
        // comes back status=null — refetchInterval then returns false
        // and the carousel never enters its loading state.
        queryClient.setQueryData(["/api/landing-pages", newPageId], {
          id: newPageId,
          heroImageStatus: "pending",
          heroImageError: null,
          heroImageUrls: null,
        });
        apiRequest("POST", `/api/landing-pages/${newPageId}/generate-hero-images`)
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ["/api/landing-pages", newPageId] });
          })
          .catch((err) => {
            console.warn("[lp-express] hero image kickoff failed:", err);
          });
      }
      toast({ title: "Hero ready" });
    },
    onError: (err: Error) => {
      // Hard failure (Sim/network/500). Surface it in-canvas so the user can
      // retry in place, not just via a transient toast.
      setHeroError(err?.message || "Hero generation failed. Please try again.");
      toast({
        title: "Failed to generate hero",
        description: err?.message || "Try again.",
        variant: "destructive",
      });
    },
  });

  const handleCreateProject = async () => {
    const name = newProjectName.trim();
    if (!name || !brandId) return;
    setCreatingProject(true);
    try {
      const res = await apiRequest("POST", `/api/brands/${brandId}/projects`, { name });
      const created = (await res.json()) as { id: string };
      await queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/projects`] });
      setProjectId(created.id);
      setNewProjectOpen(false);
      setNewProjectName("");
    } catch (err) {
      toast({
        title: "Couldn't create project",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setCreatingProject(false);
    }
  };

  const isRunning = generateMutation.isPending || quizGenerating || listicleGenerating || advertorialGenerating || productHeroGenerating || problemSolutionGenerating || coldDripGenerating || coffeeShopGenerating || offerStackGenerating || temeculaPassportGenerating || papabearLabelGenerating;
  // Hero copy generation surfaces three view modes around the live preview:
  //  • home view (inputs + pages list) when nothing is in flight,
  //  • a hero-only loading/error surface while the initial hero workflow runs
  //    or has failed/returned empty (so we don't render a half-built page of
  //    placeholders beneath a spinning hero),
  //  • the full preview once the hero copy is live.
  const heroCopyLive = !!hero?.headline;
  const heroOnlyLayout = !heroCopyLive && (isRunning || !!heroError);
  // A generated/loaded quiz counts as "live" content (no hero) — the preview
  // surface must show it, and the home/inputs view must step aside.
  const quizLive = !!quiz && templateType === "quiz";
  // Same for a generated/loaded listicle — one copy blob, no hero.
  const listicleLive = !!listicle && templateType === "listicle";
  // Same for a generated/loaded advertorial — one copy blob, no hero.
  const advertorialLive = !!advertorial && templateType === "advertorial";
  // Same for a generated/loaded product-hero (PDP) — per-section copy, no hero.
  const productHeroLive =
    (studioDesign?.rendererTemplateKey === "smb-home") ||
    (templateType === "product-hero" && hasUsableLpProductHeroSections(productHeroSections));
  // Same for a generated/loaded problem-solution page — per-section copy, no hero.
  const problemSolutionLive =
    templateType === "problem-solution" &&
    hasUsableLpProblemSolutionSections(problemSolutionSections);
  // Same for a generated/loaded Cold Drip offer page — one copy blob, no hero.
  const coldDripLive = !!coldDrip && templateType === "cold-drip";
  const papabearLabelLive =
    !!papabearLabel && templateType === "papabear-label";
  // Same for a generated/loaded Coffee Shop lead-capture page — one copy blob, no hero.
  const coffeeShopLive = !!coffeeShop && templateType === "coffee-shop";
  // Same for a generated/loaded offer-stack page — per-section copy, no hero.
  const offerStackLive =
    templateType === "offer-stack" &&
    hasUsableLpOfferStackSections(offerStackSections);
  // Same for a generated/loaded Temecula Passport mystery-drop page — one copy blob, no hero.
  const temeculaPassportLive = !!temeculaPassport && templateType === "temecula-passport";
  // The editor/preview surface takes over the whole screen the moment
  // generation starts (or a page is loaded): a live hero, a live quiz, an
  // in-flight generation, or a hero error. When true we collapse the inputs and
  // show the page canvas (with a loading state while generating) instead of the
  // home view. A quiz never produces a `hero`, so keying this switch off `hero`
  // alone previously left the inputs form up during quiz generation.
  const showPreviewSurface = !!hero || quizLive || listicleLive || advertorialLive || productHeroLive || problemSolutionLive || coldDripLive || coffeeShopLive || offerStackLive || temeculaPassportLive || papabearLabelLive || isRunning || !!heroError;
  const showHomeView = !showPreviewSurface;

  // ── Quiz background-generation polling ──────────────────────────────────
  // The quiz route returns immediately and generates in the background, so we
  // poll the run row every 4s while `quizGenerating` is set. The query stops
  // once the run reports the quiz is ready (usable blob) or failed; the effects
  // below hydrate the quiz / surface the error.
  const quizStatusQuery = useQuery<RunRow>({
    queryKey: ["/api/lp-express/runs", selectedRunId, "quiz-status", surface],
    queryFn: async () => {
      const res = await fetch(`/api/lp-express/runs/${selectedRunId}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Failed to load run (${res.status})`);
      return (await res.json()) as RunRow;
    },
    enabled: quizGenerating && !!selectedRunId,
    refetchOnWindowFocus: false,
    refetchInterval: (q) => {
      if (!quizGenerating) return false;
      const d = q.state.data as RunRow | undefined;
      if (!d) return 4000;
      if (d.status === "failed") return false;
      // The quiz is only "done" once the RUN reaches a terminal success state.
      // During a REGENERATION the previous quiz stays in generatedContent, so
      // keying purely on quiz presence would stop polling immediately with the
      // stale quiz — gate on the run status too.
      const gc = (d.generatedContent ?? {}) as any;
      if (d.status === "completed" && gc?.quiz && isLpQuizContentUsable(gc.quiz))
        return false;
      return 4000;
    },
  });

  // React to the polled run row: hydrate the quiz when it lands, or surface a
  // generation failure.
  useEffect(() => {
    if (!quizGenerating) return;
    const r = quizStatusQuery.data;
    if (!r) return;
    const gc = (r.generatedContent ?? {}) as any;
    // Only accept the quiz once the run reaches a terminal success — during a
    // regeneration the previous quiz is still present, so quiz-presence alone
    // would hydrate the stale copy (see the mutation/poll comments above).
    if (
      r.status === "completed" &&
      gc.templateType === "quiz" &&
      gc.quiz &&
      isLpQuizContentUsable(gc.quiz)
    ) {
      const wasRegen = quizRegenerating;
      setQuiz(gc.quiz as LpQuizContent);
      setPageId(r.pageId ?? "");
      setCheckoutUrlInput((r as any).checkoutUrl ?? null);
      setQuizGenerating(false);
      setQuizRegenerating(false);
      toast({ title: wasRegen ? "Quiz updated" : "Quiz ready" });
    } else if (r.status === "failed") {
      const wasRegen = quizRegenerating;
      setQuizGenerating(false);
      setQuizRegenerating(false);
      // A regeneration failure keeps the existing quiz (no error UI); a first-
      // time generation failure has no quiz to fall back to, so surface the
      // in-canvas error/retry state.
      if (!wasRegen) {
        setHeroError(r.errorMessage || "Quiz generation failed. Please try again.");
      }
      toast({
        title: wasRegen ? "Couldn't regenerate the quiz" : "Quiz generation failed",
        description: r.errorMessage || "Please try again.",
        variant: "destructive",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizStatusQuery.data, quizGenerating, quizRegenerating]);

  // Hard stop: if generation hasn't landed within ~6 minutes (it normally takes
  // 1–3), give up so the preview doesn't spin forever (e.g. a run stranded by a
  // server restart mid-generation).
  useEffect(() => {
    if (!quizGenerating) return;
    const id = setTimeout(() => {
      const wasRegen = quizRegenerating;
      setQuizGenerating(false);
      setQuizRegenerating(false);
      // Keep the existing quiz on a regeneration timeout; only a first-time
      // generation (no prior quiz) drops into the in-canvas error state.
      if (!wasRegen) {
        setHeroError(
          "The quiz is taking longer than expected. Please try generating again.",
        );
      }
      toast({
        title: "Quiz generation timed out",
        description: "Please try again.",
        variant: "destructive",
      });
    }, 6 * 60_000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizGenerating]);

  // ── Listicle background-generation polling ──────────────────────────────
  // Mirrors the quiz poll above: the listicle route returns immediately and
  // generates the copy blob (and its images) in the background, so we poll the
  // run row every 4s while `listicleGenerating` is set. "Done" is gated on the
  // RUN status — during a regeneration the previous listicle stays in
  // generatedContent, so blob presence alone would resolve with stale copy.
  const listicleStatusQuery = useQuery<RunRow>({
    queryKey: ["/api/lp-express/runs", selectedRunId, "listicle-status", surface],
    queryFn: async () => {
      const res = await fetch(`/api/lp-express/runs/${selectedRunId}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Failed to load run (${res.status})`);
      return (await res.json()) as RunRow;
    },
    enabled: listicleGenerating && !!selectedRunId,
    refetchOnWindowFocus: false,
    refetchInterval: (q) => {
      if (!listicleGenerating) return false;
      const d = q.state.data as RunRow | undefined;
      if (!d) return 4000;
      if (d.status === "failed") return false;
      const gc = (d.generatedContent ?? {}) as any;
      if (
        d.status === "completed" &&
        gc?.listicle &&
        isLpListicleContentUsable(gc.listicle)
      )
        return false;
      return 4000;
    },
  });

  // React to the polled run row: hydrate the listicle when it lands (plus its
  // image slots, whose background render keeps going after the copy is ready),
  // or surface a generation failure.
  useEffect(() => {
    if (!listicleGenerating) return;
    const r = listicleStatusQuery.data;
    if (!r) return;
    const gc = (r.generatedContent ?? {}) as any;
    if (
      r.status === "completed" &&
      gc.templateType === "listicle" &&
      gc.listicle &&
      isLpListicleContentUsable(gc.listicle)
    ) {
      const wasRegen = listicleRegenerating;
      setListicle(gc.listicle as LpListicleContent);
      // Hydrate whatever image slots exist so far and keep polling any that
      // are still rendering in the background.
      const liImgs = Array.isArray(gc.listicleImages) ? gc.listicleImages : null;
      if (liImgs) {
        setListicleImages(liImgs);
        setListicleImagesPending(
          liImgs.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl),
        );
      }
      setPageId(r.pageId ?? "");
      setCheckoutUrlInput((r as any).checkoutUrl ?? null);
      setListicleGenerating(false);
      setListicleRegenerating(false);
      toast({ title: wasRegen ? "Listicle updated" : "Listicle ready" });
    } else if (r.status === "failed") {
      const wasRegen = listicleRegenerating;
      setListicleGenerating(false);
      setListicleRegenerating(false);
      // A regeneration failure keeps the existing listicle (no error UI); a
      // first-time generation failure has nothing to fall back to, so surface
      // the in-canvas error/retry state.
      if (!wasRegen) {
        setHeroError(r.errorMessage || "Listicle generation failed. Please try again.");
      }
      toast({
        title: wasRegen ? "Couldn't regenerate the listicle" : "Listicle generation failed",
        description: r.errorMessage || "Please try again.",
        variant: "destructive",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listicleStatusQuery.data, listicleGenerating, listicleRegenerating]);

  // Hard stop for a stranded listicle generation (same as the quiz timeout).
  useEffect(() => {
    if (!listicleGenerating) return;
    const id = setTimeout(() => {
      const wasRegen = listicleRegenerating;
      setListicleGenerating(false);
      setListicleRegenerating(false);
      if (!wasRegen) {
        setHeroError(
          "The listicle is taking longer than expected. Please try generating again.",
        );
      }
      toast({
        title: "Listicle generation timed out",
        description: "Please try again.",
        variant: "destructive",
      });
    }, 6 * 60_000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listicleGenerating]);

  // ── Advertorial background-generation polling ───────────────────────────
  // Mirrors the quiz/listicle polls above: the advertorial route returns
  // immediately and generates the copy blob (and its images) in the
  // background, so we poll the run row every 4s while `advertorialGenerating`
  // is set. "Done" is gated on the RUN status — during a regeneration the
  // previous advertorial stays in generatedContent, so blob presence alone
  // would resolve with stale copy.
  const advertorialStatusQuery = useQuery<RunRow>({
    queryKey: ["/api/lp-express/runs", selectedRunId, "advertorial-status", surface],
    queryFn: async () => {
      const res = await fetch(`/api/lp-express/runs/${selectedRunId}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Failed to load run (${res.status})`);
      return (await res.json()) as RunRow;
    },
    enabled: advertorialGenerating && !!selectedRunId,
    refetchOnWindowFocus: false,
    refetchInterval: (q) => {
      if (!advertorialGenerating) return false;
      const d = q.state.data as RunRow | undefined;
      if (!d) return 4000;
      if (d.status === "failed") return false;
      const gc = (d.generatedContent ?? {}) as any;
      if (
        d.status === "completed" &&
        gc?.advertorial &&
        isLpAdvertorialContentUsable(gc.advertorial)
      )
        return false;
      return 4000;
    },
  });

  // React to the polled run row: hydrate the advertorial when it lands (plus
  // its image slots, whose background render keeps going after the copy is
  // ready), or surface a generation failure.
  useEffect(() => {
    if (!advertorialGenerating) return;
    const r = advertorialStatusQuery.data;
    if (!r) return;
    const gc = (r.generatedContent ?? {}) as any;
    if (
      r.status === "completed" &&
      gc.templateType === "advertorial" &&
      gc.advertorial &&
      isLpAdvertorialContentUsable(gc.advertorial)
    ) {
      const wasRegen = advertorialRegenerating;
      setAdvertorial(gc.advertorial as LpAdvertorialContent);
      // Hydrate whatever image slots exist so far and keep polling any that
      // are still rendering in the background.
      const advImgs = Array.isArray(gc.advertorialImages) ? gc.advertorialImages : null;
      if (advImgs) {
        setAdvertorialImages(advImgs);
        setAdvertorialImagesPending(
          advImgs.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl),
        );
      }
      setPageId(r.pageId ?? "");
      setCheckoutUrlInput((r as any).checkoutUrl ?? null);
      setAdvertorialGenerating(false);
      setAdvertorialRegenerating(false);
      toast({ title: wasRegen ? "Advertorial updated" : "Advertorial ready" });
    } else if (r.status === "failed") {
      const wasRegen = advertorialRegenerating;
      setAdvertorialGenerating(false);
      setAdvertorialRegenerating(false);
      // A regeneration failure keeps the existing advertorial (no error UI); a
      // first-time generation failure has nothing to fall back to, so surface
      // the in-canvas error/retry state.
      if (!wasRegen) {
        setHeroError(r.errorMessage || "Advertorial generation failed. Please try again.");
      }
      toast({
        title: wasRegen ? "Couldn't regenerate the advertorial" : "Advertorial generation failed",
        description: r.errorMessage || "Please try again.",
        variant: "destructive",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advertorialStatusQuery.data, advertorialGenerating, advertorialRegenerating]);

  // Hard stop for a stranded advertorial generation (same as the quiz timeout).
  useEffect(() => {
    if (!advertorialGenerating) return;
    const id = setTimeout(() => {
      const wasRegen = advertorialRegenerating;
      setAdvertorialGenerating(false);
      setAdvertorialRegenerating(false);
      if (!wasRegen) {
        setHeroError(
          "The advertorial is taking longer than expected. Please try generating again.",
        );
      }
      toast({
        title: "Advertorial generation timed out",
        description: "Please try again.",
        variant: "destructive",
      });
    }, 6 * 60_000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advertorialGenerating]);

  // ── Product Hero background-generation polling ──────────────────────────────
  // The product-hero route returns immediately and generates each section's
  // copy/assets and optional page-local Studio design in the background, so we
  // poll the run row every 4s while `productHeroGenerating` is set.
  // "Done" = the RUN is completed AND the per-section keys are usable. Per-section
  // REGENERATION runs through its own job poll (regenerateProductHeroSection), so
  // this poll only ever covers first-time generation.
  const productHeroStatusQuery = useQuery<RunRow>({
    queryKey: ["/api/lp-express/runs", brandId, selectedRunId, "product-hero-status", surface],
    queryFn: async () => {
      const res = await fetch(`/api/lp-express/runs/${selectedRunId}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Failed to load run (${res.status})`);
      return (await res.json()) as RunRow;
    },
    enabled: productHeroGenerating && !!selectedRunId && !studioDesignRecoveryRequestPending,
    refetchOnWindowFocus: false,
    refetchInterval: (q) => {
      if (!productHeroGenerating || studioDesignRecoveryRequestPending) return false;
      const d = q.state.data as RunRow | undefined;
      if (!d) return 4000;
      const gc = (d.generatedContent ?? {}) as any;
      if (isSmbHomeAttempt(gc)) {
        return resolveSmbHomeLifecycle(d).status === "rendering" ? 4000 : false;
      }
      const studioDesignPending = gc.studioBrandDesign?.status === "pending";
      const studioDesignAmbiguous = gc.studioBrandDesign?.submissionState === "ambiguous";
      if (studioDesignAmbiguous && (d.status === "failed" || d.status === "completed")) return false;
      if (d.status === "failed" && !studioDesignPending) return false;
      if (
        d.status === "completed" &&
        (gc.studioDesign?.rendererTemplateKey === "smb-home"
          ? isLpSmbHomeContentUsable(assembleLpSmbHomeContent(gc))
          : hasUsableLpProductHeroSections(gc)) &&
        !studioDesignPending
      )
        return false;
      return 4000;
    },
  });
  const studioRecoveryRunId = selectedRunId || urlRunId;
  const studioRecoveryStatusQueryKey = (runId: string) => [
    "/api/lp-express/runs",
    brandId,
    runId,
    "product-hero-status",
    surface,
  ] as const;
  const isCurrentStudioRecoveryOrigin = (
    variables: StudioDesignRecoveryVariables,
  ): boolean =>
    variables.navigationEpoch === navigationEpochRef.current &&
    variables.brandId === brandId &&
    variables.pageId === pageId &&
    variables.runId === (selectedRunId || urlRunId);
  const recoverStudioDesignMutation = useMutation<{
    status: "pending" | "ready" | "failed";
    html?: string;
  }, Error, StudioDesignRecoveryVariables>({
    mutationFn: async (variables) => {
      const response = await apiRequest(
        "POST",
        `/api/landing-pages/${variables.pageId}/lp-express/recover-studio-brand-design`,
        {},
      );
      return parseJsonResponse(response);
    },
    onMutate: async (variables) => {
      if (!isCurrentStudioRecoveryOrigin(variables)) return;
      // Keep the currently selected run/persona/product/angle mounted. Removing
      // the old failed query prevents its stale terminal row from winning the
      // first render after recovery starts.
      setStudioDesignRecoveryActive(true);
      setStudioDesignRecoveryRequestPending(true);
      setProductHeroGenerating(true);
      setHeroError(null);
      setStudioPageDesignStatus((current) =>
        current
          ? { ...current, status: "pending", submissionState: "submitted" }
          : current,
      );
      if (variables.runId) {
        await queryClient.cancelQueries({
          queryKey: studioRecoveryStatusQueryKey(variables.runId),
        });
        queryClient.removeQueries({
          queryKey: studioRecoveryStatusQueryKey(variables.runId),
        });
      }
    },
    onSuccess: (result, variables) => {
      if (!isCurrentStudioRecoveryOrigin(variables)) return;
      setStudioDesignRecoveryRequestPending(false);
      setProductHeroGenerating(true);
      if (variables.runId) setSelectedRunId(variables.runId);
      // The exact run query is the source of truth for the immutable page
      // snapshot, sections, and terminal status. Enabling the existing poll
      // below refetches this run without navigating or issuing Generate.
      queryClient.invalidateQueries({
        queryKey: studioRecoveryStatusQueryKey(variables.runId),
      });
      if (result.status === "failed") {
        setStudioDesignRecoveryActive(false);
        setProductHeroGenerating(false);
        setStudioPageDesignStatus((current) =>
          current ? { ...current, status: "failed" } : current,
        );
        setHeroError("Your page design could not be recovered.");
      }
    },
    onError: (_error, variables) => {
      if (!isCurrentStudioRecoveryOrigin(variables)) return;
      setStudioDesignRecoveryRequestPending(false);
      setStudioDesignRecoveryActive(false);
      setProductHeroGenerating(false);
      setStudioPageDesignStatus((current) =>
        current ? { ...current, status: "failed", submissionState: "failed" } : current,
      );
      setHeroError("Your page design could not be recovered.");
      // Do not surface provider response bodies, editor IDs, or provider
      // diagnostics in the product editor.
      toast({
        title: "Couldn't recover the existing design",
        description: "No new generation was started. You can keep this page open and try again.",
        variant: "destructive",
      });
    },
  });
  const beginStudioDesignRecovery = () => {
    const remote = studioPageDesignStatus;
    if (
      !brandId ||
      !pageId ||
      !studioRecoveryRunId ||
      remote?.status !== "failed" ||
      !remote.mpEditorId?.trim()
    ) {
      return;
    }
    recoverStudioDesignMutation.mutate({
      pageId,
      runId: studioRecoveryRunId,
      brandId,
      navigationEpoch: navigationEpochRef.current,
      remote: {
        attemptId: remote.attemptId ?? null,
        editorId: remote.mpEditorId.trim(),
        status: "failed",
        submissionState: remote.submissionState,
      },
    });
  };
  // Studio-backed LP2 generation persists its page-local design state only
  // after copy has been written (and image work has started). Use that durable
  // contract for the loading copy instead of the removed brand/template
  // preflight status. A missing state means the copy workflow is still starting.
  const studioPageDesignStage = (() => {
    if (!productHeroGenerating) return null;
    const generatedContent = (productHeroStatusQuery.data?.generatedContent ?? {}) as any;
    const design =
      generatedContent.studioBrandDesign ?? studioPageDesignStatus;
    const designInput = generatedContent.studioBrandDesignInput;
    const designSystemId =
      designInput?.designSystemId ?? studioPageDesignStatus?.designSystemId;
    if (
      design?.status === "pending" ||
      designSystemId
    ) {
      return design?.submissionState === "submitted"
        ? "Finishing your page design…"
        : "Creating your page design…";
    }
    return null;
  })();
  // A page can have all Product Hero copy sections while its paid Studio
  // design fork is still running. Treat either the run's non-terminal status
  // or the durable page-design state as authoritative for the wait/timeout
  // guard below.
  const productHeroRunWaiting =
    productHeroGenerating &&
    !!selectedRunId &&
    (!productHeroStatusQuery.data ||
      (productHeroStatusQuery.data.status !== "failed" &&
        productHeroStatusQuery.data.status !== "completed") ||
      (productHeroStatusQuery.data.generatedContent as any)?.studioBrandDesign?.status ===
        "pending" ||
      studioPageDesignStatus?.status === "pending");
  const studioBrandDesignFailed =
    surface === "lp-express-2" &&
    (templateType === "product-hero" || studioDesign?.rendererTemplateKey === "smb-home") &&
    studioPageDesignStatus?.status === "failed";
  const studioDesignRecoveryAvailable =
    studioBrandDesignFailed &&
    (studioDesign?.rendererTemplateKey !== "smb-home" ||
      (studioPageDesignStatus?.submissionState !== "ambiguous" &&
        studioPageDesignStatus?.workflowPhase === "revision-submitted")) &&
    typeof studioPageDesignStatus?.mpEditorId === "string" &&
    !!studioPageDesignStatus.mpEditorId.trim();

  // ── Cold Drip background-generation polling ─────────────────────────────
  // Mirrors the advertorial poll above: with SIM_LP_COLD_DRIP_URL set the
  // route returns immediately and the copy blob generates in the background,
  // so we poll the run row every 4s while `coldDripGenerating` is set. "Done"
  // is gated on the RUN status + a usable blob. (The AI image slots are
  // seeded on the same page write and polled separately via coldDripImagesQuery.)
  const coldDripStatusQuery = useQuery<RunRow>({
    queryKey: ["/api/lp-express/runs", selectedRunId, "cold-drip-status", surface],
    queryFn: async () => {
      const res = await fetch(`/api/lp-express/runs/${selectedRunId}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Failed to load run (${res.status})`);
      return (await res.json()) as RunRow;
    },
    enabled: coldDripGenerating && !!selectedRunId,
    refetchOnWindowFocus: false,
    refetchInterval: (q) => {
      if (!coldDripGenerating) return false;
      const d = q.state.data as RunRow | undefined;
      if (!d) return 4000;
      if (d.status === "failed") return false;
      const gc = (d.generatedContent ?? {}) as any;
      if (
        d.status === "completed" &&
        gc?.coldDrip &&
        isLpColdDripContentUsable(normalizeLpColdDripContent(gc.coldDrip))
      )
        return false;
      return 4000;
    },
  });

  // ── Coffee Shop background-generation polling ────────────────────────────
  // Mirrors the cold-drip poll above for the coffee-shop copy blob.
  const coffeeShopStatusQuery = useQuery<RunRow>({
    queryKey: ["/api/lp-express/runs", selectedRunId, "coffee-shop-status", surface],
    queryFn: async () => {
      const res = await fetch(`/api/lp-express/runs/${selectedRunId}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Failed to load run (${res.status})`);
      return (await res.json()) as RunRow;
    },
    enabled: coffeeShopGenerating && !!selectedRunId,
    refetchOnWindowFocus: false,
    refetchInterval: (q) => {
      if (!coffeeShopGenerating) return false;
      const d = q.state.data as RunRow | undefined;
      if (!d) return 4000;
      if (d.status === "failed") return false;
      const gc = (d.generatedContent ?? {}) as any;
      if (
        d.status === "completed" &&
        gc?.coffeeShop &&
        isLpCoffeeShopContentUsable(normalizeLpCoffeeShopContent(gc.coffeeShop))
      )
        return false;
      return 4000;
    },
  });

  // ── Temecula Passport background-generation polling ─────────────────────
  // Mirrors the coffee-shop poll above for the temecula-passport copy blob.
  const temeculaPassportStatusQuery = useQuery<RunRow>({
    queryKey: ["/api/lp-express/runs", selectedRunId, "temecula-passport-status", surface],
    queryFn: async () => {
      const res = await fetch(`/api/lp-express/runs/${selectedRunId}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Failed to load run (${res.status})`);
      return (await res.json()) as RunRow;
    },
    enabled: temeculaPassportGenerating && !!selectedRunId,
    refetchOnWindowFocus: false,
    refetchInterval: (q) => {
      if (!temeculaPassportGenerating) return false;
      const d = q.state.data as RunRow | undefined;
      if (!d) return 4000;
      if (d.status === "failed") return false;
      const gc = (d.generatedContent ?? {}) as any;
      if (
        d.status === "completed" &&
        gc?.temeculaPassport &&
        isLpTemeculaPassportContentUsable(normalizeLpTemeculaPassportContent(gc.temeculaPassport))
      )
        return false;
      return 4000;
    },
  });

  // ── PapaBear Label background-generation polling ─────────────────────────
  // The initial route fans the six claim-safe section mappers out in the
  // background and persists the final normalized blob before completing the
  // run. Explicit per-section regeneration keeps using its own job poll.
  const papabearLabelStatusQuery = useQuery<RunRow>({
    queryKey: ["/api/lp-express/runs", selectedRunId, "papabear-label-status", surface],
    queryFn: async () => {
      const res = await fetch(`/api/lp-express/runs/${selectedRunId}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Failed to load run (${res.status})`);
      return (await res.json()) as RunRow;
    },
    enabled: papabearLabelGenerating && !!selectedRunId,
    refetchOnWindowFocus: false,
    refetchInterval: (q) => {
      if (!papabearLabelGenerating) return false;
      const d = q.state.data as RunRow | undefined;
      if (!d) return 4000;
      return resolveLpPapabearLabelPollState(d).generating ? 4000 : false;
    },
  });

  useEffect(() => {
    if (!papabearLabelGenerating) return;
    const r = papabearLabelStatusQuery.data;
    if (!r) return;
    const state = resolveLpPapabearLabelPollState(r);
    if (state.content) {
      setPapabearLabel(state.content);
      const images = Array.isArray((r.generatedContent as any)?.papabearLabelImages)
        ? ((r.generatedContent as any).papabearLabelImages as LpImageSlot[])
        : null;
      setPapabearLabelImages(images);
      setPapabearLabelImagesPending(
        !!images?.some(
          (slot) => slot?.imageStatus === "pending" && !slot?.imageUrl,
        ),
      );
      setPageId(r.pageId ?? "");
      setCheckoutUrlInput((r as any).checkoutUrl ?? null);
      setPapabearLabelGenerating(false);
      toast({ title: "Papabear page ready" });
    } else if (state.error) {
      setPapabearLabelGenerating(false);
      setHeroError(state.error);
      toast({
        title: "Papabear page generation failed",
        description: state.error,
        variant: "destructive",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [papabearLabelStatusQuery.data, papabearLabelGenerating]);

  useEffect(() => {
    if (!papabearLabelGenerating) return;
    const id = setTimeout(() => {
      toast({
        title: "Your Papabear page is still being written",
        description:
          "You can keep this page open or return later; generation will continue safely in the background.",
      });
    }, LP_PAPABEAR_LABEL_LONG_WAIT_NOTICE_MS);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [papabearLabelGenerating]);

  // Pages created before editorial reason imagery was added have no saved
  // papabearLabelImages array. Kick off the same durable batch route once when
  // such a page is opened; the route persists pending slots before returning,
  // so closing the browser does not cancel the renders.
  useEffect(() => {
    if (
      !papabearLabelLive ||
      !pageId ||
      papabearLabelImages !== null ||
      papabearLabelImagesMutation.isPending
    ) {
      return;
    }
    papabearLabelImagesMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [papabearLabelLive, pageId, papabearLabelImages]);

  // React to the polled run row: hydrate the offer page when the blob lands,
  // or surface a generation failure via the in-canvas error/retry state.
  useEffect(() => {
    if (coldDripGenerating) {
      const r = coldDripStatusQuery.data;
      if (r) {
        const gc = (r.generatedContent ?? {}) as any;
        if (
          r.status === "completed" &&
          gc.templateType === "cold-drip" &&
          gc.coldDrip &&
          isLpColdDripContentUsable(normalizeLpColdDripContent(gc.coldDrip))
        ) {
          setColdDrip(normalizeLpColdDripContent(gc.coldDrip));
          // The run write seeds the AI image slots pending before completion —
          // hydrate them and start the slot poll for the background fill.
          const cdImgs = Array.isArray(gc.coldDripImages) ? gc.coldDripImages : null;
          setColdDripImages(cdImgs);
          setColdDripImagesPending(
            !!cdImgs?.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl),
          );
          setPageId(r.pageId ?? "");
          setCheckoutUrlInput((r as any).checkoutUrl ?? null);
          setColdDripGenerating(false);
          toast({ title: "Offer page ready" });
        } else if (r.status === "failed") {
          setColdDripGenerating(false);
          setHeroError(r.errorMessage || "Offer page generation failed. Please try again.");
          toast({
            title: "Offer page generation failed",
            description: r.errorMessage || "Please try again.",
            variant: "destructive",
          });
        }
      }
    }

    if (coffeeShopGenerating) {
      const r = coffeeShopStatusQuery.data;
      if (r) {
        const gc = (r.generatedContent ?? {}) as any;
        // Only accept the copy once the run reaches a terminal success —
        // during a whole-page regeneration the previous blob is still present,
        // so blob-presence alone would hydrate the stale copy.
        if (
          r.status === "completed" &&
          gc.templateType === "coffee-shop" &&
          gc.coffeeShop &&
          isLpCoffeeShopContentUsable(normalizeLpCoffeeShopContent(gc.coffeeShop))
        ) {
          const wasRegen = coffeeShopRegenerating;
          setCoffeeShop(normalizeLpCoffeeShopContent(gc.coffeeShop));
          // The run write seeds the AI image slots pending before completion —
          // hydrate them and start the slot poll for the background fill.
          const csImgs = Array.isArray(gc.coffeeShopImages) ? gc.coffeeShopImages : null;
          setCoffeeShopImages(csImgs);
          setCoffeeShopImagesPending(
            !!csImgs?.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl),
          );
          setPageId(r.pageId ?? "");
          setCheckoutUrlInput((r as any).checkoutUrl ?? null);
          setCoffeeShopGenerating(false);
          setCoffeeShopRegenerating(false);
          toast({ title: wasRegen ? "Page copy updated" : "Lead-capture page ready" });
        } else if (r.status === "failed") {
          const wasRegen = coffeeShopRegenerating;
          setCoffeeShopGenerating(false);
          setCoffeeShopRegenerating(false);
          // A regeneration failure keeps the existing page copy (no error UI);
          // a first-time generation failure has nothing to fall back to, so
          // surface the in-canvas error/retry state.
          if (!wasRegen) {
            setHeroError(r.errorMessage || "Lead-capture page generation failed. Please try again.");
          }
          toast({
            title: wasRegen ? "Couldn't regenerate the page copy" : "Lead-capture page generation failed",
            description: r.errorMessage || "Please try again.",
            variant: "destructive",
          });
        }
      }
    }

    if (temeculaPassportGenerating) {
      const r = temeculaPassportStatusQuery.data;
      if (r) {
        const gc = (r.generatedContent ?? {}) as any;
        // Only accept the copy once the run reaches a terminal success —
        // during a whole-page regeneration the previous blob is still present,
        // so blob-presence alone would hydrate the stale copy.
        if (
          r.status === "completed" &&
          gc.templateType === "temecula-passport" &&
          gc.temeculaPassport &&
          isLpTemeculaPassportContentUsable(normalizeLpTemeculaPassportContent(gc.temeculaPassport))
        ) {
          const wasRegen = temeculaPassportRegenerating;
          setTemeculaPassport(normalizeLpTemeculaPassportContent(gc.temeculaPassport));
          // The run write seeds the AI image slots pending before completion —
          // hydrate them and start the slot poll for the background fill.
          const tpImgs = Array.isArray(gc.temeculaPassportImages) ? gc.temeculaPassportImages : null;
          setTemeculaPassportImages(tpImgs);
          setTemeculaPassportImagesPending(
            !!tpImgs?.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl),
          );
          setPageId(r.pageId ?? "");
          setCheckoutUrlInput((r as any).checkoutUrl ?? null);
          setTemeculaPassportGenerating(false);
          setTemeculaPassportRegenerating(false);
          toast({ title: wasRegen ? "Page copy updated" : "Mystery-drop page ready" });
        } else if (r.status === "failed") {
          const wasRegen = temeculaPassportRegenerating;
          setTemeculaPassportGenerating(false);
          setTemeculaPassportRegenerating(false);
          // A regeneration failure keeps the existing page copy (no error UI);
          // a first-time generation failure has nothing to fall back to, so
          // surface the in-canvas error/retry state.
          if (!wasRegen) {
            setHeroError(r.errorMessage || "Mystery-drop page generation failed. Please try again.");
          }
          toast({
            title: wasRegen ? "Couldn't regenerate the page copy" : "Mystery-drop page generation failed",
            description: r.errorMessage || "Please try again.",
            variant: "destructive",
          });
        }
      }
    }

    if (productHeroGenerating) {
      const r = studioDesignRecoveryRequestPending ? undefined : productHeroStatusQuery.data;
      if (r) {
        if (r.brandId !== brandId || r.id !== selectedRunId) return;
        setSmbHomeRunReady(isLpSmbHomeRunReady(r));
        const gc = (r.generatedContent ?? {}) as any;
        if (isSmbHomeAttempt(gc)) {
          applyGcToLocalState(gc);
          const home = resolveSmbHomeLifecycle(r);
          setProductHeroGenerating(home.status === "rendering");
          setSmbHomeRunReady(home.status === "ready");
          setHeroError(home.message);
          if (home.status !== "rendering") setStudioDesignRecoveryActive(false);
          return;
        }
        const studioDesignPending = gc.studioBrandDesign?.status === "pending";
        const studioDesignFailed = gc.studioBrandDesign?.status === "failed";
        if (gc.studioBrandDesign && typeof gc.studioBrandDesign === "object") {
          // Keep only the status gate in the UI; provider identifiers remain
          // local to the recovery eligibility check and are never rendered.
          setStudioPageDesignStatus(gc.studioBrandDesign);
        }
        if (
          r.status === "completed" &&
          gc.templateType === "product-hero" &&
          hasUsableLpProductHeroSections(gc) &&
          !studioDesignPending &&
          !studioDesignFailed
        ) {
          setProductHeroSections(
            Object.fromEntries(
              (LP_PRODUCT_HERO_BLOCK_KEYS as readonly string[])
                .filter((k) => k in gc)
                .map((k) => [k, gc[k]]),
            ),
          );
          setStudioDesign(resolveLpStudioPreviewDesign(gc));
          setSectionLayout(
            sectionLayoutFromSections(gc.sections, LP_PRODUCT_HERO_SECTION_TYPES),
          );
          // Hydrate whatever AI lifestyle slots exist so far and keep polling any
          // that are still rendering in the background.
          const phImgs = Array.isArray(gc.productHeroImages) ? gc.productHeroImages : null;
          if (phImgs) {
            setProductHeroImages(phImgs);
            setProductHeroImagesPending(
              phImgs.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl),
            );
          }
          setPageId(r.pageId ?? "");
          setCheckoutUrlInput((r as any).checkoutUrl ?? null);
          setProductHeroGenerating(false);
          setStudioDesignRecoveryActive(false);
          setHeroError(null);
          toast({ title: "Product page ready" });
        } else if (studioDesignPending) {
          // Recovery keeps the existing page/run row scoped to this run while
          // the already-admitted remote job is polled read-only.
          setProductHeroGenerating(true);
        } else if (r.status === "failed") {
          setProductHeroGenerating(false);
          setStudioDesignRecoveryActive(false);
          // First-time failure has nothing to fall back to, so surface the in-canvas
          // error/retry state.
          setHeroError(
            studioDesignFailed
              ? studioBrandDesignUserMessage(gc.studioBrandDesign)
              : r.errorMessage || "Product page generation failed. Please try again.",
          );
          toast({
            title: "Product page generation failed",
            description: studioDesignFailed
              ? studioBrandDesignUserMessage(gc.studioBrandDesign)
              : r.errorMessage || "Please try again.",
            variant: "destructive",
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, selectedRunId, productHeroStatusQuery.data, coldDripStatusQuery.data, coffeeShopStatusQuery.data, temeculaPassportStatusQuery.data, productHeroGenerating, studioDesignRecoveryRequestPending, coldDripGenerating, coffeeShopGenerating, coffeeShopRegenerating, temeculaPassportGenerating, temeculaPassportRegenerating]);

  // Hard stop for a stranded generation (same as the quiz timeout).
  useEffect(() => {
    if (!productHeroGenerating && !coldDripGenerating && !coffeeShopGenerating && !temeculaPassportGenerating) return;
    const id = setTimeout(() => {
      if (productHeroGenerating) {
        // A Product Hero run may have usable copy while the page-local Studio
        // design admission is still pending. Do not tell the user to Generate
        // again: that could submit a second paid provider request. The run
        // poll remains active and will clear this state at its terminal result.
        if (productHeroRunWaiting) return;
        setProductHeroGenerating(false);
        setHeroError(
          "The product page is taking longer than expected. Please try generating again.",
        );
        toast({
          title: "Product page generation timed out",
          description: "Please try again.",
          variant: "destructive",
        });
      }
      if (coldDripGenerating) {
        setColdDripGenerating(false);
        setHeroError(
          "The offer page is taking longer than expected. Please try generating again.",
        );
        toast({
          title: "Offer page generation timed out",
          description: "Please try again.",
          variant: "destructive",
        });
      }
      if (coffeeShopGenerating) {
        setCoffeeShopGenerating(false);
        setHeroError(
          "The lead-capture page is taking longer than expected. Please try generating again.",
        );
        toast({
          title: "Lead-capture page generation timed out",
          description: "Please try again.",
          variant: "destructive",
        });
      }
      if (temeculaPassportGenerating) {
        setTemeculaPassportGenerating(false);
        setHeroError(
          "The mystery-drop page is taking longer than expected. Please try generating again.",
        );
        toast({
          title: "Mystery-drop page generation timed out",
          description: "Please try again.",
          variant: "destructive",
        });
      }
    }, 6 * 60_000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productHeroGenerating, productHeroRunWaiting, coldDripGenerating, coffeeShopGenerating, temeculaPassportGenerating]);

  // ── Problem / Solution background-generation polling ────────────────────────
  // Mirror of the product-hero poll above: the route returns immediately and
  // generates each ps* section's copy in the background, so we poll the run row
  // every 4s while `problemSolutionGenerating` is set.
  const problemSolutionStatusQuery = useQuery<RunRow>({
    queryKey: ["/api/lp-express/runs", selectedRunId, "problem-solution-status", surface],
    queryFn: async () => {
      const res = await fetch(`/api/lp-express/runs/${selectedRunId}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Failed to load run (${res.status})`);
      return (await res.json()) as RunRow;
    },
    enabled: problemSolutionGenerating && !!selectedRunId,
    refetchOnWindowFocus: false,
    refetchInterval: (q) => {
      if (!problemSolutionGenerating) return false;
      const d = q.state.data as RunRow | undefined;
      if (!d) return 4000;
      if (d.status === "failed") return false;
      const gc = (d.generatedContent ?? {}) as any;
      if (d.status === "completed" && hasUsableLpProblemSolutionSections(gc)) return false;
      return 4000;
    },
  });

  useEffect(() => {
    if (!problemSolutionGenerating) return;
    const r = problemSolutionStatusQuery.data;
    if (!r) return;
    const gc = (r.generatedContent ?? {}) as any;
    if (
      r.status === "completed" &&
      gc.templateType === "problem-solution" &&
      hasUsableLpProblemSolutionSections(gc)
    ) {
      setProblemSolutionSections(
        Object.fromEntries(
          (LP_PROBLEM_SOLUTION_BLOCK_KEYS as readonly string[])
            .filter((k) => k in gc)
            .map((k) => [k, gc[k]]),
        ),
      );
      setSectionLayout(
        sectionLayoutFromSections(gc.sections, LP_PROBLEM_SOLUTION_SECTION_TYPES),
      );
      // Hydrate the mechanism-diagram slot and keep polling if it's still
      // rendering in the background.
      const psImgs = Array.isArray(gc.problemSolutionImages) ? gc.problemSolutionImages : null;
      if (psImgs) {
        setProblemSolutionImages(psImgs);
        setProblemSolutionImagesPending(
          psImgs.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl),
        );
      }
      setPageId(r.pageId ?? "");
      setCheckoutUrlInput((r as any).checkoutUrl ?? null);
      setProblemSolutionGenerating(false);
      toast({ title: "Sales page ready" });
    } else if (r.status === "failed") {
      setProblemSolutionGenerating(false);
      // First-time failure has nothing to fall back to, so surface the
      // in-canvas error/retry state.
      setHeroError(r.errorMessage || "Sales page generation failed. Please try again.");
      toast({
        title: "Sales page generation failed",
        description: r.errorMessage || "Please try again.",
        variant: "destructive",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problemSolutionStatusQuery.data, problemSolutionGenerating]);

  // Hard stop for a stranded problem-solution generation (same as the quiz timeout).
  useEffect(() => {
    if (!problemSolutionGenerating) return;
    const id = setTimeout(() => {
      setProblemSolutionGenerating(false);
      setHeroError(
        "The sales page is taking longer than expected. Please try generating again.",
      );
      toast({
        title: "Sales page generation timed out",
        description: "Please try again.",
        variant: "destructive",
      });
    }, 6 * 60_000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problemSolutionGenerating]);

  // ── Offer Stack background-generation polling ───────────────────────────────
  // Mirror of the problem-solution poll above: the route returns immediately
  // and generates each os* section's copy in the background, so we poll the
  // run row every 4s while `offerStackGenerating` is set.
  const offerStackStatusQuery = useQuery<RunRow>({
    queryKey: ["/api/lp-express/runs", selectedRunId, "offer-stack-status", surface],
    queryFn: async () => {
      const res = await fetch(`/api/lp-express/runs/${selectedRunId}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Failed to load run (${res.status})`);
      return (await res.json()) as RunRow;
    },
    enabled: offerStackGenerating && !!selectedRunId,
    refetchOnWindowFocus: false,
    refetchInterval: (q) => {
      if (!offerStackGenerating) return false;
      const d = q.state.data as RunRow | undefined;
      if (!d) return 4000;
      if (d.status === "failed") return false;
      const gc = (d.generatedContent ?? {}) as any;
      if (d.status === "completed" && hasUsableLpOfferStackSections(gc)) return false;
      return 4000;
    },
  });

  useEffect(() => {
    if (!offerStackGenerating) return;
    const r = offerStackStatusQuery.data;
    if (!r) return;
    const gc = (r.generatedContent ?? {}) as any;
    if (
      r.status === "completed" &&
      gc.templateType === "offer-stack" &&
      hasUsableLpOfferStackSections(gc)
    ) {
      setOfferStackSections(
        Object.fromEntries(
          (LP_OFFER_STACK_BLOCK_KEYS as readonly string[])
            .filter((k) => k in gc)
            .map((k) => [k, gc[k]]),
        ),
      );
      setSectionLayout(
        sectionLayoutFromSections(gc.sections, LP_OFFER_STACK_SECTION_TYPES),
      );
      // Hydrate the intro-visual slot and keep polling if it's still
      // rendering in the background.
      const osImgs = Array.isArray(gc.offerStackImages) ? gc.offerStackImages : null;
      if (osImgs) {
        setOfferStackImages(osImgs);
        setOfferStackImagesPending(
          osImgs.some((s: any) => s?.imageStatus === "pending" && !s?.imageUrl),
        );
      }
      setPageId(r.pageId ?? "");
      setCheckoutUrlInput((r as any).checkoutUrl ?? null);
      setOfferStackGenerating(false);
      toast({ title: "Offer page ready" });
    } else if (r.status === "failed") {
      setOfferStackGenerating(false);
      // First-time failure has nothing to fall back to, so surface the
      // in-canvas error/retry state.
      setHeroError(r.errorMessage || "Offer page generation failed. Please try again.");
      toast({
        title: "Offer page generation failed",
        description: r.errorMessage || "Please try again.",
        variant: "destructive",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerStackStatusQuery.data, offerStackGenerating]);

  // Hard stop for a stranded offer-stack generation (same as the quiz timeout).
  useEffect(() => {
    if (!offerStackGenerating) return;
    const id = setTimeout(() => {
      setOfferStackGenerating(false);
      setHeroError(
        "The offer page is taking longer than expected. Please try generating again.",
      );
      toast({
        title: "Offer page generation timed out",
        description: "Please try again.",
        variant: "destructive",
      });
    }, 6 * 60_000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerStackGenerating]);

  // Hero-image carousel polling. Reads the live hero-image status off
  // lp_express_pages — we poll every 3s while the worker is still
  // running, and stop once it lands on ready/failed/idle.
  const papabearHeroKickoffRef = useRef<Set<string>>(new Set());
  const heroImagesQuery = useQuery<{
    id: string;
    heroImageStatus: string | null;
    heroImageError: string | null;
    heroImageUrls: Array<string | null> | null;
    heroImageSlotStatuses?: Array<string> | null;
  }>({
    queryKey: ["/api/landing-pages", pageId, surface],
    queryFn: async () => {
      const res = await fetch(`/api/landing-pages/${pageId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load landing page");
      return res.json();
    },
    enabled: !!pageId,
    refetchInterval: (q) => {
      const s = q.state.data?.heroImageStatus;
      return s === "pending" || s === "queued" || s === "rendering" ? 3000 : false;
    },
  });
  const heroImageStatus = heroImagesQuery.data?.heroImageStatus ?? null;
  const heroImageUrls = heroImagesQuery.data?.heroImageUrls ?? null;
  const heroImageSlotStatuses = heroImagesQuery.data?.heroImageSlotStatuses ?? null;
  const heroImagesLoading =
    heroImageStatus === "pending" || heroImageStatus === "queued" || heroImageStatus === "rendering";

  // Pages created before PapaBear joined the hero-image worker have an idle
  // (null) status forever. Start those legacy pages once when opened; new pages
  // are already marked pending by the create route and skip this path.
  useEffect(() => {
    if (
      !pageId ||
      !papabearLabelLive ||
      !heroImagesQuery.isFetched ||
      heroImageStatus !== null ||
      papabearHeroKickoffRef.current.has(pageId)
    ) {
      return;
    }
    papabearHeroKickoffRef.current.add(pageId);
    queryClient.setQueryData(
      ["/api/landing-pages", pageId],
      (current: Record<string, unknown> | undefined) => ({
        ...(current ?? {}),
        id: pageId,
        heroImageStatus: "pending",
        heroImageError: null,
        heroImageUrls: null,
      }),
    );
    apiRequest("POST", `/api/landing-pages/${pageId}/generate-hero-images`)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ["/api/landing-pages", pageId],
        });
      })
      .catch((error) => {
        console.warn(
          "[lp-express/papabear-label] legacy hero image kickoff failed:",
          error,
        );
      });
  }, [
    heroImageStatus,
    heroImagesQuery.isFetched,
    pageId,
    papabearLabelLive,
    queryClient,
  ]);

  // Showcase-image polling. The showcase image is generated in the
  // background (gpt-image-2 is slow), so once the copy comes back with
  // imageStatus="pending" we poll the run's generatedContent.showcase until
  // the durable R2 imageUrl appears (or it fails), then merge it into the
  // local showcase state so the renderer shows it.
  const productContextImageQuery = useQuery<RunRow>({
    queryKey: ["/api/lp-express/runs", selectedRunId, "product-context-image", surface],
    queryFn: async () => {
      const res = await fetch(`/api/lp-express/runs/${selectedRunId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to poll product context image");
      return res.json();
    },
    enabled: productContextImagePending && !!selectedRunId,
    refetchInterval: (q) => {
      const pc = (q.state.data as any)?.generatedContent?.productContext;
      const arr = Array.isArray(pc) ? pc : [];
      // Stop once no card is still pending (each is either ready or failed).
      const stillPending = arr.some(
        (c: any) => !c?.imageUrl && c?.imageStatus === "pending",
      );
      return stillPending ? 4000 : false;
    },
  });
  useEffect(() => {
    const pc = (productContextImageQuery.data as any)?.generatedContent?.productContext;
    if (!Array.isArray(pc)) return;
    setProductContext(pc);
    const stillPending = pc.some(
      (c: any) => !c?.imageUrl && c?.imageStatus === "pending",
    );
    if (!stillPending) setProductContextImagePending(false);
  }, [productContextImageQuery.data]);

  // Origin-image polling. The Farmstand still images are generated in the
  // background (gpt-image-2 is slow), so once the prompts come back with
  // imageStatus="pending" we poll the run's generatedContent.originImages until
  // each durable R2 imageUrl appears (or it fails).
  const originImagesQuery = useQuery<RunRow>({
    queryKey: ["/api/lp-express/runs", selectedRunId, "origin-images", surface],
    queryFn: async () => {
      const res = await fetch(`/api/lp-express/runs/${selectedRunId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to poll origin images");
      return res.json();
    },
    enabled: originImagesPending && !!selectedRunId,
    refetchInterval: (q) => {
      const oi = (q.state.data as any)?.generatedContent?.originImages;
      const arr = Array.isArray(oi) ? oi : [];
      const stillPending = arr.some(
        (s: any) => !s?.imageUrl && s?.imageStatus === "pending",
      );
      return stillPending ? 4000 : false;
    },
  });
  useEffect(() => {
    const oi = (originImagesQuery.data as any)?.generatedContent?.originImages;
    if (!Array.isArray(oi)) return;
    setOriginImages(oi);
    const stillPending = oi.some(
      (s: any) => !s?.imageUrl && s?.imageStatus === "pending",
    );
    if (!stillPending) setOriginImagesPending(false);
  }, [originImagesQuery.data]);

  // Section-image polling — one query per image workflow, mirroring the
  // origin-image poll. Each polls the run row until every slot has a durable
  // R2 imageUrl (or has failed), then stops.
  const makeImagePoll = (
    suffix: string,
    key: "alternativesImages" | "philosophyImages" | "proofImages" | "listicleImages" | "advertorialImages" | "coldDripImages" | "coffeeShopImages" | "temeculaPassportImages" | "papabearLabelImages" | "productHeroImages" | "problemSolutionImages" | "offerStackImages",
    pending: boolean,
  ) =>
    useQuery<RunRow>({
      queryKey: ["/api/lp-express/runs", selectedRunId, suffix, surface],
      queryFn: async () => {
        const res = await fetch(`/api/lp-express/runs/${selectedRunId}`, { credentials: "include" });
        if (!res.ok) throw new Error(`Failed to poll ${suffix}`);
        return res.json();
      },
      enabled: pending && !!selectedRunId,
      refetchInterval: (q) => {
        const oi = (q.state.data as any)?.generatedContent?.[key];
        const arr = Array.isArray(oi) ? oi : [];
        const stillPending = arr.some((s: any) => !s?.imageUrl && s?.imageStatus === "pending");
        return stillPending ? 4000 : false;
      },
    });
  const alternativesImagesQuery = makeImagePoll("alternatives-images", "alternativesImages", alternativesImagesPending);
  const philosophyImagesQuery = makeImagePoll("philosophy-images", "philosophyImages", philosophyImagesPending);
  const proofImagesQuery = makeImagePoll("proof-images", "proofImages", proofImagesPending);
  const listicleImagesQuery = makeImagePoll("listicle-images", "listicleImages", listicleImagesPending);
  const advertorialImagesQuery = makeImagePoll("advertorial-images", "advertorialImages", advertorialImagesPending);
  const coldDripImagesQuery = makeImagePoll("cold-drip-images", "coldDripImages", coldDripImagesPending);
  const coffeeShopImagesQuery = makeImagePoll("coffee-shop-images", "coffeeShopImages", coffeeShopImagesPending);
  const temeculaPassportImagesQuery = makeImagePoll("temecula-passport-images", "temeculaPassportImages", temeculaPassportImagesPending);
  const papabearLabelImagesQuery = makeImagePoll("papabear-label-images", "papabearLabelImages", papabearLabelImagesPending);
  const productHeroImagesQuery = makeImagePoll("product-hero-images", "productHeroImages", productHeroImagesPending);
  const problemSolutionImagesQuery = makeImagePoll("problem-solution-images", "problemSolutionImages", problemSolutionImagesPending);
  const offerStackImagesQuery = makeImagePoll("offer-stack-images", "offerStackImages", offerStackImagesPending);
  useEffect(() => {
    const oi = (alternativesImagesQuery.data as any)?.generatedContent?.alternativesImages;
    if (!Array.isArray(oi)) return;
    setAlternativesImages(oi);
    if (!oi.some((s: any) => !s?.imageUrl && s?.imageStatus === "pending")) setAlternativesImagesPending(false);
  }, [alternativesImagesQuery.data]);
  useEffect(() => {
    const oi = (philosophyImagesQuery.data as any)?.generatedContent?.philosophyImages;
    if (!Array.isArray(oi)) return;
    setPhilosophyImages(oi);
    if (!oi.some((s: any) => !s?.imageUrl && s?.imageStatus === "pending")) setPhilosophyImagesPending(false);
  }, [philosophyImagesQuery.data]);
  useEffect(() => {
    const oi = (proofImagesQuery.data as any)?.generatedContent?.proofImages;
    if (!Array.isArray(oi)) return;
    setProofImages(oi);
    if (!oi.some((s: any) => !s?.imageUrl && s?.imageStatus === "pending")) setProofImagesPending(false);
  }, [proofImagesQuery.data]);
  useEffect(() => {
    const oi = (listicleImagesQuery.data as any)?.generatedContent?.listicleImages;
    if (!Array.isArray(oi)) return;
    setListicleImages(oi);
    if (!oi.some((s: any) => !s?.imageUrl && s?.imageStatus === "pending")) setListicleImagesPending(false);
  }, [listicleImagesQuery.data]);
  useEffect(() => {
    const oi = (advertorialImagesQuery.data as any)?.generatedContent?.advertorialImages;
    if (!Array.isArray(oi)) return;
    setAdvertorialImages(oi);
    if (!oi.some((s: any) => !s?.imageUrl && s?.imageStatus === "pending")) setAdvertorialImagesPending(false);
  }, [advertorialImagesQuery.data]);
  useEffect(() => {
    const oi = (coldDripImagesQuery.data as any)?.generatedContent?.coldDripImages;
    if (!Array.isArray(oi)) return;
    setColdDripImages(oi);
    if (!oi.some((s: any) => !s?.imageUrl && s?.imageStatus === "pending")) setColdDripImagesPending(false);
  }, [coldDripImagesQuery.data]);
  useEffect(() => {
    const oi = (coffeeShopImagesQuery.data as any)?.generatedContent?.coffeeShopImages;
    if (!Array.isArray(oi)) return;
    setCoffeeShopImages(oi);
    if (!oi.some((s: any) => !s?.imageUrl && s?.imageStatus === "pending")) setCoffeeShopImagesPending(false);
  }, [coffeeShopImagesQuery.data]);
  useEffect(() => {
    const oi = (temeculaPassportImagesQuery.data as any)?.generatedContent?.temeculaPassportImages;
    if (!Array.isArray(oi)) return;
    setTemeculaPassportImages(oi);
    if (!oi.some((s: any) => !s?.imageUrl && s?.imageStatus === "pending")) setTemeculaPassportImagesPending(false);
  }, [temeculaPassportImagesQuery.data]);
  useEffect(() => {
    const images = (papabearLabelImagesQuery.data as any)?.generatedContent
      ?.papabearLabelImages;
    if (!Array.isArray(images)) return;
    setPapabearLabelImages(images);
    if (
      !images.some(
        (slot: any) =>
          !slot?.imageUrl && slot?.imageStatus === "pending",
      )
    ) {
      setPapabearLabelImagesPending(false);
    }
  }, [papabearLabelImagesQuery.data]);
  useEffect(() => {
    const oi = (productHeroImagesQuery.data as any)?.generatedContent?.productHeroImages;
    if (!Array.isArray(oi)) return;
    setProductHeroImages(oi);
    if (!oi.some((s: any) => !s?.imageUrl && s?.imageStatus === "pending")) setProductHeroImagesPending(false);
  }, [productHeroImagesQuery.data]);
  useEffect(() => {
    const oi = (problemSolutionImagesQuery.data as any)?.generatedContent?.problemSolutionImages;
    if (!Array.isArray(oi)) return;
    setProblemSolutionImages(oi);
    if (!oi.some((s: any) => !s?.imageUrl && s?.imageStatus === "pending")) setProblemSolutionImagesPending(false);
  }, [problemSolutionImagesQuery.data]);
  useEffect(() => {
    const oi = (offerStackImagesQuery.data as any)?.generatedContent?.offerStackImages;
    if (!Array.isArray(oi)) return;
    setOfferStackImages(oi);
    if (!oi.some((s: any) => !s?.imageUrl && s?.imageStatus === "pending")) setOfferStackImagesPending(false);
  }, [offerStackImagesQuery.data]);

  // Deletes a merged page row. A row backed by a run is deleted via the run
  // endpoint (which also removes any published page, taking it offline). An
  // orphaned published-only row (no run) is deleted via the published-page
  // endpoint. Either way the public page goes offline and the row leaves the list.
  const deleteRunMutation = useMutation({
    mutationFn: async (row: {
      runId: string;
      publishedId: string | null;
    }) => {
      const url = row.runId
        ? `/api/lp-express/runs/${row.runId}`
        : `/api/brands/${brandId}/lp-express/published-pages/${row.publishedId}`;
      const res = await apiRequest("DELETE", url);
      return res.json();
    },
    onSuccess: (_data, row) => {
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/runs`],
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/published-pages`],
      });
      if (row.runId && selectedRunId === row.runId) goHome();
      toast({ title: "Page deleted" });
    },
    onError: (err: Error) => {
      toast({
        title: "Delete failed",
        description: err?.message || "Try again.",
        variant: "destructive",
      });
    },
  });

  // Publish a draft straight from the list (no detour into the editor). Auto-
  // generates the public slug server-side; the user can rename it later via the
  // editor's Public URL card. Only usable once the run has produced a saved page.
  const publishFromListMutation = useMutation({
    mutationFn: async (pageId: string) => {
      const res = await apiRequest("POST", `/api/landing-pages/${pageId}/publish`, {});
      return parseJsonResponse(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/published-pages`],
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/lp-express/runs`],
      });
      toast({ title: "Page published", description: "Your landing page is now live." });
    },
    onError: (e: any) => {
      toast({
        title: "Publish failed",
        description: e?.message || "Could not publish.",
        variant: "destructive",
      });
    },
  });

  // Clear all preview/draft state back to the LP Express "home" (inputs + the
  // list of pages), without touching the URL. Shared by goHome and the
  // brand-switch reset effect below.
  const resetPreviewState = () => {
    setSmbPageMetadata({});
    setHero(null);
    setHeroError(null);
    setQuiz(null);
    setQuizGenerating(false);
    setListicle(null);
    setListicleGenerating(false);
    setListicleImages(null);
    setListicleImagesPending(false);
    setAdvertorial(null);
    setColdDrip(null);
    setPapabearLabel(null);
    setPapabearLabelImages(null);
    setPapabearLabelImagesPending(false);
    setColdDripGenerating(false);
    setCoffeeShop(null);
    setCoffeeShopGenerating(false);
    setCoffeeShopImages(null);
    setCoffeeShopImagesPending(false);
    setTemeculaPassport(null);
    setTemeculaPassportGenerating(false);
    setTemeculaPassportImages(null);
    setTemeculaPassportImagesPending(false);
    setAdvertorialGenerating(false);
    setAdvertorialImages(null);
    setAdvertorialImagesPending(false);
    setProductHeroSections({});
    setProductHeroGenerating(false);
    setProductHeroImages(null);
    setProductHeroImagesPending(false);
    setStudioDesign(null);
    setSmbHomeRunReady(false);
    setStudioPageDesignStatus(null);
    setStudioDesignRecoveryActive(false);
    setStudioDesignRecoveryRequestPending(false);
    setLeadin("");
    setAdvisory(null);
    setShowcase(null);
    setProductContext(null);
    setProductContextImagePending(false);
    setOriginImages(null);
    setOriginImagesPending(false);
    setStations(null);
    setVillain(null);
    setOriginCopy(null);
    setQuotes(null);
    setTimeline(null);
    setMembership(null);
    setAlternatives(null);
    setPhilosophy(null);
    setDifferentiator(null);
    setFaq(null);
    setReviews(null);
    setFinalCta(null);
    setFooter(null);
    setProof(null);
    setAlternativesImages(null);
    setAlternativesImagesPending(false);
    setPhilosophyImages(null);
    setPhilosophyImagesPending(false);
    setProofImages(null);
    setProofImagesPending(false);
    setSectionLayout(canonicalSectionLayout(LP_LONG_FORM_SECTION_TYPES));
    setVideoOverrides({});
    setElementStates({});
    setTheme({});
    setElementStyles({});
    setComponentOrder({});
    setReviewOverrides({});
    setPageId("");
    setCheckoutUrlInput(null);
    setSelectedRunId("");
    setInputsExpanded(true);
  };

  // Close the current preview and return to the LP Express "home" (inputs +
  // the list of pages), without deleting anything. Clears all preview state and
  // drops the ?runId from the URL so the load-by-url effect won't re-open it.
  const goHome = () => {
    if (studioDesignRecoveryActive) return;
    navigationEpochRef.current += 1;
    resetPreviewState();
    navigate(surfacePath);
  };

  // When the user switches brands while a draft preview is open, the page
  // component is reused (only the :brandId route param changes), so stale
  // preview state from the previous brand would otherwise stay on screen.
  // Reset back to home whenever the brand actually changes (not on first
  // mount, so deep-linked ?runId loads still work).
  const prevBrandIdRef = useRef(brandId);
  useEffect(() => {
    if (prevBrandIdRef.current === brandId) return;
    prevBrandIdRef.current = brandId;
    navigationEpochRef.current += 1;
    resetPreviewState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  const emptyResource =
    showHomeView &&
    studioDesign?.rendererTemplateKey !== "smb-home" &&
    !(selectedRunId && templateType === "smb-home") && !isLoadingAny ? (
      personas.length === 0 ? "personas" :
      products.length === 0 ? "products" :
      angles.length === 0 ? "angles" :
      null
    ) : null;
  const lpDesignSystemPicker = (
    <LpExpressDesignSystemPicker
      visible={
        surface === "lp-express-2" &&
        !!brandId &&
        (selectedTemplateType?.source === "template-studio" || smbHomeEligible)
      }
      systems={accessibleLpDesignSystems}
      loading={lpDesignSystemsLoading}
      error={lpDesignSystemsError}
      unavailable={hasUnavailablePersistedLpDesignSystem}
      failed={hasFailedDesignSystemSelection}
      pending={isDesignSystemSelectionPending}
      disabled={isRunning}
      value={selectedLpDesignSystemValue}
      onChange={saveLpDesignSystem}
    />
  );
  const handleSmbHomeAdmitted = (originBrandId: string, result: { runId?: string; pageId?: string }) => {
    if (originBrandId !== brandId || (!result.runId && !result.pageId)) return;
    void queryClient.invalidateQueries({ queryKey: [`/api/brands/${originBrandId}/lp-express/runs`, surface] });
    void queryClient.invalidateQueries({ queryKey: [`/api/brands/${originBrandId}/lp-express/published-pages`, surface] });
    setSmbHomeRunReady(false);
    navigationEpochRef.current += 1;
    const query = result.runId
      ? `runId=${encodeURIComponent(result.runId)}`
      : `pageId=${encodeURIComponent(result.pageId!)}`;
    navigate(`${surfacePath}?${query}`);
  };
  const smbHomeGenerator = surface === "lp-express-2" && brandId &&
    (smbHomeEligible || smbHomeCapabilityQuery.isError) ? (
      <LpExpressSmbHomeGenerator
        brandId={brandId}
        eligible={smbHomeEligible}
        eligibilityError={smbHomeCapabilityQuery.isError}
        onRetryEligibility={() => void smbHomeCapabilityQuery.refetch()}
        selectedRunId={selectedRunId}
        selectedPageId={pageId}
        selectedPageReady={
          studioDesign?.rendererTemplateKey === "smb-home" &&
          smbHomeRunReady &&
          !productHeroGenerating
        }
        designSystemReady={
          !lpDesignSystemsLoading &&
          !lpDesignSystemsError &&
          !isDesignSystemSelectionPending &&
          !hasFailedDesignSystemSelection &&
          !hasUnavailablePersistedLpDesignSystem
        }
        onAdmitted={handleSmbHomeAdmitted}
      />
    ) : null;
  const homeOverview = showHomeView && surface === "lp-express-2" && pageType === "hp";
  const showStandardInputs = !emptyResource && !homeOverview;

  return (
    <div className={`p-8 mx-auto space-y-6 ${showPreviewSurface ? "max-w-[1600px]" : "max-w-7xl"}`}>
      <div className="flex items-center justify-between gap-4 flex-wrap fade-in-up">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="text-lp-express-title">
            <Rocket className="h-6 w-6 text-primary" />
            {surface === "lp-express-2" ? "Landing Pages Express 2" : "Landing Pages Express"}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {studioDesign?.rendererTemplateKey === "smb-home"
              ? "Edit your saved home page and supplied photos."
              : homeOverview
                ? "Review your business facts and choose a template to generate a Home page. No persona, product, or angle is needed."
                : surface === "lp-express-2" && (smbHomeEligibilityPending || smbHomeCapabilityQuery.isError)
                  ? "Create and manage landing pages for your brand."
                  : "Pick a persona, product, and angle. We draft the landing page."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {showHomeView && user?.isMaster && (
            <Link href={`/brands/${brandId}/landing-pages-express/leads`}>
              <Button variant="outline" size="sm" data-testid="button-lp-express-leads">
                <Inbox className="h-4 w-4 mr-1.5" />
                Leads
              </Button>
            </Link>
          )}
          {showHomeView && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCustomDomainModalOpen(true)}
              data-testid="button-open-custom-domain"
            >
              <Globe className="h-4 w-4 mr-1.5" />
              Custom domain
              {brandDomain ? (
                brandDomainVerified ? (
                  <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600">
                    <CheckCircle2 className="h-3 w-3" />
                    <span className="truncate max-w-[140px]">{brandDomain.hostname}</span>
                  </span>
                ) : brandDomain.status === "failed" ? (
                  <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-medium text-destructive">
                    <AlertTriangle className="h-3 w-3" /> Action needed
                  </span>
                ) : (
                  <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-medium text-amber-600">
                    <Clock className="h-3 w-3" /> Pending
                  </span>
                )
              ) : null}
            </Button>
          )}
          {showHomeView && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setMetaPixelModalOpen(true)}
              data-testid="button-open-meta-pixel"
            >
              <Radar className="h-4 w-4 mr-1.5" />
              Meta Pixel
              {savedMetaPixelIdValid && (
                <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600">
                  <CheckCircle2 className="h-3 w-3" />
                  <span className="truncate max-w-[140px]">{savedMetaPixelId}</span>
                </span>
              )}
            </Button>
          )}
          {showPreviewSurface && (
            <Button
              variant="outline"
              size="sm"
              onClick={goHome}
              disabled={studioDesignRecoveryActive}
              data-testid="button-lp-express-home"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              Back to all pages
            </Button>
          )}
        </div>
      </div>

      {showHomeView && surface === "lp-express-2" && (
        <div role="group" aria-label="Page type" className="inline-flex gap-1 rounded-lg border p-1">
          <Button size="sm" variant={pageType === "lp" ? "secondary" : "ghost"} aria-label="Landing pages" aria-pressed={pageType === "lp"} onClick={() => selectPageType("lp")}>LP</Button>
          <Button size="sm" variant={pageType === "hp" ? "secondary" : "ghost"} aria-label="Home pages" aria-pressed={pageType === "hp"} onClick={() => selectPageType("hp")}>HP</Button>
        </div>
      )}
      {isLoadingAny ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </div>
          <Skeleton className="h-64" />
        </div>
      ) : (
        <>
          <LpInputsSection>
          {homeOverview || emptyResource ? (
            <>
          {smbHomeEligibilityPending ? (
            <div role="status" className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="smb-home-access-check-loading">
              <Loader2 className="h-4 w-4 animate-spin" />
              Checking Home page availability…
            </div>
          ) : homeOverview || (surface === "lp-express-2" && smbHomeCapabilityQuery.isError) ? (
            <div className="mt-5 space-y-3">
              {lpDesignSystemPicker}
              {smbHomeGenerator}
              {!smbHomeEligible && !smbHomeCapabilityQuery.isError && <p className="text-sm text-muted-foreground">Home page creation is not available for this brand. You can still manage saved Home pages below.</p>}
            </div>
          ) : (
            <EmptyResource brandId={brandId!} resource={emptyResource!} />
          )}
            </>
          ) : null}
          {showStandardInputs && studioDesign?.rendererTemplateKey !== "smb-home" && (showPreviewSurface && !inputsExpanded ? (
            <Card className="p-3 flex items-center gap-3 flex-wrap border-border/60" data-testid="card-inputs-collapsed">
              <div className="h-8 w-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0 ring-1 ring-primary/20">
                <Wand2 className="h-3.5 w-3.5" />
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs min-w-0 flex-1">
                {selectedPersona && (
                  <span className="flex items-center gap-1.5 min-w-0">
                    <Users className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="truncate font-medium">{selectedPersona.name}</span>
                  </span>
                )}
                {selectedProduct && (
                  <span className="flex items-center gap-1.5 min-w-0">
                    <Package className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="truncate font-medium">{selectedProduct.name}</span>
                  </span>
                )}
                {selectedAngle && (
                  <span className="flex items-center gap-1.5 min-w-0">
                    <Sparkles className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="truncate font-medium">{selectedAngle.name}</span>
                  </span>
                )}
                {studioPageDesignStage && (
                  <span
                    className="flex items-center gap-1.5 text-primary font-medium"
                    data-testid="text-lp-studio-page-design-status"
                  >
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {studioPageDesignStage}
                  </span>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-xs gap-1"
                onClick={() => setInputsExpanded(true)}
                data-testid="button-edit-inputs"
              >
                <Pencil className="h-3 w-3" />
                Change inputs
              </Button>
            </Card>
          ) : (
          <Card className="relative overflow-hidden p-6 space-y-5 border-border/60 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.12)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_8px_24px_-12px_rgba(0,0,0,0.6)] bg-gradient-to-b from-card to-muted/30 dark:from-card dark:to-background/40">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-primary/[0.06] blur-3xl"
            />
            <div className="relative flex items-center gap-3">
              <div className="relative h-10 w-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary flex items-center justify-center shrink-0 ring-1 ring-primary/20 shadow-sm">
                <Wand2 className="h-4 w-4" />
              </div>
              <div>
                <h2 className="text-base font-semibold tracking-tight">Set your inputs</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Persona, product, and angle are required
                </p>
              </div>
            </div>

            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-3">
              <div
                className={
                  showMissingHints && !personaId
                    ? "rounded-xl ring-2 ring-amber-400/70 ring-offset-1 ring-offset-background"
                    : undefined
                }
              >
                <PickerSelect
                  icon={<Users className="h-4 w-4" />}
                  label="Persona"
                  placeholder="Choose persona"
                  value={personaId}
                  onChange={setPersonaId}
                  options={personas.map((p) => ({
                    value: p.id,
                    label: p.name,
                    imageUrl: (p as { imageUrl?: string | null }).imageUrl ?? null,
                  }))}
                  testId="select-persona"
                  searchable
                  disabled={isRunning}
                />
              </div>
              <div
                className={
                  showMissingHints && !productId
                    ? "rounded-xl ring-2 ring-amber-400/70 ring-offset-1 ring-offset-background"
                    : undefined
                }
              >
                <PickerSelect
                  icon={<Package className="h-4 w-4" />}
                  label="Product"
                  placeholder="Choose product"
                  value={productId}
                  onChange={setProductId}
                  options={products.map((p) => {
                    const media = (p as { mediaJson?: string[] | null }).mediaJson;
                    const first = Array.isArray(media) && media.length > 0 ? media[0] : null;
                    return { value: p.id, label: p.name, imageUrl: first };
                  })}
                  testId="select-product"
                  searchable
                  disabled={isRunning}
                />
              </div>
              <div
                className={[
                  "flex flex-col gap-1.5",
                  showMissingHints && !hasAngle
                    ? "rounded-xl ring-2 ring-amber-400/70 ring-offset-1 ring-offset-background"
                    : "",
                ].join(" ")}
              >
                {/* Same segmented Saved/Custom toggle as Ads & Email Express */}
                <div className="inline-flex items-center self-start rounded-lg border border-border/60 bg-muted/40 p-0.5">
                  <button
                    type="button"
                    onClick={() => setCustomAngleMode(false)}
                    disabled={isRunning}
                    className={[
                      "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
                      !customAngleMode
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground",
                    ].join(" ")}
                    data-testid="button-angle-mode-saved"
                  >
                    <BookOpen className="h-3.5 w-3.5" /> Saved angle
                  </button>
                  <button
                    type="button"
                    onClick={() => setCustomAngleMode(true)}
                    disabled={isRunning}
                    className={[
                      "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
                      customAngleMode
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground",
                    ].join(" ")}
                    data-testid="button-angle-mode-custom"
                  >
                    <Sparkles className="h-3.5 w-3.5" /> Custom concept angle
                  </button>
                </div>

                {customAngleMode ? (
                  <div>
                    <Label htmlFor="lp-express-concept" className="text-xs font-medium">
                      Custom concept angle
                    </Label>
                    <p className="text-[11px] text-muted-foreground mb-1.5">
                      Describe your narrative concept. We'll use it as the angle for this landing page.
                    </p>
                    <Textarea
                      id="lp-express-concept"
                      placeholder="e.g. The 2pm energy crash isn't a willpower problem — it's a fuel problem."
                      value={customAngleText}
                      onChange={(e) => setCustomAngleText(e.target.value)}
                      rows={4}
                      maxLength={2000}
                      className="resize-none text-sm"
                      disabled={isRunning}
                      data-testid="input-concept"
                    />
                  </div>
                ) : (
                  <>
                    <PickerSelect
                      icon={<Sparkles className="h-4 w-4" />}
                      label="Narrative Angle"
                      placeholder={
                        !personaId || !productId
                          ? "Pick persona & product first"
                          : availableAngles.length === 0
                            ? "No saved angles — use a custom concept angle"
                            : "Choose narrative angle"
                      }
                      value={angleId}
                      onChange={setAngleId}
                      groups={angleGroups}
                      testId="select-angle"
                      searchable
                      wrapDescription
                      disabled={isRunning || !personaId || !productId || availableAngles.length === 0}
                    />
                  </>
                )}
              </div>
              <PickerSelect
                icon={<Tag className="h-4 w-4" />}
                label="Sale / Discount"
                placeholder="No discount"
                value={saleId === "" ? "__none__" : saleId}
                onChange={setSaleId}
                options={[
                  { value: "__none__", label: "No discount", description: "Show full price — no sale applied" },
                  ...sales.map((s) => ({
                    value: s.id,
                    label: s.status === "active" ? `${s.name} · Active` : s.name,
                    description: formatSaleSummary(s),
                  })),
                ]}
                testId="select-sale"
                wrapDescription
                disabled={isRunning}
              />
            </div>

            <div className="relative flex items-center gap-2 pt-4 border-t border-dashed border-border/50 flex-wrap">
              <Wand2 className="h-3 w-3 text-muted-foreground/70" />
              <span className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground/70">
                Template
              </span>
              {landingTemplateTypes.length === 0 ? (
                <span
                  className="text-xs text-muted-foreground/70 italic"
                  data-testid="text-no-templates"
                >
                  No templates available for this brand.
                </span>
              ) : (
                <Select
                  value={templateTypeKey}
                  onValueChange={setTemplateTypeKey}
                  disabled={isRunning}
                >
                  <SelectTrigger className="h-7 text-xs w-52 border-transparent bg-transparent hover:bg-muted/50 focus:bg-background shadow-none px-2" data-testid="select-template-type">
                    <SelectValue placeholder="Select template…" />
                  </SelectTrigger>
                  <SelectContent>
                    {landingTemplateTypes.map((t) => (
                      <SelectItem key={t.id} value={t.key} data-testid={`select-template-type-${t.key}`}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
               )}
               {lpDesignSystemPicker}
              <span className="text-[11px] text-muted-foreground/60 ml-auto">
                More page types coming soon.
              </span>
            </div>
            <div className="relative flex items-center gap-2 flex-wrap">
              <Folder className="h-3 w-3 text-muted-foreground/70" />
              <span className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground/70">
                Project
              </span>
              <Select
                value={projectId === "" ? "none" : projectId}
                onValueChange={(v) => {
                  if (v === "__new") { setNewProjectOpen(true); return; }
                  setProjectId(v === "none" ? "" : v);
                }}
                disabled={isRunning}
              >
                <SelectTrigger className="h-7 text-xs w-52 border-transparent bg-transparent hover:bg-muted/50 focus:bg-background shadow-none px-2" data-testid="select-project">
                  <SelectValue placeholder="(no project)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">(no project)</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                  <SelectItem value="__new" data-testid="select-project-new">
                    + New project…
                  </SelectItem>
                </SelectContent>
              </Select>
              <span className="text-[11px] text-muted-foreground/60 ml-auto">
                Saved pages from this run will be filed here.
              </span>
            </div>
          </Card>
          ))}

          {showStandardInputs && studioDesign?.rendererTemplateKey !== "smb-home" && (inputsExpanded || !showPreviewSurface) && (
            <div className="flex items-center justify-end gap-3 flex-wrap">
              {!canGenerate && !isRunning && missingInputs.length > 0 && (
                <span
                  className={[
                    "text-xs flex items-center gap-1.5",
                    showMissingHints ? "text-amber-600 font-medium" : "text-muted-foreground",
                  ].join(" ")}
                  data-testid="text-generate-missing-hint"
                >
                  <Sparkles className="h-3.5 w-3.5 shrink-0" />
                  {missingInputs.length === 1
                    ? `Almost there — just ${missingInputs[0].label}.`
                    : `To get started, ${missingInputs
                        .map((m) => m.label)
                        .join(", ")
                        .replace(/, ([^,]*)$/, " and $1")}.`}
                </span>
              )}
              {brand?.name && (
                <span className="text-xs text-muted-foreground">
                  Brand: <span className="font-medium text-foreground">{brand.name}</span>
                </span>
              )}
              <Button
                size="lg"
                onClick={() => {
                  if (!canGenerate) {
                    // Instead of a silently-dead button, light up the checklist
                    // and the incomplete pickers with friendly guidance.
                    setShowMissingHints(true);
                    return;
                  }
                  setShowMissingHints(false);
                  generateMutation.mutate();
                }}
                disabled={
                  isRunning ||
                  (selectedTemplateUsesLpDesignSystem &&
                    (isDesignSystemSelectionPending ||
                      hasFailedDesignSystemSelection ||
                      hasUnavailablePersistedLpDesignSystem))
                }
                aria-disabled={
                  !canGenerate ||
                  isRunning ||
                  (selectedTemplateUsesLpDesignSystem && isDesignSystemSelectionPending)
                }
                data-testid="button-generate"
                className={["gap-2", !canGenerate && !isRunning ? "opacity-60" : ""].join(" ")}
              >
                {isRunning ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {studioPageDesignStage || "Starting…"}
                  </>
                ) : (
                  <>
                    {hero ? "Regenerate landing page" : "Generate landing page"}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          )}
          </LpInputsSection>

          {showHomeView && (
            <LpDashboardSection>
              {/* Stat cards: Total / Live / Drafts */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Card className="border-border/40 p-4" data-testid="stat-total-pages">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <LayoutList className="h-3.5 w-3.5" /> Total pages
                  </div>
                  <div className="text-2xl font-bold mt-1" data-testid="text-stat-total">
                    {mergedPages.length}
                  </div>
                </Card>
                <Card className="border-border/40 p-4" data-testid="stat-live-pages">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <CircleDot className="h-3.5 w-3.5 text-emerald-600" /> Live
                  </div>
                  <div className="text-2xl font-bold mt-1 text-emerald-600" data-testid="text-stat-live">
                    {pagesLiveCount}
                  </div>
                </Card>
                <Card className="border-border/40 p-4" data-testid="stat-draft-pages">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <Circle className="h-3.5 w-3.5" /> Drafts
                  </div>
                  <div className="text-2xl font-bold mt-1" data-testid="text-stat-drafts">
                    {pagesDraftCount}
                  </div>
                </Card>
              </div>

              {/* Toolbar: search + All/Live/Draft filter + sort */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[180px]">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  <Input
                    value={pagesSearch}
                    onChange={(e) => setPagesSearch(e.target.value)}
                    placeholder="Search pages…"
                    className="pl-8 h-9"
                    data-testid="input-search-pages"
                  />
                </div>
                <div className="flex items-center gap-1 rounded-md border border-border/40 p-0.5">
                  {(["all", "live", "draft"] as const).map((f) => (
                    <Button
                      key={f}
                      type="button"
                      variant={pagesFilter === f ? "secondary" : "ghost"}
                      size="sm"
                      className="h-7 px-3 text-xs capitalize"
                      onClick={() => setPagesFilter(f)}
                      data-testid={`button-filter-${f}`}
                    >
                      {f}
                    </Button>
                  ))}
                </div>
                <Select value={pagesSort} onValueChange={(v) => setPagesSort(v as typeof pagesSort)}>
                  <SelectTrigger className="h-9 w-[150px]" data-testid="select-sort-pages">
                    <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="name">Name A–Z</SelectItem>
                    <SelectItem value="status">Status</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Table-style panel */}
              <Card className="border-border/40 overflow-hidden">
                {Boolean(runsError || publishedPagesError) && (
                  <div role="alert" className="p-4 text-sm text-destructive">
                    Some saved pages could not be loaded. This library may be incomplete.
                    <Button size="sm" variant="outline" className="ml-2" onClick={() => { void refetchRuns(); void refetchPublishedPages(); }}>Retry saved pages</Button>
                  </div>
                )}
                {(loadingRuns || loadingPublishedPages) && mergedPages.length === 0 ? (
                  <div className="p-4 space-y-2">
                    <Skeleton className="h-14" />
                    <Skeleton className="h-14" />
                    <Skeleton className="h-14" />
                  </div>
                ) : visiblePages.length === 0 && !(runsError || publishedPagesError) ? (
                  <div
                    className="flex flex-col items-center justify-center gap-2 py-14 text-center"
                    data-testid="empty-lp-pages"
                  >
                    <div className="rounded-full bg-muted p-3">
                      <FileText className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <p className="text-sm font-medium">No {homeOverview ? "home" : "landing"} pages found</p>
                    <p className="text-xs text-muted-foreground max-w-xs">
                      {mergedPages.length === 0
                        ? `Your saved ${homeOverview ? "home" : "landing"} pages will appear here.`
                        : "No pages match your search or filter. Try clearing them."}
                    </p>
                  </div>
                ) : (
                  <div>
                    {/* Column headers (desktop only) */}
                    <div className="hidden md:grid grid-cols-[minmax(0,1.4fr)_110px_110px_minmax(280px,auto)] gap-4 px-4 py-2.5 border-b border-border/40 bg-muted/30 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      <span>Page</span>
                      <span>Status</span>
                      <span>Created</span>
                      <span className="text-right">Actions</span>
                    </div>

                    <div className="divide-y divide-border/40">
                      {pagedPages.map((row) => {
                        const pub = row.published;
                        const live = row.publishStatus === "published";
                        const unpub = row.publishStatus === "unpublished";
                        const busy =
                          !!pub &&
                          togglePublishedMutation.isPending &&
                          togglePublishedMutation.variables?.id === pub.id;
                        const deleting =
                          deleteRunMutation.isPending &&
                          (row.runId
                            ? deleteRunMutation.variables?.runId === row.runId
                            : deleteRunMutation.variables?.publishedId ===
                              row.published?.id);
                        const publishing =
                          publishFromListMutation.isPending &&
                          !!row.pageId &&
                          publishFromListMutation.variables === row.pageId;
                        const openRun = () => {
                          navigationEpochRef.current += 1;
                          navigate(`${surfacePath}?runId=${row.runId}`);
                        };

                        return (
                          <div
                            key={row.key}
                            className={`grid grid-cols-1 md:grid-cols-[minmax(0,1.4fr)_110px_110px_minmax(280px,auto)] gap-3 md:gap-4 px-4 py-3 items-start ${
                              row.runId
                                ? "cursor-pointer hover:bg-muted/40 transition-colors"
                                : ""
                            }`}
                            data-testid={`row-lp-page-${row.key}`}
                            {...(row.runId
                              ? {
                                  role: "button",
                                  tabIndex: 0,
                                  onClick: openRun,
                                  onKeyDown: (e: any) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      openRun();
                                    }
                                  },
                                }
                              : {})}
                          >
                            {/* Page column */}
                            <div className="min-w-0">
                              <div
                                className="font-semibold text-sm truncate"
                                data-testid={row.runId ? `link-open-run-${row.runId}` : undefined}
                              >
                                {row.productName}
                              </div>
                              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                                <span
                                  className="inline-flex items-center gap-1 max-w-full text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 font-medium"
                                  data-testid={`badge-template-type-${row.key}`}
                                >
                                  <LayoutTemplate className="h-3 w-3 shrink-0" />
                                  <span className="truncate">{templateTypeLabel(row.templateType)}</span>
                                </span>
                              </div>
                              {(row.angleName || row.personaName) && (
                                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                                  {row.angleName && (
                                    <span className="inline-flex items-center gap-1 max-w-full text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border/40">
                                      <Sparkles className="h-3 w-3 shrink-0" />
                                      <span className="truncate">{row.angleName}</span>
                                    </span>
                                  )}
                                  {row.personaName && (
                                    <span className="inline-flex items-center gap-1 max-w-full text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border/40">
                                      <Users className="h-3 w-3 shrink-0" />
                                      <span className="truncate">{row.personaName}</span>
                                    </span>
                                  )}
                                </div>
                              )}
                              {pub && (
                                <div className="mt-1.5 text-[11px]">
                                  {live ? (
                                    <a
                                      href={lpExpressPublicUrl({
                                        origin: window.location.origin,
                                        slug: pub.slug,
                                        rowBrandSlug: pub.brandSlug,
                                        fallbackBrandSlug: previewBrandSlug,
                                      })}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="inline-flex items-center gap-1 text-primary hover:underline truncate max-w-full"
                                      data-testid={`link-published-page-${pub.id}`}
                                    >
                                      <ExternalLink className="h-3 w-3 shrink-0" />
                                      <span className="truncate">
                                        /p/{pub.brandSlug || previewBrandSlug}/{pub.slug}
                                      </span>
                                    </a>
                                  ) : (
                                    <span className="text-muted-foreground truncate">
                                      /p/{pub.brandSlug || previewBrandSlug}/{pub.slug}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Status column */}
                            <div data-testid={`status-lp-page-${row.key}`}>
                              {live ? (
                                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  <CircleDot className="h-3 w-3" /> Live
                                </span>
                              ) : unpub ? (
                                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                  <EyeOff className="h-3 w-3" /> Unpublished
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border/40">
                                  <Circle className="h-3 w-3" /> Draft
                                </span>
                              )}
                            </div>

                            {/* Created column */}
                            <div
                              className="text-xs text-muted-foreground whitespace-nowrap"
                              data-testid={`text-page-date-${row.key}`}
                            >
                              {row.createdAt
                                ? new Date(row.createdAt).toLocaleDateString(undefined, {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })
                                : ""}
                            </div>

                            {/* Actions column */}
                            <div
                              className="flex flex-col items-start md:items-end gap-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex flex-wrap items-center gap-1.5">
                                {row.runId && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 px-2 text-xs"
                                    onClick={openRun}
                                    data-testid={`button-edit-page-${row.key}`}
                                  >
                                    <Pencil className="h-3 w-3 mr-1" /> Edit
                                  </Button>
                                )}

                                {/* Draft: primary Publish — publishes directly
                                    from the list when the run has a saved page;
                                    falls back to opening the editor otherwise. */}
                                {!pub && row.runId && (
                                  <Button
                                    size="sm"
                                    className="h-7 px-2 text-xs"
                                    disabled={publishing}
                                    onClick={() => {
                                      if (row.pageId) {
                                        publishFromListMutation.mutate(row.pageId);
                                      } else {
                                        openRun();
                                      }
                                    }}
                                    data-testid={`button-publish-draft-${row.runId}`}
                                  >
                                    {publishing ? (
                                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                    ) : (
                                      <Globe className="h-3 w-3 mr-1" />
                                    )}
                                    Publish
                                  </Button>
                                )}

                                {/* Live / Unpublished: copy link + unpublish/republish toggle */}
                                {pub && (
                                  <>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      className="h-7 w-7"
                                      title="Copy link"
                                      onClick={() =>
                                        copyPublishedLink(
                                          pub.brandSlug || previewBrandSlug,
                                          pub.slug,
                                        )
                                      }
                                      data-testid={`button-copy-published-${pub.id}`}
                                    >
                                      <Copy className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-7 px-2 text-xs"
                                      disabled={busy}
                                      title={live ? "Unpublish" : "Republish"}
                                      onClick={() =>
                                        togglePublishedMutation.mutate({
                                          id: pub.id,
                                          action: live ? "unpublish" : "republish",
                                        })
                                      }
                                      data-testid={`button-toggle-published-${pub.id}`}
                                    >
                                      {busy ? (
                                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                      ) : live ? (
                                        <EyeOff className="h-3 w-3 mr-1" />
                                      ) : (
                                        <Globe className="h-3 w-3 mr-1" />
                                      )}
                                      {live ? "Unpublish" : "Republish"}
                                    </Button>
                                  </>
                                )}

                                {/* Delete — works for run-backed rows AND
                                    orphaned published-only rows. When the row is
                                    live or unpublished, deleting also takes the
                                    public page offline. */}
                                {(row.runId || row.published) && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                    disabled={deleting}
                                    title="Delete"
                                    onClick={() => {
                                      const offlineWarning =
                                        live || unpub
                                          ? " Its public page will be taken offline and can't be restored."
                                          : "";
                                      if (
                                        window.confirm(
                                          `Delete this page? This can't be undone.${offlineWarning}`,
                                        )
                                      ) {
                                        deleteRunMutation.mutate({
                                          runId: row.runId,
                                          publishedId: row.published?.id ?? null,
                                        });
                                      }
                                    }}
                                    data-testid={`button-delete-draft-${row.runId || row.published?.id}`}
                                  >
                                    {deleting ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-3.5 w-3.5" />
                                    )}
                                  </Button>
                                )}
                              </div>

                              {/* Custom-domain toggle (connected domain + live page only;
                                  shown while pending too so pages can be opted in early) */}
                              {pub && live && brandDomain && pub.pageId && (
                                <div className="flex items-center gap-1.5">
                                  <Globe className="h-3 w-3 text-muted-foreground shrink-0" />
                                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                                    Custom domain
                                  </span>
                                  <Switch
                                    className="scale-90"
                                    checked={!!pub.customDomainEnabled}
                                    disabled={toggleCustomDomainEnabledMutation.isPending}
                                    onCheckedChange={(checked) =>
                                      toggleCustomDomainEnabledMutation.mutate({
                                        pageId: pub.pageId!,
                                        enabled: checked,
                                      })
                                    }
                                    data-testid={`switch-row-custom-domain-${pub.id}`}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Pagination footer (only when more than one page) */}
                    {visiblePages.length > PAGES_PER_PAGE && (
                      <div
                        className="flex items-center justify-between gap-3 px-4 py-3 border-t border-border/40 bg-muted/20"
                        data-testid="pagination-lp-pages"
                      >
                        <span className="text-xs text-muted-foreground" data-testid="text-pagination-range">
                          Showing {(pagesPage - 1) * PAGES_PER_PAGE + 1}–
                          {Math.min(pagesPage * PAGES_PER_PAGE, visiblePages.length)} of{" "}
                          {visiblePages.length}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            disabled={pagesPage <= 1}
                            onClick={() => setPagesPage((p) => Math.max(1, p - 1))}
                            data-testid="button-pages-prev"
                          >
                            <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Prev
                          </Button>
                          <span className="text-xs text-muted-foreground tabular-nums px-1" data-testid="text-pagination-page">
                            {pagesPage} / {pagesPageCount}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            disabled={pagesPage >= pagesPageCount}
                            onClick={() => setPagesPage((p) => Math.min(pagesPageCount, p + 1))}
                            data-testid="button-pages-next"
                          >
                            Next <ChevronRight className="h-3.5 w-3.5 ml-1" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>

            </LpDashboardSection>
          )}

          {/* Brand-level Meta Pixel settings modal — opened from the home-view
              header (next to Leads / Custom domain). The ID is entered once for
              the whole brand; each page then opts in from its own publish bar.
              The resolved state is frozen onto the published page at publish
              time, so changes surface as "Unpublished changes". */}
          <Dialog open={metaPixelModalOpen} onOpenChange={setMetaPixelModalOpen}>
            <DialogContent className="sm:max-w-md" data-testid="modal-meta-pixel">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Radar className="h-5 w-5 text-primary" />
                  Meta Pixel
                </DialogTitle>
                <DialogDescription>
                  Saved once for this brand — turn it on per page from each page's publish bar.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2" data-testid="card-lp-express-meta-pixel-brand">
                <div className="flex items-center gap-2">
                  <Input
                    value={metaPixelIdDraft}
                    onChange={(e) => setMetaPixelIdInput(e.target.value)}
                    placeholder="e.g. 1234567890123456"
                    inputMode="numeric"
                    className="h-9 text-sm"
                    data-testid="input-meta-pixel-id"
                  />
                  <Button
                    size="sm"
                    onClick={() => saveMetaPixelIdMutation.mutate(metaPixelIdDraftTrimmed)}
                    disabled={
                      saveMetaPixelIdMutation.isPending ||
                      metaPixelIdInput === null ||
                      !metaPixelIdDraftValid ||
                      metaPixelIdDraftTrimmed === savedMetaPixelId
                    }
                    data-testid="button-save-meta-pixel-id"
                  >
                    {saveMetaPixelIdMutation.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Check className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Save
                  </Button>
                </div>
                {!metaPixelIdDraftValid ? (
                  <p
                    className="text-[11px] text-destructive"
                    data-testid="text-meta-pixel-id-error"
                  >
                    {META_PIXEL_ID_ERROR}
                  </p>
                ) : (
                  <p className="text-[11px] text-muted-foreground">
                    Find your Pixel ID in Meta Events Manager. After publishing a page with the pixel installed, verify events with the Meta Pixel Helper extension or the Test Events tab in Events Manager.
                  </p>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Mount the whole editor/preview subtree whenever the preview surface
              should show — this is a strict superset of (hero || quizLive ||
              quizGenerating) that also covers long-form generation-in-progress
              and, crucially, the heroError failure state, so the renderer's
              in-canvas error/retry UI always mounts instead of a blank pane. */}
          {showPreviewSurface && (
            <LpEditorShell>
          {/* Publish bar — full width, above the collapsible control rail so the
              status / version-history / Publish controls span the whole editor
              and stay reachable while editing. */}
          <LpPublishSettingsSection>
          {(hero || quizLive || listicleLive || advertorialLive || productHeroLive || problemSolutionLive || coldDripLive || coffeeShopLive || offerStackLive || temeculaPassportLive || papabearLabelLive) && pageId && (
            <Card
              className="border-border/40 sticky top-0 z-30 shadow-sm bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
              data-testid="card-lp-express-publish"
            >
              <div className="p-3">
                <div className="flex flex-wrap items-center gap-2.5">
                {/* Status indicator: Draft / Live / Unpublished */}
                {publishState?.status === "published" ? (
                  <span
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                    data-testid="status-publish"
                  >
                    <CircleDot className="h-3.5 w-3.5" /> Live
                  </span>
                ) : publishState?.status === "unpublished" ? (
                  <span
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
                    data-testid="status-publish"
                  >
                    <EyeOff className="h-3.5 w-3.5" /> Unpublished
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-muted text-muted-foreground border border-border/40"
                    data-testid="status-publish"
                  >
                    <Circle className="h-3.5 w-3.5" /> Draft
                  </span>
                )}

                {/* Stale-snapshot warning: live page was published before the
                    latest edits, so a republish is needed to push them live. */}
                {publishState?.status === "published" && publishState?.hasUnpublishedChanges && (
                  <span
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
                    data-testid="status-unpublished-changes"
                    title="You've made changes since this page was last published. Republish to update the live page."
                  >
                    <AlertTriangle className="h-3.5 w-3.5" /> Unpublished changes
                  </span>
                )}

                {/* Quick view of the live public link. Editing the slug lives in
                    the dedicated "Public URL" card in the rail (more discoverable). */}
                {lpExpressPublicLinkVisible(publishState?.status, publicUrl) && (
                  <a
                    href={publicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline truncate max-w-[280px]"
                    data-testid="link-public-url"
                  >
                    <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{publicUrl}</span>
                  </a>
                )}

                <div className="ml-auto flex items-center gap-2">
                  {/* Version history: undo / redo / open the History panel, plus a
                      Saving…/Saved indicator. Only shown once the page is saved. */}
                  {!!pageId && (
                    <div className="flex items-center gap-1.5 mr-1">
                      <span
                        className="text-xs text-muted-foreground hidden sm:inline-flex items-center gap-1"
                        data-testid="status-save"
                      >
                        {saveBusy ? (
                          <>
                            <Loader2 className="h-3 w-3 animate-spin" /> Saving…
                          </>
                        ) : (
                          <>
                            <Check className="h-3 w-3" /> Saved
                          </>
                        )}
                      </span>
                      {/* Undo/redo are de-emphasized (ghost, tucked) next to the
                          History button so version controls stay reachable
                          without crowding the slim bar. */}
                      <div className="flex items-center rounded-md border border-border/40">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground"
                          onClick={triggerUndo}
                          disabled={!snapshotNav.canUndo || snapshotBusy || studioRevisionBusy}
                          title="Undo (⌘Z)"
                          data-testid="button-undo"
                        >
                          <Undo2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground"
                          onClick={triggerRedo}
                          disabled={!snapshotNav.canRedo || snapshotBusy || studioRevisionBusy}
                          title="Redo (⌘⇧Z)"
                          data-testid="button-redo"
                        >
                          <Redo2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs text-muted-foreground"
                          onClick={() => setHistoryOpen(true)}
                          title="Version history"
                          data-testid="button-open-history"
                        >
                          <History className="h-3.5 w-3.5 mr-1.5" /> History
                        </Button>
                      </div>
                    </div>
                  )}
                  {lpExpressPublicLinkVisible(publishState?.status, publicUrl) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyPublicUrl}
                      data-testid="button-copy-public-url"
                    >
                      <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy link
                    </Button>
                  )}
                  {publishState?.status === "published" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUnpublishConfirmOpen(true)}
                      disabled={unpublishMutation.isPending}
                      data-testid="button-unpublish"
                    >
                      {unpublishMutation.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <EyeOff className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      Unpublish
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={async () => {
                      if (checkoutUrlValidationError) {
                        toast({
                          title: "Fix the checkout link first",
                          description: checkoutUrlValidationError,
                          variant: "destructive",
                        });
                        return;
                      }
                      const ok = await flushCheckoutUrl();
                      if (!ok) return; // save failed/invalid — don't publish stale URL
                      // Wait for any in-flight content/section saves to commit so
                      // the publish snapshot includes the latest edits. If they
                      // don't settle in time, ABORT rather than snapshot stale
                      // data — the user can retry once saves finish.
                      const settled = await waitForPendingSaves();
                      if (!settled) {
                        toast({
                          title: "Still saving your edits",
                          description:
                            "Your latest changes are still saving. Please try Publish again in a moment.",
                          variant: "destructive",
                        });
                        return;
                      }
                      publishMutation.mutate();
                    }}
                    disabled={publishMutation.isPending || !!checkoutUrlValidationError}
                    data-testid="button-publish"
                  >
                    {publishMutation.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Globe className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    {publishState?.status === "published" ? "Republish" : "Publish"}
                  </Button>
                </div>
                </div>
              </div>
            </Card>
          )}
          </LpPublishSettingsSection>
          {/* Page layout — full width, above the rail and the preview so the
              move/hide/delete hint and the dynamic-content legend sit right
              above the sections they describe. */}
          {hero && (
            <div
              className="rounded-lg border border-border/40 p-3"
              data-testid="card-section-order"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <ListOrdered className="h-4 w-4 text-primary shrink-0" />
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold leading-tight">Page layout</h3>
                    <p className="text-xs text-muted-foreground">
                      Hover any section in the preview below to move, hide, or delete it. Changes save and preview instantly.
                    </p>
                  </div>
                </div>
                {sectionLayoutIsCustom && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs shrink-0"
                    onClick={resetSectionLayout}
                    disabled={saveSectionOrderMutation.isPending}
                    data-testid="button-reset-section-order"
                  >
                    <RotateCcw className="h-3 w-3 mr-1" />
                    Reset layout
                  </Button>
                )}
              </div>
              <div
                className="mt-2 flex items-start gap-2 text-[11px] text-muted-foreground"
                data-testid="legend-lp-express-preview"
              >
                <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-green-500 ring-1 ring-green-700/50 shrink-0" />
                <span>
                  A green dot marks content pulled in dynamically (live persona, product, and offer data). It's an editor-only guide and never appears on the published page.
                </span>
              </div>
            </div>
          )}
            <div className="flex flex-col lg:flex-row gap-6 items-start" data-testid="layout-lp-express-editor">
              {/* Section/theme control rail. Section add/reorder controls are
                  hero-only, but a quiz funnel still needs the rail for its URL,
                  checkout and theme controls — so show it for a live or
                  generating quiz too (not just section-based templates). */}
              {(hero || quizLive || quizGenerating || listicleLive || listicleGenerating || advertorialLive || advertorialGenerating || productHeroLive || productHeroGenerating || problemSolutionLive || problemSolutionGenerating || coldDripLive || coldDripGenerating || coffeeShopLive || coffeeShopGenerating || temeculaPassportLive || temeculaPassportGenerating || papabearLabelLive || papabearLabelGenerating) && (
              <div className={`w-full lg:shrink-0 lg:sticky lg:top-0 lg:self-start lg:max-h-[calc(100vh-3.5rem)] lg:overflow-y-auto space-y-3 lg:pr-1 ${railCollapsed ? "lg:w-auto" : "lg:w-[360px]"}`} data-testid="rail-lp-express-controls">
              {railCollapsed ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full lg:w-auto h-8 gap-1.5"
                  onClick={() => setRailCollapsed(false)}
                  data-testid="button-expand-rail"
                >
                  <PanelLeftOpen className="h-4 w-4" />
                  <span className="lg:hidden">Show controls</span>
                </Button>
              ) : (
              <>
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 gap-1.5 text-xs text-muted-foreground"
                  onClick={() => setRailCollapsed(true)}
                  data-testid="button-collapse-rail"
                >
                  <PanelLeftClose className="h-4 w-4" />
                  Hide controls
                </Button>
              </div>
              {pageId && (
              <>
            {/* URL + Checkout — compact, always-inline. Renaming the slug breaks
                any links already shared (confirmed via dialog before applying). */}
            <Card className="border-border/40" data-testid="card-lp-express-urls">
              <div className="p-4">
                <div className="space-y-4">
                    {/* --- Public URL group ----------------------------------- */}
                    <div className="space-y-2">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        URL
                      </span>

                      {/* When a custom domain is verified, it's the page's primary
                          live address — show it first so the user sees "where this
                          publishes". The /p/ URL below keeps working as a fallback. */}
                      {brandDomainVerified && publishState?.customDomainEnabled && publishState?.slug && (
                        <div
                          className="rounded-md border border-emerald-200 bg-emerald-50 p-2.5"
                          data-testid="banner-public-url-custom-domain"
                        >
                          <div className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-700">
                            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                            <span>This page is live on your custom domain</span>
                          </div>
                          <a
                            href={`https://${brandDomain.hostname}/${publishState.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1 inline-flex items-center gap-1.5 text-xs text-primary hover:underline break-all"
                            data-testid="link-public-url-custom-domain"
                          >
                            <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                            <span className="break-all">{brandDomain.hostname}/{publishState.slug}</span>
                          </a>
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            The /p/ link below still works too.
                          </p>
                        </div>
                      )}

                      {publishState?.slug ? (
                        // --- Already published: rename the live slug -----------
                        <>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground shrink-0 truncate max-w-[40%]">
                              /p/{previewBrandSlug}/
                            </span>
                            <Input
                              value={slugDraft}
                              onChange={(e) => setSlugDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  submitSlugRename();
                                }
                              }}
                              placeholder={publishState.slug}
                              className="h-9 text-sm"
                              data-testid="input-slug"
                            />
                            <Button
                              size="sm"
                              onClick={submitSlugRename}
                              disabled={
                                renameSlugMutation.isPending ||
                                !slugDraft ||
                                slugUnchanged ||
                                !slugDraftValid
                              }
                              data-testid="button-save-slug"
                            >
                              {renameSlugMutation.isPending ? (
                                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                              ) : (
                                <Check className="h-3.5 w-3.5 mr-1.5" />
                              )}
                              Save
                            </Button>
                          </div>
                          <p className="text-[11px] text-muted-foreground">
                            Auto-generated from your headline. Changing this breaks any links you've already shared.
                          </p>
                        </>
                      ) : (
                        // --- Not yet published: optional custom slug ----------
                        <>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground shrink-0 truncate max-w-[40%]">
                              /p/{previewBrandSlug}/
                            </span>
                            <Input
                              value={desiredSlugInput}
                              onChange={(e) => setDesiredSlugInput(e.target.value)}
                              placeholder="summer-sale"
                              className="h-9 text-sm"
                              data-testid="input-desired-slug"
                            />
                          </div>
                          <p className="text-[11px] text-muted-foreground">
                            Auto-generated from your headline — type above to customize.
                          </p>
                        </>
                      )}

                      {/* Custom domain — compact entry under URL. Connecting is
                          brand-level (opens the modal). Once verified, a per-page
                          toggle serves THIS page on the custom domain. */}
                      {publishState?.slug && (
                        brandDomain ? (
                          <div
                            className="flex items-center justify-between gap-3 rounded-md border border-border/40 bg-muted/30 px-2.5 py-2"
                            data-testid="card-lp-express-custom-domain"
                          >
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 text-[11px] font-medium text-foreground">
                                <Globe className="h-3 w-3 text-muted-foreground shrink-0" />
                                Serve on custom domain
                              </div>
                              {publishState?.customDomainEnabled ? (
                                brandDomainVerified ? (
                                  <a
                                    href={`https://${brandDomain.hostname}/${publishState.slug}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-0.5 inline-flex items-center gap-1.5 text-[11px] text-primary hover:underline break-all"
                                    data-testid="link-page-custom-domain"
                                  >
                                    <ExternalLink className="h-3 w-3 shrink-0" />
                                    <span className="break-all">{brandDomain.hostname}/{publishState.slug}</span>
                                  </a>
                                ) : (
                                  <p className="mt-0.5 text-[11px] text-amber-600 truncate" data-testid="text-page-custom-domain-pending">
                                    {brandDomain.hostname}/{publishState.slug} · goes live once your domain is verified
                                  </p>
                                )
                              ) : (
                                <p className="mt-0.5 text-[11px] text-muted-foreground truncate">
                                  {brandDomain.hostname} · off
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <Switch
                                checked={!!publishState?.customDomainEnabled}
                                disabled={!pageId || toggleCustomDomainEnabledMutation.isPending}
                                onCheckedChange={(checked) =>
                                  pageId &&
                                  toggleCustomDomainEnabledMutation.mutate({ pageId, enabled: checked })
                                }
                                data-testid="switch-page-custom-domain"
                              />
                              <button
                                type="button"
                                onClick={() => setCustomDomainModalOpen(true)}
                                className="text-muted-foreground hover:text-foreground p-1 -mr-1"
                                data-testid="button-manage-custom-domain-verified"
                                aria-label="Manage custom domain"
                                title="Manage custom domain"
                              >
                                <SlidersHorizontal className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setCustomDomainModalOpen(true)}
                            className="flex w-full items-center gap-1.5 text-[11px] text-primary hover:underline"
                            data-testid="button-manage-custom-domain"
                          >
                            <Globe className="h-3 w-3 shrink-0" />
                            <span>Connect a custom domain</span>
                            <ChevronRight className="h-3 w-3 shrink-0" />
                          </button>
                        )
                      )}
                    </div>

                    {/* --- Checkout URL group -------------------------------- */}
                    <div className="border-t border-border/40 pt-4">
                      {renderCheckoutUrlField("")}
                    </div>

                    {/* --- Meta Pixel group ---------------------------------- */}
                    {/* Per-page install toggle only. The brand-level Pixel ID
                        is entered once on the LP Express home screen; the
                        resolved state is frozen onto the published page at
                        publish time, so changes here surface as "Unpublished
                        changes". */}
                    <div
                      className="border-t border-border/40 pt-4 space-y-2"
                      data-testid="card-lp-express-meta-pixel"
                    >
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Meta Pixel
                      </span>
                      <div
                        className="flex items-center justify-between gap-3 rounded-md border border-border/40 bg-muted/30 px-2.5 py-2"
                        data-testid="card-meta-pixel-toggle"
                      >
                        <div className="min-w-0">
                          <div className="text-[11px] font-medium text-foreground">
                            Install Meta Pixel on this page
                          </div>
                          <p className="mt-0.5 text-[11px] text-muted-foreground">
                            {!savedMetaPixelIdValid
                              ? "Set a Meta Pixel ID first — add it on the LP Express home screen."
                              : publishState?.metaPixelEnabled
                                ? "Fires a PageView on the published page. Changes go live on the next publish."
                                : "Off — the published page won't include the pixel."}
                          </p>
                        </div>
                        {/* Disabled inputs swallow pointer events, so the
                            tooltip lives on a wrapping span. */}
                        <span
                          className="shrink-0"
                          title={!savedMetaPixelIdValid ? "Set a Meta Pixel ID first." : undefined}
                          data-testid="wrap-meta-pixel-toggle"
                        >
                          <Switch
                            checked={!!publishState?.metaPixelEnabled}
                            disabled={
                              !pageId ||
                              !savedMetaPixelIdValid ||
                              toggleMetaPixelEnabledMutation.isPending
                            }
                            onCheckedChange={(checked) =>
                              pageId &&
                              toggleMetaPixelEnabledMutation.mutate({ pageId, enabled: checked })
                            }
                            data-testid="switch-meta-pixel-enabled"
                          />
                        </span>
                      </div>
                      {metaPixelActive && (
                        <p
                          className="text-[11px] text-muted-foreground"
                          data-testid="text-meta-pixel-verify-hint"
                        >
                          After publishing, verify events with the Meta Pixel Helper browser extension or the Test Events tab in Events Manager.
                        </p>
                      )}
                    </div>
                  </div>
              </div>
            </Card>
            </>
          )}

          <AlertDialog open={unpublishConfirmOpen} onOpenChange={setUnpublishConfirmOpen}>
            <AlertDialogContent data-testid="dialog-unpublish-confirm">
              <AlertDialogHeader>
                <AlertDialogTitle>Unpublish this page?</AlertDialogTitle>
                <AlertDialogDescription>
                  The public link will stop working immediately and visitors will see a
                  "page not found" message. You can republish it again at any time.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="button-unpublish-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    unpublishMutation.mutate();
                  }}
                  disabled={unpublishMutation.isPending}
                  data-testid="button-unpublish-confirm"
                >
                  {unpublishMutation.isPending && (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  )}
                  Unpublish
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog open={slugConfirmOpen} onOpenChange={setSlugConfirmOpen}>
            <AlertDialogContent data-testid="dialog-slug-confirm">
              <AlertDialogHeader>
                <AlertDialogTitle>Change the public URL?</AlertDialogTitle>
                <AlertDialogDescription>
                  Your page will move to{" "}
                  <span className="font-medium text-foreground">/p/{previewBrandSlug}/{normalizedSlugDraft}</span>.
                  Any links you've already shared to the old URL will stop working and show a
                  "page not found" message. This can't be undone automatically.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="button-slug-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    renameSlugMutation.mutate(normalizedSlugDraft);
                  }}
                  disabled={renameSlugMutation.isPending}
                  data-testid="button-slug-confirm"
                >
                  {renameSlugMutation.isPending && (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  )}
                  Change URL
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Page theme — full theme controls (fonts, colors, button style,
              spacing), always expanded in the rail. Page layout moved above the
              preview where it's more discoverable next to the sections. */}
          {(hero || quizLive || listicleLive || advertorialLive || productHeroLive || problemSolutionLive || coldDripLive || coffeeShopLive || offerStackLive || temeculaPassportLive || papabearLabelLive) && (
            <LpThemePanel
              theme={theme}
              onSet={(patch) => applyContentOp({ kind: "setTheme", patch })}
              onReset={() => applyContentOp({ kind: "resetTheme" })}
            />
          )}
              </>
              )}
              </div>
              )}
              <div className="w-full lg:flex-1 lg:min-w-0 space-y-3" data-testid="col-lp-express-preview">
          {/* Viewport (Desktop/Mobile) toggle — blob templates (quiz/listicle)
              keep `hero` null, so gate on live blob content too, or those pages
              could never switch to the mobile iframe preview. */}
          {(hero || quizLive || listicleLive || advertorialLive || productHeroLive || problemSolutionLive || coldDripLive || coffeeShopLive || offerStackLive || temeculaPassportLive || papabearLabelLive) && (
            <div
              className="flex items-center justify-end"
              data-testid="toolbar-lp-express-viewport"
            >
              <div className="flex items-center gap-1 border border-border/40 rounded-lg p-0.5">
                <Button
                  variant={previewViewMode === "desktop" ? "secondary" : "ghost"}
                  size="sm"
                  className="h-7 px-2.5"
                  onClick={() => setPreviewViewMode("desktop")}
                  data-testid="button-preview-desktop"
                >
                  <Monitor className="h-3.5 w-3.5 mr-1.5" />
                  Desktop
                </Button>
                <Button
                  variant={previewViewMode === "mobile" ? "secondary" : "ghost"}
                  size="sm"
                  className="h-7 px-2.5"
                  onClick={() => setPreviewViewMode("mobile")}
                  data-testid="button-preview-mobile"
                >
                  <Smartphone className="h-3.5 w-3.5 mr-1.5" />
                  Mobile
                </Button>
              </div>
            </div>
          )}

          {/* Whole-page copy regenerate — builder chrome ABOVE the preview
              (never inside the page canvas) so it reads as an editor control,
              not page content. RULE (user preference, see replit.md): every
              blob-copy template's "Regenerate page" control renders here. Each
              template's copy is one blob so regeneration is all-or-nothing;
              its AI imagery is preserved server-side. */}
          <LpRegenerationControls>
           {pageId && surface === "lp-express-2" && studioDesign && productHeroLive &&
             studioPageDesignStatus?.status === "ready" && (
             <LpStudioRevisionCard
               status={studioRevisionStatus?.status}
               error={studioRevisionStatus?.error}
               conflictingBusy={
                 studioRevisionBusy ||
                 studioBulkRegeneration.copy?.status === "pending" ||
                 studioBulkRegeneration.copy?.status === "blocked" ||
                 studioBulkRegeneration.images?.status === "pending" ||
                 studioBulkRegeneration.images?.status === "blocked" ||
                 studioBulkRegeneration.section?.status === "pending" ||
                 studioBulkRegeneration.section?.status === "blocked" ||
                 regeneratingBlocks.size > 0 ||
                 productHeroImagesPending ||
                 saveContentMutation.isPending ||
                 regenerateStudioMutation.isPending
               }
               onSubmit={(instruction) => studioRevisionMutation.mutate(instruction)}
             />
           )}
          {pageId && surface === "lp-express-2" && studioDesign && productHeroLive && studioDesign.rendererTemplateKey !== "smb-home" && (
            <LpStudioRegenerateCard
              copyStatus={studioBulkRegeneration.copy?.status}
              copyError={studioBulkRegeneration.copy?.error}
              imageStatus={studioBulkRegeneration.images?.status}
              imageError={studioBulkRegeneration.images?.error}
              sectionBlockedError={
                studioBulkRegeneration.section?.status === "blocked"
                  ? studioBulkRegeneration.section?.error
                  : null
              }
              conflictingBusy={
                studioBulkRegeneration.section?.status === "blocked" ||
                studioRevisionBusy ||
                regeneratingBlocks.size > 0 ||
                productHeroImagesPending ||
                regenerateStudioMutation.isPending ||
                saveContentMutation.isPending
              }
              onRegenerateCopy={() => regenerateStudioMutation.mutate("copy")}
              onRegenerateImages={() => regenerateStudioMutation.mutate("images")}
            />
          )}
           {pageId &&
             studioDesign?.rendererTemplateKey === "smb-home" &&
             smbHomeRunReady &&
             !productHeroGenerating &&
             !studioDesignRecoveryActive &&
              studioBulkRegeneration.images?.status !== "pending" &&
              studioBulkRegeneration.images?.status !== "blocked" &&
             studioBulkRegeneration.copy?.status !== "pending" &&
             studioBulkRegeneration.copy?.status !== "blocked" &&
             studioBulkRegeneration.section?.status !== "pending" &&
             studioBulkRegeneration.section?.status !== "blocked" &&
             !studioRevisionBusy &&
             regeneratingBlocks.size === 0 &&
              !productHeroImagesPending &&
              !regenerateStudioMutation.isPending &&
              !studioRevisionMutation.isPending &&
             !saveContentMutation.isPending && (
            <div className="space-y-2">
              <Link href={`/brands/${brandId}/lp-express/preview?pageId=${encodeURIComponent(pageId)}`}>
                <Button variant="outline" size="sm">Preview saved page</Button>
              </Link>
              {studioBulkRegeneration.copy?.error && <p role="alert">{studioBulkRegeneration.copy.error}</p>}
              {studioBulkRegeneration.section?.error && <p role="alert">{studioBulkRegeneration.section.error}</p>}
               {studioDesign.catalogTemplateKey && (
                 <SmbHomeWholePageRegenerate
                   brandId={brandId!}
                   pageId={pageId}
                   catalogKey={studioDesign.catalogTemplateKey}
                   currentCopy={JSON.stringify(assembleLpSmbHomeContent({ ...smbPageMetadata, ...productHeroSections }))}
                   onAdmitted={(originBrandId, result) => {
                     if (originBrandId !== brandId || (!result.runId && !result.pageId)) return;
                     navigationEpochRef.current += 1;
                     const query = result.runId
                       ? `runId=${encodeURIComponent(result.runId)}`
                       : `pageId=${encodeURIComponent(result.pageId!)}`;
                     navigate(`${surfacePath}?${query}`);
                   }}
                 />
               )}
            </div>
          )}
          {pageId && (quizLive || listicleLive || advertorialLive || coffeeShopLive || temeculaPassportLive) && (
            <LpRegenerateCard
              idPrefix={
                quizLive ? "quiz" : listicleLive ? "listicle" : advertorialLive ? "advertorial" : coffeeShopLive ? "coffee-shop" : "temecula-passport"
              }
              busy={
                quizLive
                  ? quizRegenerating || quizGenerating || regenerateQuizMutation.isPending || saveContentMutation.isPending
                  : listicleLive
                    ? listicleRegenerating || listicleGenerating || regenerateListicleMutation.isPending || saveContentMutation.isPending
                    : advertorialLive
                      ? advertorialRegenerating || advertorialGenerating || regenerateAdvertorialMutation.isPending || saveContentMutation.isPending
                      : coffeeShopLive
                        ? coffeeShopRegenerating || coffeeShopGenerating || regenerateCoffeeShopMutation.isPending || saveContentMutation.isPending
                        : temeculaPassportRegenerating || temeculaPassportGenerating || regenerateTemeculaPassportMutation.isPending || saveContentMutation.isPending
              }
              onRegenerate={(feedback) =>
                quizLive
                  ? regenerateQuizMutation.mutate(feedback)
                  : listicleLive
                    ? regenerateListicleMutation.mutate(feedback)
                    : advertorialLive
                      ? regenerateAdvertorialMutation.mutate(feedback)
                      : coffeeShopLive
                        ? regenerateCoffeeShopMutation.mutate(feedback)
                        : regenerateTemeculaPassportMutation.mutate(feedback)
              }
            />
          )}
          </LpRegenerationControls>

          {/* Builder chrome (NOT part of the page canvas): Meta Pixel status.
              Indicator only — the preview is a React canvas and never loads
              fbevents.js, so no PageView can fire from the builder. */}
          {showPreviewSurface && metaPixelActive && (
            <div
              className="mb-2 flex items-center gap-1.5 text-[11px] text-muted-foreground"
              data-testid="badge-meta-pixel-active"
            >
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
              <span>
                Meta Pixel active on this page — it only fires on the published page, never in this preview.
              </span>
            </div>
          )}
          {showPreviewSurface && templateType === "smb-home" && heroError && (
            <Button
              variant="outline"
              disabled={productHeroGenerating}
              data-testid="button-smb-home-check-status"
              onClick={() => {
                if (!selectedRunId) return;
                queryClient.removeQueries({ queryKey: studioRecoveryStatusQueryKey(selectedRunId) });
                setHeroError(null);
                setProductHeroGenerating(true);
              }}
            >
              Check saved Home status
            </Button>
          )}
          {pageId && brandId &&
            (templateType === "smb-home" || studioDesign?.rendererTemplateKey === "smb-home") && (
            <>
              <SmbHomeImageFill
                key={`fill:${brandId}:${pageId}`}
                brandId={brandId}
                pageId={pageId}
                onBeforeFill={async () => {
                  const savesSettled = await waitForPendingSaves();
                  return savesSettled && !saveContentErrorRef.current;
                }}
              />
              <SmbHomeImageProgress
                key={`progress:${brandId}:${pageId}`}
                brandId={brandId}
                pageId={pageId}
                onRefresh={() => {
                  void (async () => {
                    const savesSettled = await waitForPendingSaves();
                    if (!savesSettled || saveContentErrorRef.current) {
                      toast({
                        title: "Image result is ready, but the editor was not refreshed",
                        description: "A page edit still needs attention. Save it before reloading the saved Home content.",
                        variant: "destructive",
                      });
                      return;
                    }
                    if (!selectedRunId) return;
                    queryClient.removeQueries({ queryKey: studioRecoveryStatusQueryKey(selectedRunId) });
                    setHeroError(null);
                    setProductHeroGenerating(true);
                  })();
                }}
              />
            </>
          )}
          {showPreviewSurface &&
            studioDesign?.rendererTemplateKey === "smb-home" &&
            productHeroGenerating && (
              <div
                className="space-y-5 rounded-xl border border-teal-900/10 bg-[#f6faf8] p-6"
                data-testid="smb-home-loading"
                role="status"
                aria-live="polite"
              >
                <div className="space-y-2">
                  <Skeleton className="h-3 w-36 bg-teal-900/10" />
                  <Skeleton className="h-12 w-4/5 bg-teal-900/10" />
                  <Skeleton className="h-5 w-3/5 bg-teal-900/10" />
                </div>
                <Skeleton className="h-10 w-40 rounded-lg bg-teal-900/10" />
                <p className="text-sm text-teal-950/70">Your saved Home page is still being completed. The previous copy stays out of the editor until this run is ready.</p>
              </div>
            )}
          {showPreviewSurface &&
            !(studioDesign?.rendererTemplateKey === "smb-home" && productHeroGenerating) && (
            <div
              className="border border-border/40 rounded-xl overflow-clip shadow-sm bg-white"
              data-testid="container-lp-express-renderer"
            >
              <LpPreviewSurface mode={previewViewMode}>
              <LpExpressRenderer
                brand={brand ?? null}
                rootProduct={selectedProduct}
                products={renderProducts}
                persona={selectedPersona}
                angle={selectedAngle}
                sale={selectedSale}
                ctaUrl={checkoutUrl}
                reviewsByProduct={reviewsByProduct}
                videosLoading={videosFetchActive}
                onRetrySlot={handleRetrySlot}
                onRetryHeroSlot={handleRetryHeroSlot}
                onRetryHero={
                  templateType === "smb-home" ||
                  studioDesignRecoveryAvailable
                    ? undefined
                    : () => generateMutation.mutate()
                }
                onRecoverStudioDesign={
                  studioDesignRecoveryAvailable
                    ? beginStudioDesignRecovery
                    : undefined
                }
                studioDesignRecoveryAvailable={studioDesignRecoveryAvailable}
                studioDesignRecoveryPending={studioDesignRecoveryActive}
                onRetryProductContext={handleRetryProductContext}
                editor={lpEditorControls}
                generatedContent={{
                  // Thread the loaded page's template variant through so the
                  // builder preview renders v2 (long-form-v2) instead of always
                  // falling back to v1. Falls back to the create-time selection.
                  templateType: templateType ?? templateTypeKey,
                  ...(studioDesign ? { studioDesign } : {}),
                  ...(studioDesign?.rendererTemplateKey === "smb-home" ? { ...smbPageMetadata, ...productHeroSections } : {}),
                  ...(studioPageDesignStatus?.status
                    ? { studioBrandDesignStatus: studioPageDesignStatus.status }
                    : {}),
                  // Coalesce null→undefined: this subtree now also mounts in the
                  // heroError state (hero===null) and the prop type is optional
                  // (undefined), not nullable. The renderer treats both as "no
                  // hero yet" and falls back to its loading/error UI.
                  hero: hero ?? undefined,
                  // Quiz Funnel: its entire content lives in this one blob; the
                  // renderer branches on templateType==="quiz" and ignores the
                  // sections model.
                  ...(quiz ? { quiz } : {}),
                  // Listicle: same one-blob model as the quiz; its AI image
                  // slots ride alongside in listicleImages (statuses included
                  // so per-slot spinners key off their OWN slot status).
                  ...(listicle ? { listicle } : {}),
                  ...(listicleImages && listicleImages.length > 0
                    ? { listicleImages }
                    : {}),
                  // Advertorial: same one-blob model; its AI image slots ride
                  // alongside in advertorialImages (statuses included so
                  // per-slot spinners key off their OWN slot status).
                  ...(advertorial ? { advertorial } : {}),
                  ...(advertorialImages && advertorialImages.length > 0
                    ? { advertorialImages }
                    : {}),
                  // Product Hero (PDP): unlike the one-blob templates above its
                  // copy lives under per-SECTION keys (phBuyBox, phTrustBar, …)
                  // that the renderer reads directly and dispatches through the
                  // sections model. Spread them straight onto generatedContent.
                  // Its AI lifestyle gallery slots ride alongside in
                  // productHeroImages (statuses included so per-slot spinners
                  // key off their OWN slot status).
                  ...(templateType === "product-hero" ? productHeroSections : {}),
                   ...(studioDesign
                     ? { lp2Regeneration: studioBulkRegeneration }
                     : {}),
                  ...(templateType === "product-hero" &&
                  productHeroImages &&
                  productHeroImages.length > 0
                    ? { productHeroImages }
                    : {}),
                  // Problem / Solution (PAS): same per-SECTION key model as the
                  // product-hero above (psHero, psProblem, …), plus the AI
                  // mechanism-diagram slot in problemSolutionImages.
                  ...(templateType === "problem-solution" ? problemSolutionSections : {}),
                  ...(templateType === "problem-solution" &&
                  problemSolutionImages &&
                  problemSolutionImages.length > 0
                    ? { problemSolutionImages }
                    : {}),
                  // Cold Drip Offer (Gaffle): one-blob copy model + AI image
                  // slots (ingredients / community wall / brand-story photos).
                  ...(coldDrip ? { coldDrip } : {}),
                  ...(templateType === "cold-drip" &&
                  coldDripImages &&
                  coldDripImages.length > 0
                    ? { coldDripImages }
                    : {}),
                  // Coffee Shop Lead Capture (Gaffle): one-blob copy model +
                  // AI image slots (hero photo / drink cards / pour bar).
                  ...(coffeeShop ? { coffeeShop } : {}),
                  ...(templateType === "coffee-shop" &&
                  coffeeShopImages &&
                  coffeeShopImages.length > 0
                    ? { coffeeShopImages }
                    : {}),
                  // Offer Stack: same per-SECTION key model (osHero, osStack, …)
                  // as product-hero / problem-solution, plus the AI
                  // intro-visual slot in offerStackImages.
                  ...(templateType === "offer-stack" ? offerStackSections : {}),
                  ...(templateType === "offer-stack" &&
                  offerStackImages &&
                  offerStackImages.length > 0
                    ? { offerStackImages }
                    : {}),
                  // Temecula Passport mystery-drop (Papabear): one-blob copy
                  // model + AI image slots.
                  ...(temeculaPassport ? { temeculaPassport } : {}),
                  ...(papabearLabel ? { papabearLabel } : {}),
                   ...(templateType === "papabear-label" &&
                   papabearLabelImages &&
                   papabearLabelImages.length > 0
                     ? {
                         papabearLabelImages: papabearLabelImages.map((slot) => ({
                           ...(slot?.key ? { key: slot.key } : {}),
                           ...(slot?.imageUrl ? { imageUrl: slot.imageUrl } : {}),
                           ...(slot?.altText ? { altText: slot.altText } : {}),
                           ...(slot?.imageStatus
                             ? { imageStatus: slot.imageStatus }
                             : {}),
                           imageLoading:
                             !slot?.imageUrl &&
                             (papabearLabelImagesMutation.isPending ||
                               slot?.imageStatus === "pending"),
                         })),
                       }
                     : {}),
                  ...(templateType === "temecula-passport" &&
                  temeculaPassportImages &&
                  temeculaPassportImages.length > 0
                    ? { temeculaPassportImages }
                    : {}),
                  // While the hero copy is still generating or has failed, render
                  // ONLY the hero section (in its loading/error state) so the rest
                  // of the page doesn't flash a wall of template placeholders.
                  sections: heroOnlyLayout
                    ? [{ id: "hero", type: "hero" as LpSectionType }]
                    : lpSectionsForRender,
                  ...(heroError ? { heroError } : {}),
                  ...(Object.keys(videoOverrides).length > 0 ? { videoOverrides } : {}),
                  ...(Object.keys(elementStates).length > 0 ? { elements: elementStates } : {}),
                  ...(Object.keys(theme).length > 0 ? { theme } : {}),
                  ...(Object.keys(elementStyles).length > 0 ? { styles: elementStyles } : {}),
                  ...(Object.keys(componentOrder).length > 0 ? { componentOrder } : {}),
                  ...(Object.keys(mediaOverrides).length > 0 ? { mediaOverrides } : {}),
                  ...(Object.keys(reviewOverrides).length > 0 ? { reviewOverrides } : {}),
                  ...(leadin ? { leadin } : {}),
                  ...(advisory ? { advisory } : {}),
                  ...(showcase?.headline || (showcase?.subheadlines?.length ?? 0) > 0 || showcaseMutation.isPending
                    ? { showcase: {
                        headline: showcase?.headline ?? "",
                        subheadlines: showcase?.subheadlines ?? [],
                      } }
                    : {}),
                  // Product Context: one block per belief card, rendered inside
                  // the "Real ingredients" BeliefSection. Each card has its own
                  // header/subtext + a background-generated image.
                  ...((productContext && productContext.length > 0) || productContextMutation.isPending
                    ? { productContext: (productContext ?? []).map((c) => ({
                        ...(c?.cardHeader ? { cardHeader: c.cardHeader } : {}),
                        ...(c?.cardSubtext ? { cardSubtext: c.cardSubtext } : {}),
                        ...(c?.imageUrl ? { imageUrl: c.imageUrl } : {}),
                        ...(c?.altText ? { altText: c.altText } : {}),
                        // Show the loading skeleton from the moment generation
                        // starts (copy phase) until the durable image is ready —
                        // never flash the static placeholder in between.
                        imageLoading:
                          !c?.imageUrl &&
                          (productContextMutation.isPending || c?.imageStatus === "pending"),
                      })) }
                    : {}),
                  ...(stations ? { stations } : {}),
                  ...(villain ? { villain } : {}),
                  ...(originCopy ? { originCopy } : {}),
                  // Origin images: one slot per Farmstand still image, keyed by
                  // slot. Each carries a per-slot loading flag so the renderer
                  // shows a spinner from generation start until the durable
                  // image is ready — never flashing the placeholder in between.
                  ...((originImages && originImages.length > 0) || originImagesMutation.isPending
                    ? { originImages: (originImages ?? []).map((s) => ({
                        ...(s?.key ? { key: s.key } : {}),
                        ...(s?.imageUrl ? { imageUrl: s.imageUrl } : {}),
                        ...(s?.altText ? { altText: s.altText } : {}),
                        ...(s?.imageStatus ? { imageStatus: s.imageStatus } : {}),
                        imageLoading:
                          !s?.imageUrl &&
                          (originImagesMutation.isPending || s?.imageStatus === "pending"),
                      })) }
                    : {}),
                  ...(quotes ? { quotes } : {}),
                  // Long-form section copy (8 workflows). Each falls back to the
                  // template's hardcoded copy when absent.
                  ...(timeline ? { timeline } : {}),
                  ...(membership ? { membership } : {}),
                  ...(alternatives ? { alternatives } : {}),
                  ...(philosophy ? { philosophy } : {}),
                  ...(differentiator ? { differentiator } : {}),
                  ...(faq ? { faq } : {}),
                  ...(reviews ? { reviews } : {}),
                  ...(finalCta ? { finalCta } : {}),
                  ...(footer ? { footer } : {}),
                  ...(proof ? { proof } : {}),
                  // Section images (3 workflows). Each slot carries a per-slot
                  // loading flag so the renderer shows a spinner from generation
                  // start until the durable image is ready — never flashing the
                  // static placeholder in between.
                  ...((alternativesImages && alternativesImages.length > 0) || alternativesImagesMutation.isPending
                    ? { alternativesImages: (alternativesImages ?? []).map((s) => ({
                        ...(s?.key ? { key: s.key } : {}),
                        ...(s?.imageUrl ? { imageUrl: s.imageUrl } : {}),
                        ...(s?.altText ? { altText: s.altText } : {}),
                        ...(s?.imageStatus ? { imageStatus: s.imageStatus } : {}),
                        imageLoading:
                          !s?.imageUrl &&
                          (alternativesImagesMutation.isPending || s?.imageStatus === "pending"),
                      })) }
                    : {}),
                  ...((philosophyImages && philosophyImages.length > 0) || philosophyImagesMutation.isPending
                    ? { philosophyImages: (philosophyImages ?? []).map((s) => ({
                        ...(s?.key ? { key: s.key } : {}),
                        ...(s?.imageUrl ? { imageUrl: s.imageUrl } : {}),
                        ...(s?.altText ? { altText: s.altText } : {}),
                        ...(s?.imageStatus ? { imageStatus: s.imageStatus } : {}),
                        imageLoading:
                          !s?.imageUrl &&
                          (philosophyImagesMutation.isPending || s?.imageStatus === "pending"),
                      })) }
                    : {}),
                  ...((proofImages && proofImages.length > 0) || proofImagesMutation.isPending
                    ? { proofImages: (proofImages ?? []).map((s) => ({
                        ...(s?.key ? { key: s.key } : {}),
                        ...(s?.imageUrl ? { imageUrl: s.imageUrl } : {}),
                        ...(s?.altText ? { altText: s.altText } : {}),
                        ...(s?.imageStatus ? { imageStatus: s.imageStatus } : {}),
                        imageLoading:
                          !s?.imageUrl &&
                          (proofImagesMutation.isPending || s?.imageStatus === "pending"),
                      })) }
                    : {}),
                  // Per-section "still generating" flags so the renderer shows
                  // spinners instead of static placeholder copy while the AI
                  // workflows are in flight.
                  loading: {
                    // Hero copy workflow in flight (initial generation) and no
                    // live headline yet → renderer shows the hero loading state.
                    hero: isRunning && !heroCopyLive,
                    advisory: advisoryMutation.isPending && !advisory,
                    stations: stationsMutation.isPending && !stations,
                    showcase: showcaseMutation.isPending && !showcase,
                    villain: villainMutation.isPending && !villain,
                    productContext:
                      productContextMutation.isPending && !productContext,
                    originCopy: originCopyMutation.isPending && !originCopy,
                    quotes: quotesMutation.isPending && !quotes,
                    timeline: timelineMutation.isPending && !timeline,
                    membership: membershipMutation.isPending && !membership,
                    alternatives: alternativesMutation.isPending && !alternatives,
                    philosophy: philosophyMutation.isPending && !philosophy,
                    differentiator: differentiatorMutation.isPending && !differentiator,
                    faq: faqMutation.isPending && !faq,
                    reviews: reviewsMutation.isPending && !reviews,
                    finalCta: finalCtaMutation.isPending && !finalCta,
                    proof: proofMutation.isPending && !proof,
                    alternativesImages: alternativesImagesMutation.isPending || alternativesImagesPending,
                    philosophyImages: philosophyImagesMutation.isPending || philosophyImagesPending,
                    proofImages: proofImagesMutation.isPending || proofImagesPending,
                    // Origin images are chained after origin copy and then
                    // background-polled, so the whole window (copy in flight →
                    // image request → poll) counts as loading. While true, the
                    // renderer shows spinners on every still-missing image slot
                    // instead of flashing the old static placeholders.
                    originImages:
                      originCopyMutation.isPending ||
                      originImagesMutation.isPending ||
                      originImagesPending,
                  },
                  ...(() => {
                    const TARGET_TOTAL = 8;
                    // All carousel slots are AI-generated — no fallback
                    // to raw product images. We only forward the server's
                    // heroImageUrls array (with nulls for still-rendering
                    // slots) so the renderer skeleton fills the gaps.
                    if (Array.isArray(heroImageUrls) && heroImageUrls.length > 0) {
                      return { heroImages: heroImageUrls };
                    }
                    if (heroImagesLoading) {
                      const placeholders = Array(TARGET_TOTAL).fill(null) as Array<string | null>;
                      return { heroImages: placeholders };
                    }
                    return {};
                  })(),
                  heroImagesLoading,
                  ...(Array.isArray(heroImageSlotStatuses)
                    ? { heroImageStatuses: heroImageSlotStatuses }
                    : {}),
                }}
              />
              </LpPreviewSurface>
              {(leadinMutation.isPending || advisoryMutation.isPending) && (
                <div className="flex items-center justify-center gap-3 py-4 text-xs text-muted-foreground border-t border-border/40 bg-muted/30">
                  {leadinMutation.isPending && !leadin && (
                    <span className="flex items-center gap-1.5">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Generating lead-in…
                    </span>
                  )}
                  {advisoryMutation.isPending && !advisory && (
                    <span className="flex items-center gap-1.5">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Generating advisory…
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
              </div>
            </div>
            </LpEditorShell>
          )}
        </>
      )}

      {/* Version history panel: lists snapshots newest-first with a friendly
          label + timestamp and a Restore action. The current pointer is marked.
          Per-page, server-authoritative. */}
      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <History className="h-4 w-4" /> Version history
            </SheetTitle>
            <SheetDescription>
              Every significant edit is saved here. Restore any version — your other
              versions are kept so you can move back and forth.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 flex-1 overflow-y-auto -mx-2 px-2">
            {historyQuery.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading history…
              </div>
            ) : (historyQuery.data?.snapshots?.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground py-6" data-testid="text-history-empty">
                No versions yet. Edits you make will appear here.
              </p>
            ) : (
              <ul className="space-y-1.5" data-testid="list-history">
                {historyQuery.data!.snapshots.map((s) => {
                  const isCurrent = s.seq === historyQuery.data!.currentSnapshotSeq;
                  return (
                    <li
                      key={s.id}
                      className={`flex items-center gap-3 rounded-lg border p-3 ${
                        isCurrent ? "border-primary bg-primary/5" : "border-border/40"
                      }`}
                      data-testid={`row-history-${s.seq}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground truncate" data-testid={`text-history-label-${s.seq}`}>
                            {s.actionLabel}
                          </span>
                          {isCurrent && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-primary shrink-0">
                              <CircleDot className="h-3 w-3" /> Current
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground" data-testid={`text-history-time-${s.seq}`}>
                          {new Date(s.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <Button
                        variant={isCurrent ? "ghost" : "outline"}
                        size="sm"
                        disabled={isCurrent || snapshotBusy || studioRevisionBusy}
                        onClick={() => restoreMutation.mutate(s.seq)}
                        data-testid={`button-restore-${s.seq}`}
                      >
                        {restoreMutation.isPending && restoreMutation.variables === s.seq ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <>
                            <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Restore
                          </>
                        )}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Brand-level custom domain modal: connect one subdomain for the whole
          brand, walk through the CNAME setup, and check verification. Pages
          opt into being served on it via their per-page toggle. */}
      <Dialog open={customDomainModalOpen} onOpenChange={setCustomDomainModalOpen}>
        <DialogContent className="max-w-lg" data-testid="dialog-custom-domain">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" /> Custom domain
            </DialogTitle>
            <DialogDescription>
              Serve your published landing pages on your own subdomain — e.g.{" "}
              <span className="font-medium text-foreground">landing.yourbrand.com/your-page</span>.
            </DialogDescription>
          </DialogHeader>

          {!brandDomain ? (
            // --- No domain yet: connect form + "while you wait" -----------
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground">
                  Your subdomain
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    value={customDomainInput}
                    onChange={(e) => setCustomDomainInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (customDomainInput.trim())
                          connectDomainMutation.mutate(customDomainInput.trim());
                      }
                    }}
                    placeholder="landing.yourbrand.com"
                    className="h-9 text-sm"
                    data-testid="input-custom-domain"
                  />
                  <Button
                    size="sm"
                    onClick={() => connectDomainMutation.mutate(customDomainInput.trim())}
                    disabled={connectDomainMutation.isPending || !customDomainInput.trim()}
                    data-testid="button-connect-custom-domain"
                  >
                    {connectDomainMutation.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Globe className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Connect
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Use a subdomain you control (e.g.{" "}
                  <span className="font-medium text-foreground">landing.yourbrand.com</span>).
                </p>
              </div>

              <div
                className="rounded-md border border-border/40 bg-muted/30 p-3"
                data-testid="panel-while-you-wait"
              >
                <p className="text-[11px] font-medium text-foreground">While you wait</p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Your published pages are already live and shareable at:
                </p>
                <code className="mt-1 block text-[11px] font-mono text-foreground break-all">
                  {window.location.origin}/p/{previewBrandSlug}/&lt;page&gt;
                </code>
              </div>
            </div>
          ) : (
            // --- Domain connected: status + 4-step CNAME wizard -----------
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  {brandDomainVerified ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="status-custom-domain">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Verified
                    </span>
                  ) : brandDomain.status === "failed" ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-red-50 text-red-700 border border-red-200" data-testid="status-custom-domain">
                      <AlertTriangle className="h-3.5 w-3.5" /> Not verified
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid="status-custom-domain">
                      <Clock className="h-3.5 w-3.5" />{" "}
                      {brandDomainLifecycle === "registering"
                        ? "Registering"
                        : brandDomainLifecycle === "ssl"
                          ? "Securing SSL"
                          : "Pending DNS"}
                    </span>
                  )}
                  <span className="text-xs font-mono text-foreground truncate" data-testid="text-custom-domain-hostname">
                    {brandDomain.hostname}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => checkDomainMutation.mutate()}
                    disabled={checkDomainMutation.isPending}
                    data-testid="button-check-custom-domain"
                  >
                    {checkDomainMutation.isPending ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <RotateCcw className="h-3 w-3 mr-1" />
                    )}
                    Check
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                    onClick={() => removeDomainMutation.mutate()}
                    disabled={removeDomainMutation.isPending}
                    data-testid="button-remove-custom-domain"
                  >
                    {removeDomainMutation.isPending ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <Trash2 className="h-3 w-3 mr-1" />
                    )}
                    Remove
                  </Button>
                </div>
              </div>

              {brandDomainVerified ? (
                <div className="space-y-3">
                  <div
                    className="rounded-md border border-emerald-200 bg-emerald-50 p-3"
                    data-testid="panel-custom-domain-verified"
                  >
                    <div className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-700">
                      <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                      <span>Your domain is verified and ready.</span>
                    </div>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Turn on <span className="font-medium text-foreground">Serve on custom domain</span> for
                      any published page to make it live at{" "}
                      <span className="font-mono text-foreground">{brandDomain.hostname}/&lt;page&gt;</span>.
                    </p>
                  </div>

                  {/* SSL/TLS status. The certificate is provisioned + renewed
                      automatically by the hosting platform once DNS verifies;
                      this reflects our live HTTPS probe of the domain. */}
                  {(() => {
                    const ssl = brandDomain.sslStatus ?? "pending";
                    const expiry = brandDomain.sslExpiresAt
                      ? new Date(brandDomain.sslExpiresAt).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })
                      : null;
                    if (ssl === "secured") {
                      return (
                        <div
                          className="rounded-md border border-emerald-200 bg-emerald-50 p-3"
                          data-testid="panel-custom-domain-ssl"
                        >
                          <div className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-700">
                            <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
                            <span data-testid="text-ssl-status">HTTPS is active — secured with a valid certificate.</span>
                          </div>
                          {expiry && (
                            <p className="mt-1 text-[11px] text-muted-foreground" data-testid="text-ssl-expiry">
                              Certificate auto-renews before it expires (currently valid through {expiry}).
                            </p>
                          )}
                        </div>
                      );
                    }
                    return (
                      <div
                        className="rounded-md border border-amber-200 bg-amber-50 p-3"
                        data-testid="panel-custom-domain-ssl"
                      >
                        <div className="flex items-center gap-1.5 text-[11px] font-medium text-amber-700">
                          <Clock className="h-3.5 w-3.5 shrink-0" />
                          <span data-testid="text-ssl-status">Securing your domain with HTTPS…</span>
                        </div>
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          The SSL certificate is issued automatically after DNS verifies. This
                          usually takes a few minutes — we keep checking and it'll turn green on
                          its own.
                        </p>
                        {brandDomain.sslError && (
                          <p className="mt-1 text-[11px] text-amber-700" data-testid="text-ssl-error">
                            {brandDomain.sslError}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="rounded-md border border-border/40 bg-muted/30 p-3 space-y-3">
                  <p className="text-[11px] font-medium text-foreground">
                    Finish setup in 4 steps
                  </p>
                  <ol className="space-y-2 text-[11px] text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="font-semibold text-foreground">1.</span>
                      <span>Open your DNS provider (where you bought the domain).</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-foreground">2.</span>
                      <span>Add a new <span className="font-medium text-foreground">CNAME</span> record:</span>
                    </li>
                  </ol>
                  <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px] pl-5">
                    <span className="text-muted-foreground">Type</span>
                    <span className="font-mono text-foreground" data-testid="text-cname-type">
                      {customDomainState?.recordType || "CNAME"}
                    </span>
                    <span className="text-muted-foreground">Name / Host</span>
                    <span className="font-mono text-foreground break-all" data-testid="text-cname-name">
                      {brandDomain.hostname}
                    </span>
                    <span className="text-muted-foreground">Value / Target</span>
                    <span className="flex items-center gap-1.5 min-w-0">
                      <span className="font-mono text-foreground break-all" data-testid="text-cname-target">
                        {customDomainState?.target || "—"}
                      </span>
                      {customDomainState?.target && (
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground shrink-0"
                          onClick={() => {
                            navigator.clipboard?.writeText(customDomainState?.target || "");
                            toast({ title: "Copied", description: "CNAME target copied." });
                          }}
                          data-testid="button-copy-cname-target"
                          aria-label="Copy CNAME target"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      )}
                    </span>
                    <span className="text-muted-foreground">TTL</span>
                    <span className="font-mono text-foreground" data-testid="text-cname-ttl">
                      {customDomainState?.ttl ?? 3600} (or Auto)
                    </span>
                  </div>
                  <ol className="space-y-2 text-[11px] text-muted-foreground" start={3}>
                    <li className="flex gap-2">
                      <span className="font-semibold text-foreground">3.</span>
                      <span>Save the record. DNS can take a few minutes to a few hours.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-foreground">4.</span>
                      <span>Click <span className="font-medium text-foreground">Check</span> above — we also re-check automatically.</span>
                    </li>
                  </ol>
                  {brandDomain.lastError && (
                    <p className="text-[11px] text-amber-700" data-testid="text-custom-domain-error">
                      {brandDomain.lastError}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={newProjectOpen} onOpenChange={setNewProjectOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogTitle>New project</DialogTitle>
          <div className="space-y-3 pt-2">
            <Input
              autoFocus
              placeholder="Project name"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newProjectName.trim()) handleCreateProject();
              }}
              data-testid="input-new-project-name"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setNewProjectOpen(false)} disabled={creatingProject}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateProject}
                disabled={!newProjectName.trim() || creatingProject}
                data-testid="button-create-project"
              >
                {creatingProject ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
